import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import moment from 'moment';
import { ChangeEvent, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Nav from 'react-bootstrap/Nav';
import Row from 'react-bootstrap/Row';
import Spinner from 'react-bootstrap/Spinner';
import Tab from 'react-bootstrap/Tab';
import DateTimePicker from 'react-datetime';
import {
  GetBadgesQuery,
  GetEmojiConfigsQuery,
  GetEmojiPacksQuery,
  GetItemsQuery,
  ItemAvailability,
  LevelRewardInput,
  LevelRewardInputType,
} from '../../../../../__gqltypes__';
import { formatReward, typeEmoji } from '../../components/formatReward';
import { GET_BADGES, GET_EMOJI_PACKS, GET_EMOJIS, GET_ITEMS } from '../../components/apolloQueries';
import {
  CREATE_LOYALTY_CAMPAIGN_CARD,
  DELETE_LOYALTY_CAMPAIGN_CARD,
  DELETE_LOYALTY_CAMPAIGN_CONFIG,
  GET_LOYALTY_CAMPAIGN_ADMIN,
  GET_LOYALTY_CAMPAIGN_SKU_OPTIONS,
  UPDATE_LOYALTY_CAMPAIGN_CARD,
  UPSERT_LOYALTY_CAMPAIGN_CONFIG,
} from './graphql';
import EditRewardsModal from '../../components/EditRewardsModal';
import { LoadingLogo } from '../../../devtools/components/Modal';
import CardModal from './components/CardModal';
import ConfigModal from './components/ConfigModal';
import {
  ConfigModalMode,
  LOYALTY_CAMPAIGN_ITEM_CATEGORY_LABELS,
  LoyaltyCampaignCardKind,
  LoyaltyCampaignCardRow,
  LoyaltyCampaignCardSectionId,
  LoyaltyCampaignCardType,
  LoyaltyCampaignClaimMode,
  LoyaltyCampaignConfigRow,
  LoyaltyCampaignItemCategory,
  LoyaltyCampaignOfferOptionRow,
  LoyaltyCampaignSkuPriceRow,
  RewardFormInput,
  SavedRewardRow,
  VanityItemType,
  VANITY_ITEM_LABELS,
} from './types';
import {
  buildOfferPriceMap,
  buildOfferSkuMap,
  buildSkuPriceMap,
  findOfferIdBySku,
  formatOfferOptionLabel,
  formatCompactNumber,
  formatLoyaltyPoints,
  formatSavedReward,
  fromLevelRewardInputToRewardForm,
  fromRewardFormToLevelRewardInput,
  getSkuPlatformLabel,
  getAllowedRewardTypesForItemCategory,
  getCardClaimMode,
  getCardClaimType,
  getItemCategoryBadgeStyle,
  getItemCategoryFromReward,
  getItemCategoryFromRewardForm,
  getLoyaltyPointsByPrice,
  getOfferPriceUSD,
  getRewardLabel,
  getSkuPriceUSD,
  toLevelRewardInputType,
  getVanityItemTypeFromReward,
  getVanityItemTypeFromRewardForm,
  parseSkus,
  scrollToSection,
  toRewardItemId,
  toRewardFormInput,
  toTitleLabel,
} from './utils';
import { downloadLoyaltyConfigWorkbook, parseLoyaltyConfigWorkbook } from './loyaltyPointMappingParser';

const DRAFT_CARD_ID_PREFIX = 'draft-card:';
const DRAFT_GROUP_ID_PREFIX = 'draft-group:';
const REGULAR_SHOP_SUBGROUP_NAME_OPTIONS = [
  'SPECIAL ITEMS',
  'EXCLUSIVE ITEMS',
  'RESOURCE ITEMS',
  'COMEBACK ITEMS',
  'PREMIUM ITEMS',
  'SEASONAL ITEMS',
  'REGULAR ITEMS',
  'LIMITED-TIME ITEMS',
  'WEEKLY DEALS',
  'OTHER ITEMS',
  'RETURNING ITEMS',
  'REWIND ITEMS',
];
const REGULAR_SHOP_SUBGROUP_NAME_LABELS: Record<string, string> = {
  'SPECIAL ITEMS': 'SPECIAL ITEMS',
  'EXCLUSIVE ITEMS': 'EXCLUSIVE ITEMS',
  'RESOURCE ITEMS': 'RESOURCE ITEMS',
  'COMEBACK ITEMS': 'COMEBACK ITEMS',
  'PREMIUM ITEMS': 'PREMIUM ITEMS',
  'SEASONAL ITEMS': 'SEASONAL ITEMS',
  'REGULAR ITEMS': 'REGULAR ITEMS',
  'LIMITED-TIME ITEMS': 'LIMITED-TIME ITEMS',
  'WEEKLY DEALS': 'WEEKLY DEALS',
  'OTHER ITEMS': 'OTHER ITEMS',
  'RETURNING ITEMS': 'RETURNING ITEMS',
  'REWIND ITEMS': 'REWIND ITEMS',
};
const ITEM_CATEGORY_ORDER: LoyaltyCampaignItemCategory[] = ['COINS', 'DIAMONDS', 'BOOSTS', 'FRAGMENTS', 'VANITY', 'OTHER'];
const CARD_SECTION_IDS: LoyaltyCampaignCardSectionId[] = [
  'bundle',
  'item-coins',
  'item-diamonds',
  'item-boosts',
  'item-fragments',
  'item-vanity',
  'item-other',
];
const REWARD_TYPES_REQUIRING_ITEM_ID = new Set<LevelRewardInputType>([
  LevelRewardInputType.CONTENT_PACK_REWARD,
  LevelRewardInputType.STICKER,
  LevelRewardInputType.PROFILE_FRAME,
  LevelRewardInputType.APP_SKIN,
  LevelRewardInputType.BADGE,
  LevelRewardInputType.POSTER,
  LevelRewardInputType.EMOJI,
  LevelRewardInputType.EMOJI_PACK,
]);
const SHOULD_SKIP_GENERIC_OFFER_LABEL = (offerId: string): boolean => /^diamond\.\d+$/.test(offerId);
type CardLifecycleState = 'scheduled' | 'active' | 'ended';
type ImportedConfigChangeType = 'added' | 'updated';
const PAGE_MAX_WIDTH = 1680;
const SECTION_MAX_WIDTH = 1480;

const buildNamedItemLookup = (itemsData?: GetItemsQuery): Record<string, string> => {
  const lookup: Record<string, string> = {};

  const register = (id?: string | null, name?: string | null): void => {
    const trimmedId = id?.trim();
    const trimmedName = name?.trim();
    if (!trimmedId || !trimmedName) {
      return;
    }
    lookup[trimmedId] = trimmedName;
    lookup[trimmedId.includes('.') ? trimmedId.split('.')[0] : trimmedId] = trimmedName;
  };

  itemsData?.liveOps.stickers.list.forEach((item) => register(item.id, item.name));
  itemsData?.liveOps.profileFrames.list.forEach((item) => register(item.id, item.name));
  itemsData?.liveOps.appskins.list.forEach((item) => register(item.id, item.name));
  itemsData?.liveOps.posters.forEach((item) => {
    register(item.id, item.name);
    register(item.itemId, item.name);
  });

  return lookup;
};

const buildBadgeLookup = (badgesData?: GetBadgesQuery): Record<string, string> =>
  (badgesData?.liveOps.badgeConfigs.list ?? []).reduce<Record<string, string>>((acc, badge) => {
    const badgeId = badge.id?.trim();
    const badgeName = badge.name?.trim();
    if (badgeId && badgeName) {
      acc[badgeId] = badgeName;
      acc[badgeId.includes('.') ? badgeId.split('.')[0] : badgeId] = badgeName;
    }
    return acc;
  }, {});

const buildEmojiLookup = (emojisData?: GetEmojiConfigsQuery): Record<string, string> =>
  (emojisData?.liveOps.emojisConfig.list ?? []).reduce<Record<string, string>>((acc, emoji) => {
    const emojiId = emoji.id?.trim();
    const emojiName = emoji.name?.trim();
    if (emojiId && emojiName) {
      acc[emojiId] = emojiName;
      acc[emojiId.includes('.') ? emojiId.split('.')[0] : emojiId] = emojiName;
    }
    return acc;
  }, {});

const buildEmojiPackLookup = (emojiPacksData?: GetEmojiPacksQuery): Record<string, string> =>
  (emojiPacksData?.liveOps.emojiPacks.list ?? []).reduce<Record<string, string>>((acc, pack) => {
    const packId = pack.id?.trim();
    const packName = pack.name?.trim();
    if (packId && packName) {
      acc[packId] = packName;
      acc[packId.includes('.') ? packId.split('.')[0] : packId] = packName;
    }
    return acc;
  }, {});

const getDraftRewardValidationError = (reward: RewardFormInput): string | null => {
  const rewardType = reward.type as LevelRewardInputType;
  const rewardLabel = toTitleLabel(reward.type || 'reward');
  const amount = Number(reward.amount);

  if (!Number.isFinite(amount) || amount <= 0) {
    return `${rewardLabel} amount must be greater than 0.`;
  }

  if (REWARD_TYPES_REQUIRING_ITEM_ID.has(rewardType) && reward.itemId.trim().length === 0) {
    return `${rewardLabel} selection is required.`;
  }

  if (rewardType === LevelRewardInputType.CURRENCY_CATEGORY_FRAGMENT && reward.itemId.trim() === 'RANDOM') {
    return 'Random fragment is not supported in Loyalty Campaign. Choose a specific category.';
  }

  return null;
};

const getSubgroupValidationErrors = (
  group: LoyaltyCampaignItemGroup,
  subgroup: Pick<LoyaltyCampaignItemSubgroup, 'startDate' | 'endDate'>
): Array<{ message: string; field: 'startDate' | 'endDate' | 'schedule' }> => {
  const errors: Array<{ message: string; field: 'startDate' | 'endDate' | 'schedule' }> = [];

  if (subgroup.endDate && !subgroup.startDate) {
    errors.push({ message: 'End date requires a start date.', field: 'endDate' });
  }
  if (subgroup.startDate && subgroup.endDate && moment.utc(subgroup.endDate).isBefore(moment.utc(subgroup.startDate))) {
    errors.push({ message: 'End date must be later than or equal to start date.', field: 'endDate' });
  }
  if (group.endDate && subgroup.startDate && moment.utc(subgroup.startDate).isAfter(moment.utc(group.endDate))) {
    errors.push({ message: 'Start date cannot be later than group end date.', field: 'startDate' });
  }
  if (group.startDate && subgroup.startDate && moment.utc(subgroup.startDate).isBefore(moment.utc(group.startDate))) {
    errors.push({ message: 'Start date cannot be earlier than group start date.', field: 'startDate' });
  }
  if (group.endDate && subgroup.endDate && moment.utc(subgroup.endDate).isAfter(moment.utc(group.endDate))) {
    errors.push({ message: 'End date cannot be later than group end date.', field: 'endDate' });
  }
  return errors;
};

const getDraftCardValidationError = (card: DraftLoyaltyCampaignCard): string | null => {
  const rewards = card.draftRewardInputs ?? [];
  if (rewards.length === 0) {
    return `Card ${card.id} has no reward.`;
  }

  const invalidRewardMessage = rewards.map(getDraftRewardValidationError).find((message) => message !== null);
  if (invalidRewardMessage) {
    return `${card.groupName || card.name || card.id}: ${invalidRewardMessage}`;
  }

  return null;
};

const pageShellStyle = {
  maxWidth: PAGE_MAX_WIDTH,
  margin: '0 auto',
  paddingBottom: 120,
};

const sectionCardStyle = {
  borderRadius: 20,
  border: '1px solid #e3e7eb',
  boxShadow: '0 14px 40px rgba(15, 23, 42, 0.06)',
  overflow: 'hidden' as const,
};

const sectionHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: 16,
  marginBottom: 20,
  flexWrap: 'wrap' as const,
};

const sectionEyebrowStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: '6px 12px',
  borderRadius: 999,
  background: '#eef4ff',
  color: '#2b5fb8',
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'uppercase' as const,
};

const metaChipStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '8px 12px',
  borderRadius: 6,
  background: '#f8fafc',
  border: '1px solid #d9e2ec',
  color: '#475569',
  fontSize: 13,
  fontWeight: 700,
};

const rewardChipStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '8px 12px',
  borderRadius: 6,
  background: '#e0f2fe',
  border: '1px solid #7dd3fc',
  color: '#0c4a6e',
  fontSize: 13,
  fontWeight: 800,
  boxShadow: '0 4px 12px rgba(56, 189, 248, 0.18)',
};

const priceChipStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '8px 12px',
  borderRadius: 6,
  background: '#fff4d6',
  border: '1px solid #f2c96d',
  color: '#7a4b00',
  fontSize: 13,
  fontWeight: 800,
  boxShadow: '0 4px 12px rgba(242, 201, 109, 0.24)',
};

const getScheduleTogglePanelStyle = (enabled: boolean) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  width: '100%',
  padding: '8px 10px',
  borderRadius: 10,
  border: `1px solid ${enabled ? '#93c5fd' : '#cbd5e1'}`,
  background: enabled ? '#eff6ff' : '#f8fafc',
  boxShadow: enabled ? 'inset 0 0 0 1px rgba(59, 130, 246, 0.08)' : 'none',
});

const ITEM_CATEGORY_CARD_STYLES: Record<
  LoyaltyCampaignItemCategory,
  {
    borderColor: string;
    background: string;
    badgeBackground: string;
    badgeText: string;
    headerBackground: string;
    headerBorderColor: string;
  }
> = {
  COINS: {
    borderColor: '#f5d78e',
    background: '#fffdf4',
    badgeBackground: '#b7791f',
    badgeText: '#fffaf0',
    headerBackground: '#fff3cd',
    headerBorderColor: '#f6c453',
  },
  DIAMONDS: {
    borderColor: '#b9d9ff',
    background: '#f7fbff',
    badgeBackground: '#2563eb',
    badgeText: '#eff6ff',
    headerBackground: '#d9efff',
    headerBorderColor: '#79e3ff',
  },
  BOOSTS: {
    borderColor: '#c8efd8',
    background: '#f7fdf9',
    badgeBackground: '#15803d',
    badgeText: '#f0fdf4',
    headerBackground: '#ffe0b8',
    headerBorderColor: '#ffb454',
  },
  FRAGMENTS: {
    borderColor: '#b7e7c6',
    background: '#f5fdf7',
    badgeBackground: '#15803d',
    badgeText: '#f0fdf4',
    headerBackground: '#dcfce7',
    headerBorderColor: '#86efac',
  },
  VANITY: {
    borderColor: '#e7cdf9',
    background: '#fdf9ff',
    badgeBackground: '#9333ea',
    badgeText: '#faf5ff',
    headerBackground: '#ead7ff',
    headerBorderColor: '#d6b0ff',
  },
  OTHER: {
    borderColor: '#dbe3ea',
    background: '#f8fafc',
    badgeBackground: '#475569',
    badgeText: '#f8fafc',
    headerBackground: '#e7edf3',
    headerBorderColor: '#cbd5e1',
  },
};

const LIFECYCLE_SECTION_STYLES = {
  active: {
    borderColor: '#c7d8f7',
    background: '#f6f9ff',
    headerBackground: '#e8f0ff',
    headerBorderColor: '#bfd0f3',
  },
  scheduled: {
    borderColor: '#cfe3f8',
    background: '#f4fbff',
    headerBackground: '#e1f3ff',
    headerBorderColor: '#b9def7',
  },
  ended: {
    borderColor: '#e5d7dc',
    background: '#fcf8f9',
    headerBackground: '#f6ecef',
    headerBorderColor: '#e8d3da',
  },
} as const;

const BUNDLE_CARD_STYLE = {
  borderColor: '#c7d2fe',
  background: '#f8faff',
  badgeBackground: '#3730a3',
  badgeText: '#eef2ff',
};

const actionDockStyle = {
  position: 'fixed' as const,
  right: 24,
  bottom: 24,
  zIndex: 30,
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '12px 14px',
  borderRadius: 20,
  background: 'rgba(255, 255, 255, 0.96)',
  border: '1px solid #d7dbe0',
  boxShadow: '0 16px 34px rgba(15, 23, 42, 0.16)',
  backdropFilter: 'blur(10px)',
};

const editButtonStyle = {
  backgroundColor: '#f3f7ff',
  borderColor: '#93c5fd',
  color: '#1d4ed8',
  fontWeight: 700,
};

const deleteButtonStyle = {
  backgroundColor: '#fff5f5',
  borderColor: '#fca5a5',
  color: '#c2410c',
  fontWeight: 700,
};

const addButtonStyle = {
  backgroundColor: '#0f9f6e',
  borderColor: '#0b7f58',
  color: '#ffffff',
  fontWeight: 700,
};

const pulseWarningBadgeStyle = {
  animation: 'loyaltyCampaignPulse 1.15s ease-in-out infinite',
  transformOrigin: 'center',
};

const resetButtonStyle = {
  backgroundColor: '#475569',
  borderColor: '#334155',
  color: '#ffffff',
  fontWeight: 700,
};

const SIDEBAR_STATE_ACCENT: Record<CardLifecycleState, { dot: string; text: string }> = {
  active: { dot: '#16a34a', text: '#166534' },
  scheduled: { dot: '#2563eb', text: '#1d4ed8' },
  ended: { dot: '#64748b', text: '#475569' },
};

const getRewardTypeEmoji = (rewardType?: string | null): string => {
  if (!rewardType) {
    return '';
  }
  return typeEmoji[rewardType as keyof typeof typeEmoji] ?? '';
};

const getGroupedItemAnchorId = (state: CardLifecycleState, groupId: string): string =>
  `grouped-item-${state}-${groupId.replace(/[^a-zA-Z0-9_-]/g, '-')}`;

const getGroupedItemSubgroupAnchorId = (groupId: string, subgroupId: string): string =>
  `grouped-item-subgroup-${groupId.replace(/[^a-zA-Z0-9_-]/g, '-')}-${subgroupId.replace(/[^a-zA-Z0-9_-]/g, '-')}`;

const getGroupScheduledStartDate = (group: LoyaltyCampaignItemGroup): string | null => {
  const candidateDates = [
    group.startDate,
    ...group.subgroups.map((subgroup) => subgroup.startDate),
    ...group.cards.map((card) => card.subgroupStartDate ?? card.groupStartDate ?? null),
  ].filter((value): value is string => Boolean(value));

  if (candidateDates.length === 0) {
    return null;
  }

  return [...candidateDates].sort((left, right) => moment.utc(left).valueOf() - moment.utc(right).valueOf())[0] ?? null;
};

const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Local timezone';

const formatLocalDateTime = (value?: string | Date | null): string | null => {
  if (!value) {
    return null;
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
};

const formatUtcDateTime = (value?: string | Date | null, format = 'ddd, MMM DD YYYY HH:mm'): string | null => {
  if (!value) {
    return null;
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return `${moment.utc(date).format(format)} UTC`;
};

const formatScheduledRunAt = (startDate?: string | null): string | null => {
  if (!startDate) {
    return null;
  }

  const utcLabel = moment.utc(startDate).format('YYYY-MM-DD HH:mm UTC');
  const localLabel = formatLocalDateTime(startDate);
  return localLabel ? `${utcLabel} • ${localLabel} (${localTimezone})` : utcLabel;
};

const getSubgroupNameOptionLabel = (value: string): string =>
  REGULAR_SHOP_SUBGROUP_NAME_LABELS[value] ?? value;

const isWholeNumberString = (value: string): boolean => /^\d+$/.test(value.trim());

const getEndNowDate = (): Date => moment.utc().seconds(0).milliseconds(0).toDate();

const applyEndNowToDateRange = (startDate: Date | null) => {
  const endNow = getEndNowDate();
  return {
    startDate: startDate && startDate.getTime() > endNow.getTime() ? endNow : startDate,
    endDate: endNow,
  };
};

const applyEndNowToIsoRange = (startDate?: string | null) => {
  const endNow = getEndNowDate();
  const nextStartDate =
    startDate && new Date(startDate).getTime() > endNow.getTime()
      ? endNow.toISOString()
      : startDate ?? null;

  return {
    startDate: nextStartDate,
    endDate: endNow.toISOString(),
  };
};

const toPascalCase = (value: string): string =>
  value
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join('');

const fromRewardFormToSavedReward = (reward: RewardFormInput): SavedRewardRow => {
  const type = reward.type as LevelRewardInputType;
  const base: SavedRewardRow = {
    __typename: toPascalCase(type),
    amount: reward.amount,
  };

  switch (type) {
    case LevelRewardInputType.CURRENCY_BOOST:
      return { ...base, boostType: reward.itemId };
    case LevelRewardInputType.CURRENCY_CATEGORY_FRAGMENT:
      return { ...base, category: reward.itemId ? { id: reward.itemId } : null };
    case LevelRewardInputType.CONTENT_PACK_REWARD:
      return { ...base, contentPack: reward.itemId ? { id: reward.itemId } : null };
    case LevelRewardInputType.STICKER:
    case LevelRewardInputType.PROFILE_FRAME:
    case LevelRewardInputType.APP_SKIN:
    case LevelRewardInputType.BADGE:
    case LevelRewardInputType.POSTER:
    case LevelRewardInputType.EMOJI:
    case LevelRewardInputType.EMOJI_PACK:
      return { ...base, id: reward.itemId };
    default:
      return reward.itemId ? { ...base, id: reward.itemId } : base;
  }
};

const toDraftCard = (card: LoyaltyCampaignCardRow): DraftLoyaltyCampaignCard => ({
  ...card,
  rewards: [...(card.rewards ?? [])],
  draftRewardInputs: (card.rewards ?? [])
    .map((reward) => toRewardFormInput(reward))
    .filter((reward): reward is RewardFormInput => reward !== null),
});

const isDraftCardId = (id: string): boolean => id.startsWith(DRAFT_CARD_ID_PREFIX);
const isDraftGroupId = (id: string): boolean => id.startsWith(DRAFT_GROUP_ID_PREFIX);

const getCardSectionId = (card: LoyaltyCampaignCardRow): LoyaltyCampaignCardSectionId => {
  if ((card.kind ?? 'BUNDLE') === 'BUNDLE') {
    return 'bundle';
  }

  switch (getItemCategoryFromReward(card.rewards?.[0])) {
    case 'COINS':
      return 'item-coins';
    case 'DIAMONDS':
      return 'item-diamonds';
    case 'BOOSTS':
      return 'item-boosts';
    case 'FRAGMENTS':
      return 'item-fragments';
    case 'VANITY':
      return 'item-vanity';
    default:
      return 'item-other';
  }
};

const getCardLifecycleState = (card: LoyaltyCampaignCardRow, now = moment.utc()): CardLifecycleState => {
  const effectiveStartDate =
    (card.kind ?? 'BUNDLE') === 'ITEM' && (card.groupId ?? '').trim().length > 0
      ? card.subgroupStartDate ?? card.groupStartDate ?? null
      : null;
  const effectiveEndDate =
    (card.kind ?? 'BUNDLE') === 'ITEM' && (card.groupId ?? '').trim().length > 0
      ? card.subgroupEndDate ?? card.groupEndDate ?? null
      : null;
  const startDate = effectiveStartDate ? moment.utc(effectiveStartDate) : null;
  const endDate = effectiveEndDate ? moment.utc(effectiveEndDate) : null;

  if (endDate && now.isSameOrAfter(endDate)) {
    return 'ended';
  }
  if (startDate && now.isBefore(startDate)) {
    return 'scheduled';
  }
  return 'active';
};

const serializeReward = (reward: SavedRewardRow): string =>
  JSON.stringify({
    type: reward.__typename ?? '',
    amount: reward.amount ?? 0,
    id: reward.id ?? '',
    boostType: reward.boostType ?? '',
    unlimitedBoostType: reward.unlimitedBoostType ?? '',
    categoryId: reward.category?.id ?? '',
    contentPackId: reward.contentPack?.id ?? '',
  });

const serializeConfig = (config: LoyaltyCampaignConfigRow): string => `${config.id}::${config.amount}`;

const serializeCard = (card: LoyaltyCampaignCardRow): string =>
  JSON.stringify({
    kind: card.kind ?? 'BUNDLE',
    name: card.name ?? '',
    groupId: card.groupId ?? null,
    groupName: card.groupName ?? null,
    groupDisplayOrder: card.groupDisplayOrder ?? null,
    groupStartDate: card.groupStartDate ?? null,
    groupEndDate: card.groupEndDate ?? null,
    subgroupId: card.subgroupId ?? null,
    subgroupName: card.subgroupName ?? null,
    subgroupDisplayOrder: card.subgroupDisplayOrder ?? null,
    subgroupStartDate: card.subgroupStartDate ?? null,
    subgroupEndDate: card.subgroupEndDate ?? null,
    itemDisplayOrder: card.itemDisplayOrder ?? null,
    cost: card.cost ?? 0,
    itemBonusLoyaltyPoints: card.itemBonusLoyaltyPoints ?? null,
    claimType: getCardClaimType(card.maxClaims, card.claimType),
    maxClaims: card.maxClaims ?? null,
    displayOrder: card.displayOrder ?? null,
    rewards: (card.rewards ?? []).map(serializeReward),
  });

const toMutationRewardInput = (
  reward: SavedRewardRow
): { type: string; amount: number; itemId: string | null } | null => {
  const rewardInput = toRewardFormInput(reward);
  if (!rewardInput) {
    return null;
  }

  return {
    type: rewardInput.type,
    amount: rewardInput.amount,
    itemId: rewardInput.itemId || null,
  };
};

const isDefined = <T,>(value: T | null): value is T => value !== null;

const sameRewardList = (left: SavedRewardRow[] = [], right: SavedRewardRow[] = []): boolean =>
  JSON.stringify(left.map(serializeReward)) === JSON.stringify(right.map(serializeReward));

const hasSameSerializedList = <T,>(left: T[], right: T[], serializer: (value: T) => string): boolean =>
  JSON.stringify(left.map(serializer)) === JSON.stringify(right.map(serializer));

type DraftLoyaltyCampaignCard = LoyaltyCampaignCardRow & {
  draftRewardInputs?: RewardFormInput[];
};

type DraftLoyaltyCampaignGroup = {
  groupId: string;
  groupName: string;
  groupDisplayOrder: number | null;
  startDate: string | null;
  endDate: string | null;
};

type DraftLoyaltyCampaignSubgroup = {
  groupId: string;
  subgroupId: string;
  subgroupName: string;
  subgroupDisplayOrder: number | null;
  startDate: string | null;
  endDate: string | null;
};

type LoyaltyCampaignItemSubgroup = {
  id: string;
  name: string;
  subgroupDisplayOrder: number;
  startDate: string | null;
  endDate: string | null;
  cards: LoyaltyCampaignCardRow[];
  isDraftPlaceholder?: boolean;
};

type LoyaltyCampaignItemGroup = {
  id: string;
  name: string;
  groupDisplayOrder: number;
  startDate: string | null;
  endDate: string | null;
  cards: LoyaltyCampaignCardRow[];
  subgroups: LoyaltyCampaignItemSubgroup[];
  isDraftPlaceholder?: boolean;
};

type EditableLoyaltyCampaignGroup = {
  originalGroupId: string;
  groupId: string;
  groupName: string;
  groupDisplayOrder: string;
  useSchedule: boolean;
  startDate: Date | null;
  endDate: Date | null;
};

type GroupedItemCardsByState = Record<CardLifecycleState, LoyaltyCampaignItemGroup[]>;
type CardSaveScope =
  | 'all'
  | 'grouped'
  | 'single'
  | 'bundle'
  | { kind: 'subgroup'; groupId: string; subgroupId: string };

const replaceCardAt = (
  cards: DraftLoyaltyCampaignCard[],
  cardId: string,
  updater: (card: DraftLoyaltyCampaignCard) => DraftLoyaltyCampaignCard
): DraftLoyaltyCampaignCard[] => cards.map((card) => (card.id === cardId ? updater(card) : card));

const getClaimDisplayLabel = (maxClaims?: number | null, fallback: LoyaltyCampaignCardType = 'ONE_TIME'): string => {
  const claimType = getCardClaimType(maxClaims, fallback);
  if (claimType === 'INFINITE') {
    return 'UNLIMITED';
  }
  if ((maxClaims ?? 1) > 1) {
    return `${maxClaims} CLAIMS`;
  }
  return 'ONE TIME';
};

const serializeDraftGroup = (group: DraftLoyaltyCampaignGroup): string =>
  JSON.stringify({
    groupId: group.groupId,
    groupName: group.groupName,
    groupDisplayOrder: group.groupDisplayOrder,
    startDate: group.startDate,
    endDate: group.endDate,
  });

const serializeDraftSubgroup = (subgroup: DraftLoyaltyCampaignSubgroup): string =>
  JSON.stringify({
    groupId: subgroup.groupId,
    subgroupId: subgroup.subgroupId,
    subgroupName: subgroup.subgroupName,
    subgroupDisplayOrder: subgroup.subgroupDisplayOrder,
    startDate: subgroup.startDate,
    endDate: subgroup.endDate,
  });

const sortDraftGroups = (groups: DraftLoyaltyCampaignGroup[]): DraftLoyaltyCampaignGroup[] =>
  [...groups].sort((left, right) => {
    const leftOrder = left.groupDisplayOrder ?? Number.MAX_SAFE_INTEGER;
    const rightOrder = right.groupDisplayOrder ?? Number.MAX_SAFE_INTEGER;
    if (leftOrder !== rightOrder) {
      return leftOrder - rightOrder;
    }
    return left.groupName.localeCompare(right.groupName);
  });

const sortDraftSubgroups = (subgroups: DraftLoyaltyCampaignSubgroup[]): DraftLoyaltyCampaignSubgroup[] =>
  [...subgroups].sort((left, right) => {
    if (left.groupId !== right.groupId) {
      return left.groupId.localeCompare(right.groupId);
    }
    const leftOrder = left.subgroupDisplayOrder ?? Number.MAX_SAFE_INTEGER;
    const rightOrder = right.subgroupDisplayOrder ?? Number.MAX_SAFE_INTEGER;
    if (leftOrder !== rightOrder) {
      return leftOrder - rightOrder;
    }
    return left.subgroupName.localeCompare(right.subgroupName);
  });

const sortComparableCards = <T extends LoyaltyCampaignCardRow>(cards: T[]): T[] =>
  [...cards].sort((left, right) => {
    const leftDisplayOrder = left.displayOrder ?? Number.MAX_SAFE_INTEGER;
    const rightDisplayOrder = right.displayOrder ?? Number.MAX_SAFE_INTEGER;
    if (leftDisplayOrder !== rightDisplayOrder) {
      return leftDisplayOrder - rightDisplayOrder;
    }

    const leftItemOrder = left.itemDisplayOrder ?? Number.MAX_SAFE_INTEGER;
    const rightItemOrder = right.itemDisplayOrder ?? Number.MAX_SAFE_INTEGER;
    if (leftItemOrder !== rightItemOrder) {
      return leftItemOrder - rightItemOrder;
    }

    return left.id.localeCompare(right.id);
  });

const getPersistedLifecycleCard = (
  card: LoyaltyCampaignCardRow,
  savedCardsById: Record<string, DraftLoyaltyCampaignCard>
): LoyaltyCampaignCardRow => {
  if (isDraftCardId(card.id)) {
    return card;
  }

  return savedCardsById[card.id] ?? card;
};

const buildItemGroups = (
  cards: LoyaltyCampaignCardRow[],
  draftGroups: DraftLoyaltyCampaignGroup[] = [],
  draftSubgroups: DraftLoyaltyCampaignSubgroup[] = []
): LoyaltyCampaignItemGroup[] =>
  Object.values(
    cards.reduce<Record<string, LoyaltyCampaignItemGroup>>((acc, card) => {
      const normalizedGroupId = (card.groupId ?? '').trim();
      const key = normalizedGroupId || `ungrouped:${card.id}`;
      if (!acc[key]) {
        acc[key] = {
          id: key,
          name: (card.groupName ?? '').trim() || (normalizedGroupId || 'Ungrouped Items'),
          groupDisplayOrder: card.groupDisplayOrder ?? Number.MAX_SAFE_INTEGER,
          startDate: card.groupStartDate ?? null,
          endDate: card.groupEndDate ?? null,
          cards: [],
          subgroups: [],
        };
      }
      acc[key].cards.push(card);
      return acc;
    }, {})
  )
    .map((group) => {
      const sortedCards = [...group.cards].sort((left, right) => {
        const leftOrder = left.itemDisplayOrder ?? left.displayOrder ?? 0;
        const rightOrder = right.itemDisplayOrder ?? right.displayOrder ?? 0;
        if (leftOrder !== rightOrder) {
          return leftOrder - rightOrder;
        }
        return (left.name ?? '').localeCompare(right.name ?? '');
      });
      const draftGroup = draftGroups.find((currentDraftGroup) => currentDraftGroup.groupId === group.id);

      const subgroups = Object.values(
        sortedCards.reduce<Record<string, LoyaltyCampaignItemSubgroup>>((acc, card) => {
          const normalizedSubgroupId = (card.subgroupId ?? '').trim();
          const subgroupKey = normalizedSubgroupId || `ungrouped:${card.id}`;
          if (!acc[subgroupKey]) {
            acc[subgroupKey] = {
              id: subgroupKey,
              name: (card.subgroupName ?? '').trim() || (normalizedSubgroupId || 'Ungrouped Subgroup'),
              subgroupDisplayOrder: card.subgroupDisplayOrder ?? Number.MAX_SAFE_INTEGER,
              startDate: card.subgroupStartDate ?? null,
              endDate: card.subgroupEndDate ?? null,
              cards: [],
            };
          }
          acc[subgroupKey].cards.push(card);
          return acc;
        }, {})
      )
        .map((subgroup) => {
          const sortedSubgroupCards = [...subgroup.cards].sort((left, right) => {
            const leftOrder = left.itemDisplayOrder ?? left.displayOrder ?? 0;
            const rightOrder = right.itemDisplayOrder ?? right.displayOrder ?? 0;
            if (leftOrder !== rightOrder) {
              return leftOrder - rightOrder;
            }
            return (left.name ?? '').localeCompare(right.name ?? '');
          });
          const draftSubgroup = draftSubgroups.find(
            (currentDraftSubgroup) =>
              currentDraftSubgroup.groupId === group.id && currentDraftSubgroup.subgroupId === subgroup.id
          );
          if (!draftSubgroup) {
            return {
              ...subgroup,
              cards: sortedSubgroupCards,
            };
          }
          return {
            ...subgroup,
            name: draftSubgroup.subgroupName,
            subgroupDisplayOrder: draftSubgroup.subgroupDisplayOrder ?? Number.MAX_SAFE_INTEGER,
            startDate: draftSubgroup.startDate,
            endDate: draftSubgroup.endDate,
            cards: sortedSubgroupCards,
          };
        })
        .concat(
          draftSubgroups
            .filter(
              (draftSubgroup) =>
                draftSubgroup.groupId === group.id &&
                !sortedCards.some((card) => (card.subgroupId ?? '').trim() === draftSubgroup.subgroupId)
            )
            .map((draftSubgroup) => ({
              id: draftSubgroup.subgroupId,
              name: draftSubgroup.subgroupName,
              subgroupDisplayOrder: draftSubgroup.subgroupDisplayOrder ?? Number.MAX_SAFE_INTEGER,
              startDate: draftSubgroup.startDate,
              endDate: draftSubgroup.endDate,
              cards: [],
              isDraftPlaceholder: true,
            }))
        )
        .sort((left, right) => {
        if (left.subgroupDisplayOrder !== right.subgroupDisplayOrder) {
          return left.subgroupDisplayOrder - right.subgroupDisplayOrder;
        }
        return left.name.localeCompare(right.name);
        });

      return {
        ...group,
        name: draftGroup?.groupName ?? group.name,
        groupDisplayOrder: draftGroup?.groupDisplayOrder ?? group.groupDisplayOrder,
        startDate: draftGroup?.startDate ?? group.startDate ?? null,
        endDate: draftGroup?.endDate ?? group.endDate ?? null,
        cards: sortedCards,
        subgroups,
      };
    })
    .concat(
      draftGroups
        .filter((draftGroup) => !cards.some((card) => (card.groupId ?? '').trim() === draftGroup.groupId))
        .map((draftGroup) => ({
          id: draftGroup.groupId,
          name: draftGroup.groupName,
          groupDisplayOrder: draftGroup.groupDisplayOrder ?? Number.MAX_SAFE_INTEGER,
          startDate: draftGroup.startDate,
          endDate: draftGroup.endDate,
          cards: [],
          subgroups: draftSubgroups
            .filter((draftSubgroup) => draftSubgroup.groupId === draftGroup.groupId)
            .map((draftSubgroup) => ({
              id: draftSubgroup.subgroupId,
              name: draftSubgroup.subgroupName,
              subgroupDisplayOrder: draftSubgroup.subgroupDisplayOrder ?? Number.MAX_SAFE_INTEGER,
              startDate: draftSubgroup.startDate,
              endDate: draftSubgroup.endDate,
              cards: [],
              isDraftPlaceholder: true,
            }))
            .sort((left, right) => {
              if (left.subgroupDisplayOrder !== right.subgroupDisplayOrder) {
                return left.subgroupDisplayOrder - right.subgroupDisplayOrder;
              }
              return left.name.localeCompare(right.name);
            }),
          isDraftPlaceholder: true,
        }))
    )
    .sort((left, right) => {
      if (left.groupDisplayOrder !== right.groupDisplayOrder) {
        return left.groupDisplayOrder - right.groupDisplayOrder;
      }
      return left.name.localeCompare(right.name);
    });

const getGroupScheduleValidationError = ({
  group,
  startDate,
  endDate,
}: {
  group?: LoyaltyCampaignItemGroup;
  startDate?: string | null;
  endDate?: string | null;
}): string | null => {
  if (!group) {
    return null;
  }

  for (const subgroup of group.subgroups) {
    if (startDate && subgroup.startDate && moment.utc(subgroup.startDate).isBefore(moment.utc(startDate))) {
      return `Group start date cannot be later than subgroup "${subgroup.name}" start date.`;
    }
    if (endDate && subgroup.startDate && moment.utc(subgroup.startDate).isAfter(moment.utc(endDate))) {
      return `Group end date cannot be earlier than subgroup "${subgroup.name}" start date.`;
    }
    if (startDate && subgroup.endDate && moment.utc(subgroup.endDate).isBefore(moment.utc(startDate))) {
      return `Group start date cannot be later than subgroup "${subgroup.name}" end date.`;
    }
    if (endDate && subgroup.endDate && moment.utc(subgroup.endDate).isAfter(moment.utc(endDate))) {
      return `Group end date cannot be earlier than subgroup "${subgroup.name}" end date.`;
    }
  }

  return null;
};

const getItemScheduleValidationError = ({
  group,
  subgroup,
  startDate,
  endDate,
}: {
  group?: LoyaltyCampaignItemGroup;
  subgroup?: LoyaltyCampaignItemSubgroup;
  startDate?: string | null;
  endDate?: string | null;
}): string | null => {
  if (endDate && !startDate) {
    return 'End date requires a start date.';
  }
  if (startDate && endDate && moment.utc(endDate).isBefore(moment.utc(startDate))) {
    return 'End date must be later than or equal to start date.';
  }
  if (group?.startDate && startDate && moment.utc(startDate).isBefore(moment.utc(group.startDate))) {
    return 'Item start date cannot be earlier than group start date.';
  }
  if (group?.endDate && endDate && moment.utc(endDate).isAfter(moment.utc(group.endDate))) {
    return 'Item end date cannot be later than group end date.';
  }
  if (subgroup?.startDate && startDate && moment.utc(startDate).isBefore(moment.utc(subgroup.startDate))) {
    return 'Item start date cannot be earlier than subgroup start date.';
  }
  if (subgroup?.endDate && endDate && moment.utc(endDate).isAfter(moment.utc(subgroup.endDate))) {
    return 'Item end date cannot be later than subgroup end date.';
  }

  return null;
};

const generateGroupId = (): string => {
  const cryptoApi = typeof globalThis !== 'undefined' ? (globalThis.crypto as Crypto & { randomUUID?: () => string }) : undefined;
  if (typeof cryptoApi?.randomUUID === 'function') {
    return cryptoApi.randomUUID();
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const randomValue = Math.floor(Math.random() * 16);
    const value = char === 'x' ? randomValue : 8 + Math.floor(Math.random() * 4);
    return value.toString(16);
  });
};

const toSubgroupId = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const LoyaltyCampaign = () => {
  const [viewportWidth, setViewportWidth] = useState<number>(
    typeof window === 'undefined' ? 1280 : window.innerWidth
  );
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [configAmount, setConfigAmount] = useState('0');
  const [bulkSkusText, setBulkSkusText] = useState('');
  const [selectedSku, setSelectedSku] = useState('');
  const [configModalMode, setConfigModalMode] = useState<ConfigModalMode>('create');

  const [cardName, setCardName] = useState('');
  const [cardKind, setCardKind] = useState<LoyaltyCampaignCardKind>('BUNDLE');
  const [cardGroupId, setCardGroupId] = useState('');
  const [cardGroupName, setCardGroupName] = useState('');
  const [cardGroupDisplayOrder, setCardGroupDisplayOrder] = useState('');
  const [cardSubgroupId, setCardSubgroupId] = useState('');
  const [cardSubgroupName, setCardSubgroupName] = useState('');
  const [cardSubgroupDisplayOrder, setCardSubgroupDisplayOrder] = useState('');
  const [itemCardCategoryPreset, setItemCardCategoryPreset] = useState<LoyaltyCampaignItemCategory | null>(null);
  const [vanityItemTypePreset, setVanityItemTypePreset] = useState<VanityItemType | null>(null);
  const [cardCost, setCardCost] = useState('0');
  const [cardItemBaseStoreOfferId, setCardItemBaseStoreOfferId] = useState('');
  const [cardItemSKUPrice, setCardItemSKUPrice] = useState('');
  const [cardItemBonusLoyaltyPoints, setCardItemBonusLoyaltyPoints] = useState('');
  const [cardClaimType, setCardClaimType] = useState<LoyaltyCampaignClaimMode>('ONE_TIME');
  const [cardMaxClaims, setCardMaxClaims] = useState('3');
  const [cardUseSchedule, setCardUseSchedule] = useState(false);
  const [cardStartDate, setCardStartDate] = useState<Date | null>(moment.utc().startOf('day').toDate());
  const [cardEndDate, setCardEndDate] = useState<Date | null>(null);
  const [cardRewards, setCardRewards] = useState<RewardFormInput[]>([]);

  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showCardModal, setShowCardModal] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [activeSection, setActiveSection] = useState('loyalty-points');
  const [selectedGroupedItemView, setSelectedGroupedItemView] =
    useState<{ state: CardLifecycleState; groupId: string } | null>(null);
  const [pendingSubgroupScrollTarget, setPendingSubgroupScrollTarget] = useState<{ groupId: string; subgroupId: string } | null>(
    null
  );
  const [showDraftGroupedItemsOnly, setShowDraftGroupedItemsOnly] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const [collapsedSubgroups, setCollapsedSubgroups] = useState<Record<string, boolean>>({});
  const [showEndedBundleCards, setShowEndedBundleCards] = useState(false);
  const [editingCardId, setEditingCardId] = useState<string | null>(null);
  const [showRewardModal, setShowRewardModal] = useState(false);
  const [inlineComposerSubgroupKey, setInlineComposerSubgroupKey] = useState<string | null>(null);
  const [selectedReward, setSelectedReward] = useState<(LevelRewardInput & { itemName?: string }) | null>(null);
  const [editingRewardIndex, setEditingRewardIndex] = useState<number | null>(null);
  const [editingGroup, setEditingGroup] = useState<EditableLoyaltyCampaignGroup | null>(null);
  const [offerOptions, setOfferOptions] = useState<LoyaltyCampaignOfferOptionRow[]>([]);
  const [offerPriceMap, setOfferPriceMap] = useState<Record<string, number>>({});
  const [offerSkuMap, setOfferSkuMap] = useState<Record<string, string[]>>({});
  const [skuOptions, setSkuOptions] = useState<string[]>([]);
  const [skuPriceMap, setSkuPriceMap] = useState<Record<string, number>>({});
  const [configGroupAmount, setConfigGroupAmount] = useState<number | null>(null);
  const [editingGroupRows, setEditingGroupRows] = useState<LoyaltyCampaignConfigRow[]>([]);

  const [deletingConfigId, setDeletingConfigId] = useState<string | null>(null);
  const [deletingCardId, setDeletingCardId] = useState<string | null>(null);
  const [movingCardId, setMovingCardId] = useState<string | null>(null);
  const [draggingSubgroupItemId, setDraggingSubgroupItemId] = useState<string | null>(null);
  const [dragOverSubgroupItemId, setDragOverSubgroupItemId] = useState<string | null>(null);
  const [autoImportLoading, setAutoImportLoading] = useState(false);
  const [importingConfigs, setImportingConfigs] = useState(false);
  const [exportingConfigs, setExportingConfigs] = useState(false);
  const [importedConfigChanges, setImportedConfigChanges] = useState<Record<string, ImportedConfigChangeType>>({});
  const [removedImportedConfigs, setRemovedImportedConfigs] = useState<LoyaltyCampaignConfigRow[]>([]);
  const [showOnlyImportedConfigChanges, setShowOnlyImportedConfigChanges] = useState(false);
  const [savingConfigs, setSavingConfigs] = useState(false);
  const [savingCards, setSavingCards] = useState(false);
  const [deletingAllGrouped, setDeletingAllGrouped] = useState(false);
  const [savingAll, setSavingAll] = useState(false);
  const apolloClient = useApolloClient();
  const hasHydratedDraftRef = useRef(false);
  const configImportInputRef = useRef<HTMLInputElement | null>(null);

  const { data, loading } = useQuery(GET_LOYALTY_CAMPAIGN_ADMIN, {
    fetchPolicy: 'no-cache',
  });

  const [upsertConfig] = useMutation(UPSERT_LOYALTY_CAMPAIGN_CONFIG);
  const [deleteConfig] = useMutation(DELETE_LOYALTY_CAMPAIGN_CONFIG);
  const [createCard] = useMutation(CREATE_LOYALTY_CAMPAIGN_CARD);
  const [updateCard] = useMutation(UPDATE_LOYALTY_CAMPAIGN_CARD);
  const [deleteCard] = useMutation(DELETE_LOYALTY_CAMPAIGN_CARD);

  const queryConfigs: LoyaltyCampaignConfigRow[] = useMemo(
    () => data?.liveOps?.loyaltyCampaignConfigs ?? [],
    [data?.liveOps?.loyaltyCampaignConfigs]
  );
  const queryCampaignCards: LoyaltyCampaignCardRow[] = useMemo(
    () => data?.liveOps?.loyaltyCampaignCards ?? [],
    [data?.liveOps?.loyaltyCampaignCards]
  );

  const [savedConfigs, setSavedConfigs] = useState<LoyaltyCampaignConfigRow[]>([]);
  const [draftConfigs, setDraftConfigs] = useState<LoyaltyCampaignConfigRow[]>([]);
  const [savedCards, setSavedCards] = useState<DraftLoyaltyCampaignCard[]>([]);
  const [draftCards, setDraftCards] = useState<DraftLoyaltyCampaignCard[]>([]);
  const [savedDraftGroups, setSavedDraftGroups] = useState<DraftLoyaltyCampaignGroup[]>([]);
  const [draftGroups, setDraftGroups] = useState<DraftLoyaltyCampaignGroup[]>([]);
  const [savedDraftSubgroups, setSavedDraftSubgroups] = useState<DraftLoyaltyCampaignSubgroup[]>([]);
  const [draftSubgroups, setDraftSubgroups] = useState<DraftLoyaltyCampaignSubgroup[]>([]);
  const subgroupNameOptions = useMemo(
    () => Array.from(new Set(REGULAR_SHOP_SUBGROUP_NAME_OPTIONS)),
    []
  );

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (hasHydratedDraftRef.current || loading || !data) {
      return;
    }

    const nextConfigs = [...queryConfigs].sort((a, b) => a.id.localeCompare(b.id));
    const nextCards = [...queryCampaignCards]
      .sort((a, b) => {
        const left = a.displayOrder ?? 0;
        const right = b.displayOrder ?? 0;
        if (left !== right) {
          return left - right;
        }
        const leftCreated = a.created ? new Date(a.created).getTime() : 0;
        const rightCreated = b.created ? new Date(b.created).getTime() : 0;
        return leftCreated - rightCreated;
      })
      .map(toDraftCard);

    setSavedConfigs(nextConfigs);
    setDraftConfigs(nextConfigs);
    setSavedCards(nextCards);
    setDraftCards(nextCards);
    setSavedDraftGroups([]);
    setDraftGroups([]);
    setSavedDraftSubgroups([]);
    setDraftSubgroups([]);
    hasHydratedDraftRef.current = true;
  }, [data, loading, queryCampaignCards, queryConfigs]);

  useEffect(() => {
    let isMounted = true;
    apolloClient
      .query({
        query: GET_LOYALTY_CAMPAIGN_SKU_OPTIONS,
        fetchPolicy: 'no-cache',
      })
      .then((response) => {
        if (!isMounted) {
          return;
        }
        const nextOptions = response?.data?.liveOps?.loyaltyCampaignSkuOptions ?? [];
        setSkuOptions(Array.isArray(nextOptions) ? nextOptions : []);
        const nextPrices = response?.data?.liveOps?.loyaltyCampaignSkuPrices ?? [];
        setSkuPriceMap(buildSkuPriceMap(nextPrices as LoyaltyCampaignSkuPriceRow[]));
        const nextOffers = response?.data?.liveOps?.loyaltyCampaignOfferOptions ?? [];
        const parsedOffers = Array.isArray(nextOffers) ? (nextOffers as LoyaltyCampaignOfferOptionRow[]) : [];
        setOfferOptions(parsedOffers);
        setOfferPriceMap(buildOfferPriceMap(parsedOffers));
        setOfferSkuMap(buildOfferSkuMap(parsedOffers));
      })
      .catch(() => {
        if (isMounted) {
          setOfferOptions([]);
          setOfferPriceMap({});
          setOfferSkuMap({});
          setSkuOptions([]);
          setSkuPriceMap({});
        }
      });

    return () => {
      isMounted = false;
    };
  }, [apolloClient]);

  useEffect(() => {
    if (!notice) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setNotice((currentNotice) => (currentNotice === notice ? null : currentNotice));
    }, 30000);

    return () => window.clearTimeout(timeoutId);
  }, [notice]);

  const pending = loading && !data;
  const { data: itemsData } = useQuery<GetItemsQuery>(GET_ITEMS);
  const { data: badgesData } = useQuery<GetBadgesQuery>(GET_BADGES);
  const { data: emojisData } = useQuery<GetEmojiConfigsQuery>(GET_EMOJIS);
  const { data: emojiPacksData } = useQuery<GetEmojiPacksQuery>(GET_EMOJI_PACKS);
  const uiScale = Math.max(0.82, Math.min(1, viewportWidth / 1440));
  const scaled = (value: number): number => Number((value * uiScale).toFixed(2));
  const namedItemLookup = useMemo(() => buildNamedItemLookup(itemsData), [itemsData]);
  const badgeLookup = useMemo(() => buildBadgeLookup(badgesData), [badgesData]);
  const emojiLookup = useMemo(() => buildEmojiLookup(emojisData), [emojisData]);
  const emojiPackLookup = useMemo(() => buildEmojiPackLookup(emojiPacksData), [emojiPacksData]);
  const getDraftRewardSelectionLabel = (reward: RewardFormInput): string | null => {
    if (reward.itemName?.trim()) {
      return reward.itemName.trim();
    }

    const rewardType = reward.type as LevelRewardInputType;
    const itemId = reward.itemId.trim();

    if (!itemId) {
      return null;
    }

    if (
      rewardType === LevelRewardInputType.STICKER ||
      rewardType === LevelRewardInputType.PROFILE_FRAME ||
      rewardType === LevelRewardInputType.APP_SKIN ||
      rewardType === LevelRewardInputType.POSTER
    ) {
      return namedItemLookup[itemId] ?? itemId;
    }

    if (rewardType === LevelRewardInputType.BADGE) {
      return badgeLookup[itemId] ?? itemId;
    }

    if (rewardType === LevelRewardInputType.EMOJI) {
      return emojiLookup[itemId] ?? itemId;
    }

    if (rewardType === LevelRewardInputType.EMOJI_PACK) {
      return emojiPackLookup[itemId] ?? itemId;
    }

    return itemId;
  };
  const getDraftRewardDisplayLabel = (reward: RewardFormInput): string => {
    const rewardType = reward.type as LevelRewardInputType;
    const selectionLabel = getDraftRewardSelectionLabel(reward);
    const rewardIcon = getRewardTypeEmoji(reward.type);
    const rewardKindLabel = toTitleLabel(reward.type);

    if (
      rewardType === LevelRewardInputType.STICKER ||
      rewardType === LevelRewardInputType.PROFILE_FRAME ||
      rewardType === LevelRewardInputType.APP_SKIN ||
      rewardType === LevelRewardInputType.BADGE ||
      rewardType === LevelRewardInputType.POSTER ||
      rewardType === LevelRewardInputType.EMOJI ||
      rewardType === LevelRewardInputType.EMOJI_PACK
    ) {
      if (selectionLabel) {
        return `${rewardIcon ? `${rewardIcon} ` : ''}${rewardKindLabel}: ${selectionLabel}`.trim();
      }
    }

    if (rewardType === LevelRewardInputType.CURRENCY_BOOST) {
      return `${rewardIcon ? `${rewardIcon} ` : ''}${reward.amount} ${selectionLabel ?? reward.itemId} boosts`.trim();
    }

    if (rewardType === LevelRewardInputType.CURRENCY_CATEGORY_FRAGMENT) {
      return `${rewardIcon ? `${rewardIcon} ` : ''}${reward.amount} ${selectionLabel ?? reward.itemId} ${rewardKindLabel}`.trim();
    }

    return formatReward({
      ...fromRewardFormToLevelRewardInput(reward),
      itemName: selectionLabel ?? undefined,
    });
  };
  const getDraftRewardButtonLabel = (reward: RewardFormInput): string => {
    const rewardType = reward.type as LevelRewardInputType;
    const selectionLabel = getDraftRewardSelectionLabel(reward);
    const rewardKindLabel = toTitleLabel(reward.type);

    if (
      rewardType === LevelRewardInputType.STICKER ||
      rewardType === LevelRewardInputType.PROFILE_FRAME ||
      rewardType === LevelRewardInputType.APP_SKIN ||
      rewardType === LevelRewardInputType.BADGE ||
      rewardType === LevelRewardInputType.POSTER ||
      rewardType === LevelRewardInputType.EMOJI ||
      rewardType === LevelRewardInputType.EMOJI_PACK
    ) {
      return selectionLabel ? `${rewardKindLabel}: ${selectionLabel}` : rewardKindLabel;
    }

    if (rewardType === LevelRewardInputType.CURRENCY_BOOST) {
      return `${reward.amount} ${selectionLabel ?? reward.itemId} boosts`.trim();
    }

    if (rewardType === LevelRewardInputType.CURRENCY_CATEGORY_FRAGMENT) {
      return `${reward.amount} ${selectionLabel ?? reward.itemId} ${rewardKindLabel}`.trim();
    }

    return getDraftRewardDisplayLabel(reward);
  };
  const getSavedRewardDisplayLabel = (reward: SavedRewardRow): string => {
    const rewardType = toLevelRewardInputType(reward);
    const itemId = toRewardItemId(reward).trim();
    const rewardKindLabel = rewardType ? toTitleLabel(rewardType) : '';
    let mappedItemName = '';

    if (itemId) {
      if (
        rewardType === LevelRewardInputType.STICKER ||
        rewardType === LevelRewardInputType.PROFILE_FRAME ||
        rewardType === LevelRewardInputType.APP_SKIN ||
        rewardType === LevelRewardInputType.POSTER
      ) {
        mappedItemName = namedItemLookup[itemId] ?? '';
      } else if (rewardType === LevelRewardInputType.BADGE) {
        mappedItemName = reward.name?.trim() || badgeLookup[itemId] || '';
      } else if (rewardType === LevelRewardInputType.EMOJI) {
        mappedItemName = emojiLookup[itemId] ?? '';
      } else if (rewardType === LevelRewardInputType.EMOJI_PACK) {
        mappedItemName = emojiPackLookup[itemId] ?? '';
      }
    }

    if (mappedItemName) {
      const rewardIcon = getRewardTypeEmoji(rewardType);
      return `${rewardIcon ? `${rewardIcon} ` : ''}${rewardKindLabel}: ${mappedItemName}`.trim();
    }

    return formatSavedReward(reward);
  };
  const offerById = useMemo(
    () =>
      offerOptions.reduce<Record<string, LoyaltyCampaignOfferOptionRow>>((acc, offer) => {
        const offerId = (offer.id ?? '').trim();
        if (offerId) {
          acc[offerId] = offer;
        }
        return acc;
      }, {}),
    [offerOptions]
  );
  const activeItemCategory =
    cardKind === 'ITEM'
      ? cardRewards[0]
        ? getItemCategoryFromRewardForm(cardRewards[0])
        : itemCardCategoryPreset
      : null;
  const activeVanityItemType =
    activeItemCategory === 'VANITY'
      ? cardRewards[0]
        ? getVanityItemTypeFromRewardForm(cardRewards[0])
        : vanityItemTypePreset
      : null;
  const activeItemRewardTypes =
    cardKind === 'ITEM' ? getAllowedRewardTypesForItemCategory(activeItemCategory, activeVanityItemType) : undefined;
  const directCurrencyRewardType =
    activeItemCategory === 'COINS'
      ? LevelRewardInputType.CURRENCY_COIN
      : activeItemCategory === 'DIAMONDS'
        ? LevelRewardInputType.CURRENCY_DIAMOND
        : null;
  const directCurrencyReward =
    directCurrencyRewardType && cardRewards[0]?.type === directCurrencyRewardType ? cardRewards[0] : null;
  const directCurrencyAmount = directCurrencyReward?.amount ?? 0;
  const normalizedCardCost = Number(cardCost);
  const hasLoyaltyPointCost = Number.isFinite(normalizedCardCost) && normalizedCardCost > 0;
  const hasSkuPrice = cardItemSKUPrice.trim().length > 0;
  const hasValidCardInput =
    (cardKind === 'ITEM' || cardName.trim().length > 0) &&
    Number.isFinite(normalizedCardCost) &&
    normalizedCardCost >= 0 &&
    (hasLoyaltyPointCost || hasSkuPrice) &&
    (cardClaimType !== 'CUSTOM' || (Number.isFinite(Number(cardMaxClaims)) && Number(cardMaxClaims) > 1)) &&
    (!cardUseSchedule ||
      (cardStartDate !== null && (cardEndDate === null || cardEndDate.getTime() >= cardStartDate.getTime())));
  const hasValidConfigAmount = Number.isFinite(Number(configAmount));

  useEffect(() => {
    if (cardKind !== 'ITEM' || (activeItemCategory !== 'COINS' && activeItemCategory !== 'DIAMONDS')) {
      return;
    }

    if (editingCardId) {
      return;
    }

    if (!directCurrencyAmount || !Number.isFinite(directCurrencyAmount)) {
      setCardName('');
      return;
    }

    setCardName(
      activeItemCategory === 'COINS'
        ? `${formatCompactNumber(directCurrencyAmount)} Coins`
        : `${formatCompactNumber(directCurrencyAmount)} Diamonds`
    );
  }, [activeItemCategory, cardKind, directCurrencyAmount, editingCardId]);

  useEffect(() => {
    if (cardKind !== 'ITEM' || activeItemCategory !== 'VANITY') {
      return;
    }

    if (editingCardId) {
      return;
    }

    const vanityReward = cardRewards[0];
    if (!vanityReward?.itemId) {
      return;
    }

    setCardName(namedItemLookup[vanityReward.itemId] ?? toTitleLabel(vanityReward.itemId));
  }, [activeItemCategory, cardKind, cardRewards, editingCardId, namedItemLookup]);

  const configGroups = useMemo(() => {
    const groups = new Map<number, LoyaltyCampaignConfigRow[]>();
    for (const config of draftConfigs) {
      const amount = Number(config.amount ?? 0);
      const list = groups.get(amount) ?? [];
      list.push(config);
      groups.set(amount, list);
    }
    return Array.from(groups.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([amount, rows]) => ({ amount, rows: rows.sort((a, b) => a.id.localeCompare(b.id)) }));
  }, [draftConfigs]);
  const importedConfigChangeCount = Object.keys(importedConfigChanges).length;
  const visibleConfigGroups = useMemo(
    () =>
      showOnlyImportedConfigChanges
        ? configGroups
            .map((group) => ({
              ...group,
              rows: group.rows.filter((row) => Boolean(importedConfigChanges[row.id])),
            }))
            .filter((group) => group.rows.length > 0)
        : configGroups,
    [configGroups, importedConfigChanges, showOnlyImportedConfigChanges]
  );

  const savedCardsById = useMemo(
    () =>
      savedCards.reduce<Record<string, DraftLoyaltyCampaignCard>>((acc, card) => {
        acc[card.id] = card;
        return acc;
      }, {}),
    [savedCards]
  );

  const bundleCampaignCards = useMemo(
    () => draftCards.filter((card) => getCardSectionId(card) === 'bundle'),
    [draftCards]
  );

  const bundleCardGroupsByState = useMemo(() => {
    const now = moment.utc();
    return {
      scheduled: bundleCampaignCards.filter(
        (card) => getCardLifecycleState(getPersistedLifecycleCard(card, savedCardsById), now) === 'scheduled'
      ),
      active: bundleCampaignCards.filter(
        (card) => getCardLifecycleState(getPersistedLifecycleCard(card, savedCardsById), now) === 'active'
      ),
      ended: bundleCampaignCards.filter(
        (card) => getCardLifecycleState(getPersistedLifecycleCard(card, savedCardsById), now) === 'ended'
      ),
    };
  }, [bundleCampaignCards, savedCardsById]);

  const getCardPricingLabel = (card: LoyaltyCampaignCardRow): string => formatLoyaltyPoints(card.cost);

const serializeComparableCard = (card: LoyaltyCampaignCardRow): string =>
  JSON.stringify(JSON.parse(serializeCard(card)));

const hasSameCards = (left: LoyaltyCampaignCardRow[], right: LoyaltyCampaignCardRow[]): boolean =>
  hasSameSerializedList(sortComparableCards(left), sortComparableCards(right), serializeComparableCard);

  const countChangedRows = <T extends { id: string }>(
    draftRows: T[],
    savedRows: T[],
    serializer: (value: T) => string
  ): number =>
    draftRows.filter((draftRow) => {
      const savedRow = savedRows.find((row) => row.id === draftRow.id);
      return !savedRow || serializer(savedRow) !== serializer(draftRow);
    }).length + savedRows.filter((savedRow) => !draftRows.some((draftRow) => draftRow.id === savedRow.id)).length;

  const itemCardGroupsByState = useMemo(() => {
    const createEmptyGroups = (): Record<LoyaltyCampaignItemCategory, LoyaltyCampaignCardRow[]> => ({
      COINS: [],
      DIAMONDS: [],
      BOOSTS: [],
      FRAGMENTS: [],
      VANITY: [],
      OTHER: [],
    });
    const now = moment.utc();
    const groups: Record<CardLifecycleState, Record<LoyaltyCampaignItemCategory, LoyaltyCampaignCardRow[]>> = {
      scheduled: createEmptyGroups(),
      active: createEmptyGroups(),
      ended: createEmptyGroups(),
    };

    for (const card of draftCards.filter((currentCard) => getCardSectionId(currentCard) !== 'bundle')) {
      const lifecycleState = getCardLifecycleState(getPersistedLifecycleCard(card, savedCardsById), now);
      groups[lifecycleState][getItemCategoryFromReward(card.rewards?.[0])].push(card);
    }

    return groups;
  }, [draftCards, savedCardsById]);
  const activeItemCardGroups = itemCardGroupsByState.active;
  const scheduledItemCardGroups = itemCardGroupsByState.scheduled;
  const endedItemCardGroups = itemCardGroupsByState.ended;

  const groupedItemCardsByState = useMemo((): GroupedItemCardsByState => {
    const buildGroups = (
      cardsByCategory: Record<LoyaltyCampaignItemCategory, LoyaltyCampaignCardRow[]>,
      placeholderGroups: DraftLoyaltyCampaignGroup[] = []
    ) =>
      buildItemGroups(
        Object.values(cardsByCategory)
          .flat()
          .filter((card) => (card.groupId ?? '').trim().length > 0),
        placeholderGroups,
        draftSubgroups
      );

    return {
      active: buildGroups(activeItemCardGroups, draftGroups),
      scheduled: buildGroups(scheduledItemCardGroups),
      ended: buildGroups(endedItemCardGroups),
    };
  }, [activeItemCardGroups, draftGroups, draftSubgroups, scheduledItemCardGroups, endedItemCardGroups]);

  const ungroupedItemCardsByState = useMemo(() => {
    const filterUngrouped = (cardsByCategory: Record<LoyaltyCampaignItemCategory, LoyaltyCampaignCardRow[]>) =>
      (Object.keys(cardsByCategory) as LoyaltyCampaignItemCategory[]).reduce<
        Record<LoyaltyCampaignItemCategory, LoyaltyCampaignCardRow[]>
      >(
        (acc, category) => {
          acc[category] = cardsByCategory[category].filter((card) => (card.groupId ?? '').trim().length === 0);
          return acc;
        },
        {
          COINS: [],
          DIAMONDS: [],
          BOOSTS: [],
          FRAGMENTS: [],
          VANITY: [],
          OTHER: [],
        }
      );

    return {
      active: filterUngrouped(activeItemCardGroups),
      scheduled: filterUngrouped(scheduledItemCardGroups),
      ended: filterUngrouped(endedItemCardGroups),
    };
  }, [activeItemCardGroups, scheduledItemCardGroups, endedItemCardGroups]);

  const draftGroupedItemCardsByState = useMemo(
    () => ({
      active: groupedItemCardsByState.active.filter((group) =>
        draftGroups.some((draftGroup) => draftGroup.groupId === group.id)
      ),
      scheduled: groupedItemCardsByState.scheduled.filter((group) =>
        draftGroups.some((draftGroup) => draftGroup.groupId === group.id)
      ),
      ended: groupedItemCardsByState.ended.filter((group) =>
        draftGroups.some((draftGroup) => draftGroup.groupId === group.id)
      ),
    }),
    [draftGroups, groupedItemCardsByState]
  );

  const groupedItemsToRender = useMemo(
    () => (showDraftGroupedItemsOnly ? draftGroupedItemCardsByState : groupedItemCardsByState),
    [draftGroupedItemCardsByState, groupedItemCardsByState, showDraftGroupedItemsOnly]
  );

  const resolvedGroupedItemView = useMemo(() => {
    if (selectedGroupedItemView) {
      const selectedGroupExists = groupedItemsToRender[selectedGroupedItemView.state].some(
        (group) => group.id === selectedGroupedItemView.groupId
      );
      if (selectedGroupExists) {
        return selectedGroupedItemView;
      }
    }
    if (groupedItemsToRender.active[0]) {
      return { state: 'active' as CardLifecycleState, groupId: groupedItemsToRender.active[0].id };
    }
    if (groupedItemsToRender.scheduled[0]) {
      return { state: 'scheduled' as CardLifecycleState, groupId: groupedItemsToRender.scheduled[0].id };
    }
    if (groupedItemsToRender.ended[0]) {
      return { state: 'ended' as CardLifecycleState, groupId: groupedItemsToRender.ended[0].id };
    }
    return null;
  }, [groupedItemsToRender, selectedGroupedItemView]);

  const selectedGroupedItemGroup = useMemo(() => {
    if (!resolvedGroupedItemView) {
      return null;
    }

    return (
      groupedItemsToRender[resolvedGroupedItemView.state].find(
        (group) => group.id === resolvedGroupedItemView.groupId
      ) ?? null
    );
  }, [groupedItemsToRender, resolvedGroupedItemView]);

  useEffect(() => {
    const allGroups = Object.values(groupedItemsToRender).flat();
    if (allGroups.length === 0) {
      return;
    }

    setCollapsedSubgroups((prev) => {
      let changed = false;
      const next = { ...prev };

      allGroups.forEach((group) => {
        group.subgroups.forEach((subgroup) => {
          const key = `${group.id}:${subgroup.id}`;
          if (next[key] === undefined) {
            next[key] = true;
            changed = true;
          }
        });
      });

      return changed ? next : prev;
    });
  }, [groupedItemsToRender]);

  useEffect(() => {
    if (!pendingSubgroupScrollTarget || !resolvedGroupedItemView || !selectedGroupedItemGroup || !inlineComposerSubgroupKey) {
      return undefined;
    }

    if (pendingSubgroupScrollTarget.groupId !== resolvedGroupedItemView.groupId) {
      return undefined;
    }

    if (inlineComposerSubgroupKey !== `${pendingSubgroupScrollTarget.groupId}:${pendingSubgroupScrollTarget.subgroupId}`) {
      return undefined;
    }

    const targetSubgroupExists = selectedGroupedItemGroup.subgroups.some(
      (subgroup) => subgroup.id === pendingSubgroupScrollTarget.subgroupId
    );
    if (!targetSubgroupExists) {
      return undefined;
    }

    const targetElement = document.getElementById(
      getGroupedItemSubgroupAnchorId(pendingSubgroupScrollTarget.groupId, pendingSubgroupScrollTarget.subgroupId)
    );
    if (!targetElement) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      window.requestAnimationFrame(() => {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setPendingSubgroupScrollTarget(null);
      });
    }, 80);

    return () => window.clearTimeout(timeoutId);
  }, [inlineComposerSubgroupKey, pendingSubgroupScrollTarget, resolvedGroupedItemView, selectedGroupedItemGroup]);

  const isConfigDirty = useMemo(() => {
    const saved = [...savedConfigs].sort((a, b) => a.id.localeCompare(b.id));
    const draft = [...draftConfigs].sort((a, b) => a.id.localeCompare(b.id));
    return !hasSameSerializedList(saved, draft, serializeConfig);
  }, [draftConfigs, savedConfigs]);

  const isCardSectionDirty = (sectionId: LoyaltyCampaignCardSectionId): boolean => {
    const saved = savedCards.filter((card) => getCardSectionId(card) === sectionId);
    const draft = draftCards.filter((card) => getCardSectionId(card) === sectionId);
    return !hasSameCards(saved, draft);
  };

  const isAnyCardDirty = CARD_SECTION_IDS.some((sectionId) => isCardSectionDirty(sectionId));
  const isGroupedItemDirty = !hasSameCards(
    savedCards.filter((card) => (card.kind ?? 'BUNDLE') === 'ITEM' && (card.groupId ?? '').trim().length > 0),
    draftCards.filter((card) => (card.kind ?? 'BUNDLE') === 'ITEM' && (card.groupId ?? '').trim().length > 0)
  ) ||
    !hasSameSerializedList(sortDraftGroups(savedDraftGroups), sortDraftGroups(draftGroups), serializeDraftGroup) ||
    !hasSameSerializedList(
      sortDraftSubgroups(savedDraftSubgroups),
      sortDraftSubgroups(draftSubgroups),
      serializeDraftSubgroup
    );
  const isSingleItemDirty = !hasSameCards(
    savedCards.filter((card) => (card.kind ?? 'BUNDLE') === 'ITEM' && (card.groupId ?? '').trim().length === 0),
    draftCards.filter((card) => (card.kind ?? 'BUNDLE') === 'ITEM' && (card.groupId ?? '').trim().length === 0)
  );

  const hasAnyDraftChanges = isConfigDirty || isAnyCardDirty;
  const changedConfigCount = countChangedRows(draftConfigs, savedConfigs, serializeConfig);
  const changedCardCount = countChangedRows(draftCards, savedCards, serializeComparableCard);

  const isSubgroupDirty = (groupId: string, subgroupId: string): boolean => {
    const scopedDraftCards = draftCards.filter(
      (card) => (card.groupId ?? '').trim() === groupId && (card.subgroupId ?? '').trim() === subgroupId
    );
    const scopedSavedCards = savedCards.filter(
      (card) => (card.groupId ?? '').trim() === groupId && (card.subgroupId ?? '').trim() === subgroupId
    );
    const draftSubgroup = draftSubgroups.find(
      (subgroup) => subgroup.groupId === groupId && subgroup.subgroupId === subgroupId
    );
    const savedSubgroup = savedDraftSubgroups.find(
      (subgroup) => subgroup.groupId === groupId && subgroup.subgroupId === subgroupId
    );
    return (
      !hasSameCards(scopedSavedCards, scopedDraftCards) ||
      serializeDraftSubgroup(
        draftSubgroup ?? {
          groupId,
          subgroupId,
          subgroupName: '',
          subgroupDisplayOrder: null,
          startDate: null,
          endDate: null,
        }
      ) !==
        serializeDraftSubgroup(
          savedSubgroup ?? {
            groupId,
            subgroupId,
            subgroupName: '',
            subgroupDisplayOrder: null,
            startDate: null,
            endDate: null,
          }
        )
    );
  };

  const isSubgroupSettingsDirty = (groupId: string, subgroupId: string): boolean => {
    const scopedDraftCards = draftCards.filter(
      (card) => (card.groupId ?? '').trim() === groupId && (card.subgroupId ?? '').trim() === subgroupId
    );
    const scopedSavedCards = savedCards.filter(
      (card) => (card.groupId ?? '').trim() === groupId && (card.subgroupId ?? '').trim() === subgroupId
    );
    const subgroupCardSettingsChanged = scopedDraftCards.some((draftCard) => {
      const savedCard = scopedSavedCards.find((card) => card.id === draftCard.id);
      return (
        (savedCard?.subgroupName ?? null) !== (draftCard.subgroupName ?? null) ||
        (savedCard?.subgroupStartDate ?? null) !== (draftCard.subgroupStartDate ?? null) ||
        (savedCard?.subgroupEndDate ?? null) !== (draftCard.subgroupEndDate ?? null)
      );
    });

    const draftSubgroup = draftSubgroups.find(
      (subgroup) => subgroup.groupId === groupId && subgroup.subgroupId === subgroupId
    );
    const savedSubgroup = savedDraftSubgroups.find(
      (subgroup) => subgroup.groupId === groupId && subgroup.subgroupId === subgroupId
    );

    return (
      subgroupCardSettingsChanged ||
      serializeDraftSubgroup(
        draftSubgroup ?? {
          groupId,
          subgroupId,
          subgroupName: '',
          subgroupDisplayOrder: null,
          startDate: null,
          endDate: null,
        }
      ) !==
        serializeDraftSubgroup(
          savedSubgroup ?? {
            groupId,
            subgroupId,
            subgroupName: '',
            subgroupDisplayOrder: null,
            startDate: null,
            endDate: null,
          }
        )
    );
  };

  const getSubgroupChangeSummary = (groupId: string, subgroupId: string): string[] => {
    const draftSubgroup = draftSubgroups.find(
      (subgroup) => subgroup.groupId === groupId && subgroup.subgroupId === subgroupId
    );
    const savedSubgroup = savedDraftSubgroups.find(
      (subgroup) => subgroup.groupId === groupId && subgroup.subgroupId === subgroupId
    );
    const scopedDraftCards = draftCards.filter(
      (card) => (card.groupId ?? '').trim() === groupId && (card.subgroupId ?? '').trim() === subgroupId
    );
    const scopedSavedCards = savedCards.filter(
      (card) => (card.groupId ?? '').trim() === groupId && (card.subgroupId ?? '').trim() === subgroupId
    );

    const changes: string[] = [];
    const draftName = draftSubgroup?.subgroupName ?? scopedDraftCards[0]?.subgroupName ?? '';
    const savedName = savedSubgroup?.subgroupName ?? scopedSavedCards[0]?.subgroupName ?? '';
    if ((draftName || '') !== (savedName || '')) {
      changes.push('Name changed');
    }

    const draftStart = draftSubgroup?.startDate ?? scopedDraftCards[0]?.subgroupStartDate ?? null;
    const savedStart = savedSubgroup?.startDate ?? scopedSavedCards[0]?.subgroupStartDate ?? null;
    const draftEnd = draftSubgroup?.endDate ?? scopedDraftCards[0]?.subgroupEndDate ?? null;
    const savedEnd = savedSubgroup?.endDate ?? scopedSavedCards[0]?.subgroupEndDate ?? null;
    if (draftStart !== savedStart || draftEnd !== savedEnd) {
      changes.push('Schedule changed');
    }

    const itemFieldsChanged =
      scopedDraftCards.length !== scopedSavedCards.length ||
      scopedDraftCards.some((draftCard) => {
        const savedCard = scopedSavedCards.find((card) => card.id === draftCard.id);
        if (!savedCard) {
          return true;
        }
        return serializeComparableCard(savedCard) !== serializeComparableCard(draftCard);
      });
    if (itemFieldsChanged) {
      changes.push('Items changed');
    }

    return changes;
  };

  const getGroupDraftState = (groupId: string): 'draft' | 'changed' | null => {
    const scopedDraftCards = draftCards.filter((card) => (card.groupId ?? '').trim() === groupId);
    const scopedSavedCards = savedCards.filter((card) => (card.groupId ?? '').trim() === groupId);
    const draftGroup = draftGroups.find((group) => group.groupId === groupId);
    const savedGroup = savedDraftGroups.find((group) => group.groupId === groupId);
    const scopedDraftSubgroups = draftSubgroups.filter((subgroup) => subgroup.groupId === groupId);
    const scopedSavedSubgroups = savedDraftSubgroups.filter((subgroup) => subgroup.groupId === groupId);
    const hasServerData = scopedSavedCards.length > 0 || Boolean(savedGroup) || scopedSavedSubgroups.length > 0;
    const hasDraftData = scopedDraftCards.length > 0 || Boolean(draftGroup) || scopedDraftSubgroups.length > 0;
    const isDirty =
      !hasSameCards(scopedSavedCards, scopedDraftCards) ||
      serializeDraftGroup(draftGroup ?? { groupId, groupName: '', groupDisplayOrder: null, startDate: null, endDate: null }) !==
        serializeDraftGroup(savedGroup ?? { groupId, groupName: '', groupDisplayOrder: null, startDate: null, endDate: null }) ||
      !hasSameSerializedList(
        sortDraftSubgroups(scopedSavedSubgroups),
        sortDraftSubgroups(scopedDraftSubgroups),
        serializeDraftSubgroup
      );

    if (!hasServerData && hasDraftData) {
      return 'draft';
    }
    if (isDirty) {
      return 'changed';
    }
    return null;
  };

  const getSubgroupDraftState = (groupId: string, subgroupId: string): 'draft' | 'changed' | null => {
    const scopedDraftCards = draftCards.filter(
      (card) => (card.groupId ?? '').trim() === groupId && (card.subgroupId ?? '').trim() === subgroupId
    );
    const scopedSavedCards = savedCards.filter(
      (card) => (card.groupId ?? '').trim() === groupId && (card.subgroupId ?? '').trim() === subgroupId
    );
    const draftSubgroup = draftSubgroups.find(
      (subgroup) => subgroup.groupId === groupId && subgroup.subgroupId === subgroupId
    );
    const savedSubgroup = savedDraftSubgroups.find(
      (subgroup) => subgroup.groupId === groupId && subgroup.subgroupId === subgroupId
    );
    const hasServerData = scopedSavedCards.length > 0 || Boolean(savedSubgroup);
    const hasDraftData = scopedDraftCards.length > 0 || Boolean(draftSubgroup);
    const isDirty =
      !hasSameCards(scopedSavedCards, scopedDraftCards) ||
      serializeDraftSubgroup(
        draftSubgroup ?? {
          groupId,
          subgroupId,
          subgroupName: '',
          subgroupDisplayOrder: null,
          startDate: null,
          endDate: null,
        }
      ) !==
        serializeDraftSubgroup(
          savedSubgroup ?? {
            groupId,
            subgroupId,
            subgroupName: '',
            subgroupDisplayOrder: null,
            startDate: null,
            endDate: null,
          }
        );

    if (!hasServerData && hasDraftData) {
      return 'draft';
    }
    if (isDirty) {
      return 'changed';
    }
    return null;
  };

  const renderDraftStateBadge = (state: 'draft' | 'changed' | null) => {
    if (!state) {
      return null;
    }

    const isDraft = state === 'draft';
    return (
      <span
        style={{
          ...metaChipStyle,
          fontSize: scaled(11),
          padding: `${scaled(5)}px ${scaled(9)}px`,
          background: isDraft ? '#eff6ff' : '#fff7ed',
          borderColor: isDraft ? '#93c5fd' : '#fdba74',
          color: isDraft ? '#1d4ed8' : '#b45309',
          fontWeight: 700,
        }}
      >
        {isDraft ? 'Draft' : 'Changed'}
      </span>
    );
  };

  const buildRewardMutationPayload = (
    card: DraftLoyaltyCampaignCard
  ): Array<{ type: string; amount: number; itemId: string | null }> =>
    (card.draftRewardInputs ?? [])
      .map((reward) => ({
        type: reward.type,
        amount: reward.amount,
        itemId: reward.itemId || null,
      }))
      .filter(isDefined);

  const buildCardUpdateVariables = (
    savedCard: DraftLoyaltyCampaignCard | undefined,
    nextCard: DraftLoyaltyCampaignCard
  ): Record<string, unknown> => {
    const variables: Record<string, unknown> = {
      id: nextCard.id,
    };

    if (!savedCard || (savedCard.kind ?? 'BUNDLE') !== (nextCard.kind ?? 'BUNDLE')) {
      variables.kind = nextCard.kind ?? 'BUNDLE';
    }
    if (!savedCard || (savedCard.name ?? '') !== (nextCard.name ?? '')) {
      variables.name = nextCard.name.trim();
    }
    if (!savedCard || (savedCard.groupId ?? null) !== (nextCard.groupId ?? null)) {
      variables.groupId = nextCard.groupId ?? null;
    }
    if (!savedCard || (savedCard.groupName ?? null) !== (nextCard.groupName ?? null)) {
      variables.groupName = nextCard.groupName ?? null;
    }
    if (!savedCard || (savedCard.groupDisplayOrder ?? null) !== (nextCard.groupDisplayOrder ?? null)) {
      variables.groupDisplayOrder = nextCard.groupDisplayOrder ?? null;
    }
    if (!savedCard || (savedCard.groupStartDate ?? null) !== (nextCard.groupStartDate ?? null)) {
      variables.groupStartDate = nextCard.groupStartDate ?? null;
    }
    if (!savedCard || (savedCard.groupEndDate ?? null) !== (nextCard.groupEndDate ?? null)) {
      variables.groupEndDate = nextCard.groupEndDate ?? null;
    }
    if (!savedCard || (savedCard.subgroupId ?? null) !== (nextCard.subgroupId ?? null)) {
      variables.subgroupId = nextCard.subgroupId ?? null;
    }
    if (!savedCard || (savedCard.subgroupName ?? null) !== (nextCard.subgroupName ?? null)) {
      variables.subgroupName = nextCard.subgroupName ?? null;
    }
    if (!savedCard || (savedCard.subgroupDisplayOrder ?? null) !== (nextCard.subgroupDisplayOrder ?? null)) {
      variables.subgroupDisplayOrder = nextCard.subgroupDisplayOrder ?? null;
    }
    if (!savedCard || (savedCard.subgroupStartDate ?? null) !== (nextCard.subgroupStartDate ?? null)) {
      variables.subgroupStartDate = nextCard.subgroupStartDate ?? null;
    }
    if (!savedCard || (savedCard.subgroupEndDate ?? null) !== (nextCard.subgroupEndDate ?? null)) {
      variables.subgroupEndDate = nextCard.subgroupEndDate ?? null;
    }
    if (!savedCard || (savedCard.itemDisplayOrder ?? null) !== (nextCard.itemDisplayOrder ?? null)) {
      variables.itemDisplayOrder = nextCard.itemDisplayOrder ?? null;
    }
    if (!savedCard || Number(savedCard.cost ?? 0) !== Number(nextCard.cost ?? 0)) {
      variables.cost = Number(nextCard.cost);
    }
    if (
      !savedCard ||
      Number(savedCard.itemBonusLoyaltyPoints ?? 0) !== Number(nextCard.itemBonusLoyaltyPoints ?? 0)
    ) {
      variables.itemBonusLoyaltyPoints = nextCard.itemBonusLoyaltyPoints ?? null;
    }
    if (!savedCard || (savedCard.displayOrder ?? 0) !== (nextCard.displayOrder ?? 0)) {
      variables.displayOrder = nextCard.displayOrder ?? 0;
    }
    if (!savedCard || (savedCard.maxClaims ?? 1) !== (nextCard.maxClaims ?? 1)) {
      variables.maxClaims = nextCard.maxClaims ?? 1;
    }
    if (!savedCard || !sameRewardList(savedCard.rewards, nextCard.rewards)) {
      variables.rewards = buildRewardMutationPayload(nextCard);
    }

    return variables;
  };

  const clearConfigForm = () => {
    setConfigAmount('0');
    setBulkSkusText('');
    setSelectedSku('');
    setConfigModalMode('create');
    setConfigGroupAmount(null);
    setEditingGroupRows([]);
  };

  const openCreateConfigModal = () => {
    clearConfigForm();
    setConfigModalMode('create');
    setConfigGroupAmount(null);
    setShowConfigModal(true);
    setError(null);
  };

  const onSaveConfigDraft = () => {
    setError(null);
    setNotice(null);
    const amount = Number(configAmount);
    if (!Number.isFinite(amount)) {
      setError('Invalid points amount.');
      return;
    }

    if (configModalMode === 'groupEdit') {
      if (editingGroupRows.length === 0) {
        setError('No SKU group selected.');
        return;
      }

      const editingIds = new Set(editingGroupRows.map((row) => row.id));
      setDraftConfigs((prev) =>
        prev.map((row) => (editingIds.has(row.id) ? { ...row, amount } : row)).sort((a, b) => a.id.localeCompare(b.id))
      );
      setShowConfigModal(false);
      clearConfigForm();
      setNotice('Mapping group updated in draft. Click Save in section or Save All to persist.');
      return;
    }

    const ids = parseSkus(bulkSkusText);
    if (ids.length === 0) {
      setError('Add at least one SKU.');
      return;
    }

    const conflictingSkus = ids
      .map((id) => {
        const existing = draftConfigs.find((row) => row.id === id);
        if (!existing || existing.amount === amount) {
          return null;
        }
        return `${id} (${existing.amount} points)`;
      })
      .filter((value): value is string => value !== null);

    if (conflictingSkus.length > 0) {
      setError(
        `These SKU are already mapped in another group: ${conflictingSkus.join(
          ', '
        )}. Remove them from the other group first.`
      );
      return;
    }

    setDraftConfigs((prev) => {
      const map = new Map(prev.map((row) => [row.id, row]));
      for (const id of ids) {
        map.set(id, { id, amount });
      }
      return Array.from(map.values()).sort((a, b) => a.id.localeCompare(b.id));
    });
    setShowConfigModal(false);
    clearConfigForm();
    setNotice('Mapping changes staged locally. Click Save in section or Save All to persist.');
  };

  const onDeleteConfigDraft = (id: string) => {
    const shouldDelete = window.confirm(`Delete SKU config "${id}" from draft?`);
    if (!shouldDelete) {
      return;
    }

    setError(null);
    setNotice(null);
    setDeletingConfigId(id);
    setDraftConfigs((prev) => prev.filter((row) => row.id !== id));
    setDeletingConfigId(null);
    setNotice('Config removed from draft. Click Save in section or Save All to persist.');
  };

  const onEditGroupAmount = (rows: LoyaltyCampaignConfigRow[]) => {
    if (rows.length === 0) {
      return;
    }
    clearConfigForm();
    setConfigModalMode('groupEdit');
    setEditingGroupRows(rows);
    setConfigAmount(String(rows[0]?.amount ?? 0));
    setBulkSkusText(rows.map((row) => row.id).join('\n'));
    setShowConfigModal(true);
    setError(null);
    setNotice(null);
  };

  const onDeleteGroupDraft = (rows: LoyaltyCampaignConfigRow[]) => {
    if (rows.length === 0) {
      return;
    }
    const shouldDelete = window.confirm(`Delete this group and remove ${rows.length} SKU config(s) from draft?`);
    if (!shouldDelete) {
      return;
    }

    const rowIds = new Set(rows.map((row) => row.id));
    setError(null);
    setNotice(null);
    setDeletingConfigId(`group:${rows[0].amount}`);
    setDraftConfigs((prev) => prev.filter((row) => !rowIds.has(row.id)));
    setDeletingConfigId(null);
    setNotice('Mapping group removed from draft. Click Save in section or Save All to persist.');
  };

  const onAddSkuToGroup = (amount: number) => {
    clearConfigForm();
    setConfigModalMode('groupAdd');
    setConfigAmount(String(amount));
    setConfigGroupAmount(amount);
    setShowConfigModal(true);
    setError(null);
    setNotice(null);
  };

  const clearCardForm = () => {
    setEditingCardId(null);
    setCardName('');
    setCardKind('BUNDLE');
    setCardGroupId('');
    setCardGroupName('');
    setCardGroupDisplayOrder('');
    setCardSubgroupId('');
    setCardSubgroupName('');
    setCardSubgroupDisplayOrder('');
    setItemCardCategoryPreset(null);
    setVanityItemTypePreset(null);
    setCardCost('0');
    setCardItemBaseStoreOfferId('');
    setCardItemSKUPrice('');
    setCardItemBonusLoyaltyPoints('');
    setCardClaimType('ONE_TIME');
    setCardMaxClaims('3');
    setCardUseSchedule(false);
    setCardStartDate(moment.utc().startOf('day').toDate());
    setCardEndDate(null);
    setCardRewards([]);
    setShowRewardModal(false);
    setSelectedReward(null);
    setEditingRewardIndex(null);
    setInlineComposerSubgroupKey(null);
    setError(null);
  };

  const clearGroupForm = () => {
    setEditingGroup(null);
    setShowGroupModal(false);
  };

  const openEditGroupModal = (group: LoyaltyCampaignItemGroup) => {
    if (group.id.startsWith('ungrouped:')) {
      return;
    }
    const firstCard = group.cards[0];
    setEditingGroup({
      originalGroupId: group.id,
      groupId: group.id,
      groupName: group.name,
      groupDisplayOrder:
        group.groupDisplayOrder === Number.MAX_SAFE_INTEGER ? '' : String(group.groupDisplayOrder),
      useSchedule: Boolean(group.startDate || group.endDate),
      startDate: group.startDate ? new Date(group.startDate) : moment.utc().startOf('day').toDate(),
      endDate: group.endDate ? new Date(group.endDate) : null,
    });
    setShowGroupModal(true);
    setError(null);
    setNotice(null);
  };

  const getLifecycleStateFromSchedule = (startDate?: string | null, endDate?: string | null): CardLifecycleState => {
    const now = moment.utc();
    const startMoment = startDate ? moment.utc(startDate) : null;
    const endMoment = endDate ? moment.utc(endDate) : null;

    if (endMoment && now.isSameOrAfter(endMoment)) {
      return 'ended';
    }
    if (startMoment && now.isBefore(startMoment)) {
      return 'scheduled';
    }
    return 'active';
  };

  const saveGroupDraft = () => {
    if (!editingGroup) {
      return;
    }
    if (editingGroup.groupId.trim().length === 0) {
      setError('Group ID is required.');
      return;
    }
    if (editingGroup.groupName.trim().length === 0) {
      setError('Group Name is required.');
      return;
    }
    if (editingGroup.useSchedule && !editingGroup.startDate) {
      setError('Group start date is required.');
      return;
    }
    if (
      editingGroup.useSchedule &&
      editingGroup.startDate &&
      editingGroup.endDate &&
      editingGroup.endDate.getTime() < editingGroup.startDate.getTime()
    ) {
      setError('Group end date must be later than or equal to start date.');
      return;
    }

    const normalizedGroupId = editingGroup.groupId.trim();
    const normalizedGroupName = editingGroup.groupName.trim();
    const normalizedGroupDisplayOrder =
      editingGroup.groupDisplayOrder.trim() === '' ? null : Number(editingGroup.groupDisplayOrder);
    const normalizedStartDate =
      editingGroup.useSchedule && editingGroup.startDate ? editingGroup.startDate.toISOString() : null;
    const normalizedEndDate =
      editingGroup.useSchedule && editingGroup.endDate ? editingGroup.endDate.toISOString() : null;
    const existingGroup = buildItemGroups(
      draftCards.filter((card) => card.kind === 'ITEM' && (card.groupId ?? '').trim().length > 0),
      draftGroups,
      draftSubgroups
    ).find((group) => group.id === editingGroup.originalGroupId);
    const groupScheduleError = getGroupScheduleValidationError({
      group: existingGroup,
      startDate: normalizedStartDate,
      endDate: normalizedEndDate,
    });
    if (groupScheduleError) {
      setError(groupScheduleError);
      return;
    }
    const shouldCreateFirstItem = isDraftGroupId(editingGroup.originalGroupId);
    const hasGroupIdConflict = draftCards.some(
      (card) =>
        card.kind === 'ITEM' &&
        (card.groupId ?? '').trim() === normalizedGroupId &&
        (card.groupId ?? '').trim() !== editingGroup.originalGroupId
    );
    if (hasGroupIdConflict) {
      setError('Group ID already exists. Use a different group or add items to the existing one.');
      return;
    }

    setDraftCards((prev) =>
      prev.map((card) =>
        card.kind === 'ITEM' && (card.groupId ?? '').trim() === editingGroup.originalGroupId
          ? {
              ...card,
              groupId: normalizedGroupId,
              groupName: normalizedGroupName,
              groupDisplayOrder: normalizedGroupDisplayOrder,
              groupStartDate: normalizedStartDate,
              groupEndDate: normalizedEndDate,
            }
          : card
      )
    );
    setDraftGroups((prev) => {
      const nextGroup: DraftLoyaltyCampaignGroup = {
        groupId: normalizedGroupId,
        groupName: normalizedGroupName,
        groupDisplayOrder: normalizedGroupDisplayOrder,
        startDate: normalizedStartDate,
        endDate: normalizedEndDate,
      };
      const withoutOld = prev.filter((group) => group.groupId !== editingGroup.originalGroupId);
      return [...withoutOld, nextGroup].sort((left, right) => {
        const leftOrder = left.groupDisplayOrder ?? Number.MAX_SAFE_INTEGER;
        const rightOrder = right.groupDisplayOrder ?? Number.MAX_SAFE_INTEGER;
        if (leftOrder !== rightOrder) {
          return leftOrder - rightOrder;
        }
        return left.groupName.localeCompare(right.groupName);
      });
    });
    setDraftSubgroups((prev) =>
      prev.map((subgroup) =>
        subgroup.groupId === editingGroup.originalGroupId
          ? {
              ...subgroup,
              groupId: normalizedGroupId,
            }
          : subgroup
      )
    );
    setNotice(
      shouldCreateFirstItem
        ? 'Group added to draft. Add the first item to continue.'
        : 'Group changes saved locally. Click Save in section or Save All to persist.'
    );
    setActiveSection('grouped-items');
    if (shouldCreateFirstItem) {
      setShowDraftGroupedItemsOnly(true);
      setSelectedGroupedItemView({
        state: getLifecycleStateFromSchedule(normalizedStartDate, normalizedEndDate),
        groupId: normalizedGroupId,
      });
    } else {
      setSelectedGroupedItemView((prev) =>
        prev && prev.groupId === editingGroup.originalGroupId
          ? {
              ...prev,
              groupId: normalizedGroupId,
            }
          : prev
      );
    }
    clearGroupForm();
  };

  const deleteGroupDraft = (group: LoyaltyCampaignItemGroup) => {
    if (group.id.startsWith('ungrouped:')) {
      return;
    }
    const shouldDelete = window.confirm(`Delete group "${group.name}" and all items inside it from draft?`);
    if (!shouldDelete) {
      return;
    }
    setDraftCards((prev) =>
      prev.filter((card) => !(card.kind === 'ITEM' && (card.groupId ?? '').trim() === group.id))
    );
    setDraftGroups((prev) => prev.filter((draftGroup) => draftGroup.groupId !== group.id));
    setDraftSubgroups((prev) => prev.filter((subgroup) => subgroup.groupId !== group.id));
    setNotice('Group removed from draft. Click Save in section or Save All to persist.');
  };

  const deleteSubgroupDraft = (groupId: string, subgroup: LoyaltyCampaignItemSubgroup) => {
    const shouldDelete = window.confirm(`Delete subgroup "${subgroup.name}" and all items inside it from draft?`);
    if (!shouldDelete) {
      return;
    }

    setDraftCards((prev) =>
      prev.filter(
        (card) =>
          !((card.groupId ?? '').trim() === groupId && (card.subgroupId ?? '').trim() === subgroup.id)
      )
    );
    setDraftSubgroups((prev) =>
      prev.filter(
        (draftSubgroup) => !(draftSubgroup.groupId === groupId && draftSubgroup.subgroupId === subgroup.id)
      )
    );
    if (inlineComposerSubgroupKey === `${groupId}:${subgroup.id}`) {
      clearCardForm();
    }
    setNotice('Subgroup removed from draft. Click Save to persist the change.');
  };

  const openCreateCardModal = (
    kind: LoyaltyCampaignCardKind,
    itemCategory?: LoyaltyCampaignItemCategory,
    vanityItemType?: VanityItemType,
    groupPreset?: {
      groupId: string;
      groupName: string;
      groupDisplayOrder?: number | null;
      subgroupId?: string | null;
      subgroupName?: string | null;
      subgroupDisplayOrder?: number | null;
      useSchedule?: boolean;
      startDate?: Date | null;
      endDate?: Date | null;
    }
  ) => {
    clearCardForm();
    setCardKind(kind);
    setCardGroupId(groupPreset?.groupId ?? '');
    setCardGroupName(groupPreset?.groupName ?? '');
    setCardGroupDisplayOrder(
      groupPreset?.groupDisplayOrder === null || groupPreset?.groupDisplayOrder === undefined
        ? ''
        : String(groupPreset.groupDisplayOrder)
    );
    setCardSubgroupId(groupPreset?.subgroupId ?? '');
    setCardSubgroupName(groupPreset?.subgroupName ?? '');
    setCardSubgroupDisplayOrder(
      groupPreset?.subgroupDisplayOrder === null || groupPreset?.subgroupDisplayOrder === undefined
        ? ''
        : String(groupPreset.subgroupDisplayOrder)
    );
    setItemCardCategoryPreset(kind === 'ITEM' ? itemCategory ?? null : null);
    setVanityItemTypePreset(kind === 'ITEM' && itemCategory === 'VANITY' ? vanityItemType ?? null : null);
    if (kind === 'ITEM' && groupPreset) {
      setCardUseSchedule(Boolean(groupPreset.useSchedule));
      setCardStartDate(groupPreset.startDate ?? moment.utc().startOf('day').toDate());
      setCardEndDate(groupPreset.endDate ?? null);
    }
    setShowCardModal(true);
  };

  const openCreateGroupModal = () => {
    const nextGroupNumber =
      buildItemGroups(
        draftCards.filter((card) => card.kind === 'ITEM' && (card.groupId ?? '').trim().length > 0),
        draftGroups,
        draftSubgroups
      ).length + 1;
    const generatedGroupId = generateGroupId();
    setEditingGroup({
      originalGroupId: `${DRAFT_GROUP_ID_PREFIX}${Date.now()}`,
      groupId: generatedGroupId,
      groupName: `Item Group ${nextGroupNumber}`,
      groupDisplayOrder: String(
        groupedItemCardsByState.active.length +
          groupedItemCardsByState.scheduled.length +
          groupedItemCardsByState.ended.length
      ),
      useSchedule: false,
      startDate: moment.utc().startOf('day').toDate(),
      endDate: null,
    });
    setShowGroupModal(true);
    setError(null);
    setNotice(null);
  };

  const openInlineItemComposer = (group: LoyaltyCampaignItemGroup, subgroup: LoyaltyCampaignItemSubgroup) => {
    clearCardForm();
    setCardKind('ITEM');
    setCardGroupId(group.id);
    setCardGroupName(group.name);
    setCardGroupDisplayOrder(
      group.groupDisplayOrder === Number.MAX_SAFE_INTEGER ? '' : String(group.groupDisplayOrder)
    );
    setCardSubgroupId(subgroup.id);
    setCardSubgroupName(subgroup.name);
    setCardSubgroupDisplayOrder(
      subgroup.subgroupDisplayOrder === Number.MAX_SAFE_INTEGER ? '' : String(subgroup.subgroupDisplayOrder)
    );
    setCardUseSchedule(Boolean(subgroup.startDate || subgroup.endDate));
    setCardStartDate(
      subgroup.startDate
        ? new Date(subgroup.startDate)
        : group.startDate
          ? new Date(group.startDate)
          : moment.utc().startOf('day').toDate()
    );
    setCardEndDate(subgroup.endDate ? new Date(subgroup.endDate) : group.endDate ? new Date(group.endDate) : null);
    setInlineComposerSubgroupKey(`${group.id}:${subgroup.id}`);
    setShowCardModal(false);
    setError(null);
    setNotice(null);
  };

  const openCreateSubgroupItemModal = (group: LoyaltyCampaignItemGroup) => {
    const nextSubgroupIndex = group.subgroups.filter((subgroup) => !subgroup.id.startsWith('ungrouped:')).length + 1;
    const nextSubgroup: DraftLoyaltyCampaignSubgroup = {
      groupId: group.id,
      subgroupId: `${group.id}-subgroup-${nextSubgroupIndex}`,
      subgroupName:
        subgroupNameOptions[(nextSubgroupIndex - 1) % subgroupNameOptions.length] ?? subgroupNameOptions[0] ?? '',
      subgroupDisplayOrder: nextSubgroupIndex - 1,
      startDate: group.startDate,
      endDate: group.endDate,
    };
    setDraftSubgroups((prev) => sortDraftSubgroups([...prev, nextSubgroup]));
    setActiveSection('grouped-items');
    setShowDraftGroupedItemsOnly(false);
    setSelectedGroupedItemView({
      state: getLifecycleStateFromSchedule(group.startDate, group.endDate),
      groupId: group.id,
    });
    setCollapsedGroups((prev) => ({
      ...prev,
      [group.id]: false,
    }));
    setPendingSubgroupScrollTarget({ groupId: group.id, subgroupId: nextSubgroup.subgroupId });
    setNotice('Subgroup added to draft. Rename it inline or add the first item.');
    openInlineItemComposer(group, {
      id: nextSubgroup.subgroupId,
      name: nextSubgroup.subgroupName,
      subgroupDisplayOrder: nextSubgroup.subgroupDisplayOrder ?? Number.MAX_SAFE_INTEGER,
      startDate: nextSubgroup.startDate,
      endDate: nextSubgroup.endDate,
      cards: [],
      isDraftPlaceholder: true,
    });
  };

  const openCreateItemModalForSubgroup = (group: LoyaltyCampaignItemGroup, subgroup: LoyaltyCampaignItemSubgroup) => {
    openInlineItemComposer(group, subgroup);
  };

  const updateSubgroupDraft = (
    group: LoyaltyCampaignItemGroup,
    subgroup: LoyaltyCampaignItemSubgroup,
    updates: Partial<DraftLoyaltyCampaignSubgroup>
  ) => {
    const nextStartDate = updates.startDate === undefined ? subgroup.startDate : updates.startDate;
    const nextEndDate = updates.endDate === undefined ? subgroup.endDate : updates.endDate;

    const validationErrors = getSubgroupValidationErrors(group, {
      startDate: nextStartDate ?? null,
      endDate: nextEndDate ?? null,
    });
    setError(validationErrors[0]?.message ?? null);
    setDraftSubgroups((prev) => {
      const nextDraftSubgroup: DraftLoyaltyCampaignSubgroup = {
        groupId: group.id,
        subgroupId: subgroup.id,
        subgroupName: updates.subgroupName ?? subgroup.name,
        subgroupDisplayOrder:
          updates.subgroupDisplayOrder ??
          (subgroup.subgroupDisplayOrder === Number.MAX_SAFE_INTEGER
            ? null
            : subgroup.subgroupDisplayOrder),
        startDate: nextStartDate ?? null,
        endDate: nextEndDate ?? null,
      };

      const existingIndex = prev.findIndex(
        (draftSubgroup) => draftSubgroup.groupId === group.id && draftSubgroup.subgroupId === subgroup.id
      );
      if (existingIndex === -1) {
        return sortDraftSubgroups([...prev, nextDraftSubgroup]);
      }
      return sortDraftSubgroups(
        prev.map((draftSubgroup, index) => (index === existingIndex ? nextDraftSubgroup : draftSubgroup))
      );
    });

    setDraftCards((prev) =>
      prev.map((card) =>
        (card.groupId ?? '').trim() === group.id && (card.subgroupId ?? '').trim() === subgroup.id
          ? {
              ...card,
              subgroupName: updates.subgroupName ?? subgroup.name,
              subgroupStartDate: nextStartDate ?? null,
              subgroupEndDate: nextEndDate ?? null,
              startDate: nextStartDate ?? group.startDate ?? null,
              endDate: nextEndDate ?? group.endDate ?? null,
            }
          : card
      )
    );
  };

  const resetSubgroupDraft = (groupId: string, subgroupId: string) => {
    const savedSubgroup =
      savedDraftSubgroups.find((subgroup) => subgroup.groupId === groupId && subgroup.subgroupId === subgroupId) ?? null;
    const savedCardsInSubgroup = savedCards.filter(
      (card) => (card.groupId ?? '').trim() === groupId && (card.subgroupId ?? '').trim() === subgroupId
    );
    const fallbackSavedCard = savedCardsInSubgroup[0] ?? null;
    const resetSubgroupName = savedSubgroup?.subgroupName ?? fallbackSavedCard?.subgroupName ?? null;
    const resetSubgroupStartDate = savedSubgroup?.startDate ?? fallbackSavedCard?.subgroupStartDate ?? null;
    const resetSubgroupEndDate = savedSubgroup?.endDate ?? fallbackSavedCard?.subgroupEndDate ?? null;

    setError(null);
    setDraftSubgroups((prev) => {
      const filtered = prev.filter((draftSubgroup) => !(draftSubgroup.groupId === groupId && draftSubgroup.subgroupId === subgroupId));
      return savedSubgroup ? sortDraftSubgroups([...filtered, savedSubgroup]) : filtered;
    });

    setDraftCards((prev) => {
      const cardsOutsideSubgroup = prev.filter(
        (card) => (card.groupId ?? '').trim() !== groupId || (card.subgroupId ?? '').trim() !== subgroupId
      );
      const restoredSubgroupCards = savedCardsInSubgroup.map((card) => ({
        ...card,
        subgroupName: resetSubgroupName,
        subgroupStartDate: resetSubgroupStartDate,
        subgroupEndDate: resetSubgroupEndDate,
        rewards: [...(card.rewards ?? [])],
        draftRewardInputs: [...(card.draftRewardInputs ?? [])],
      }));

      return sortComparableCards([...cardsOutsideSubgroup, ...restoredSubgroupCards]);
    });
  };

  const onChangeDirectCurrencyAmount = (rawAmount: string) => {
    if (!directCurrencyRewardType) {
      return;
    }

    if (!rawAmount.trim()) {
      setCardRewards([]);
      return;
    }

    const amount = Number(rawAmount);
    setCardRewards([
      {
        type: directCurrencyRewardType,
        amount: Number.isFinite(amount) ? amount : 0,
        itemId: '',
      },
    ]);
  };

  const onSaveRewardModal = (newReward: LevelRewardInput) => {
    const nextReward = fromLevelRewardInputToRewardForm(newReward);
    if (editingRewardIndex !== null) {
      setCardRewards((prev) => prev.map((r, idx) => (idx === editingRewardIndex ? nextReward : r)));
    } else {
      setCardRewards((prev) => [...prev, nextReward]);
    }
    setShowRewardModal(false);
    setSelectedReward(null);
    setEditingRewardIndex(null);
    setError(null);
  };

  const onOpenAddRewardModal = () => {
    if (cardKind === 'ITEM' && cardRewards.length >= 1) {
      setError('Campaign card only supports one item. Remove the current item before adding another.');
      return;
    }
    setEditingRewardIndex(null);
    setSelectedReward(null);
    setShowRewardModal(true);
  };

  const onOpenEditRewardModal = (index: number) => {
    const reward = cardRewards[index];
    if (!reward) {
      return;
    }
    setSelectedReward({
      ...fromRewardFormToLevelRewardInput(reward),
      itemName: getDraftRewardSelectionLabel(reward) ?? undefined,
    });
    setEditingRewardIndex(index);
    setShowRewardModal(true);
  };

  const onSaveCardDraft = () => {
    setError(null);
    setNotice(null);
    if (cardUseSchedule && !cardStartDate) {
      setError('Start date is required.');
      return;
    }
    if (cardUseSchedule && cardStartDate && cardEndDate && cardEndDate.getTime() < cardStartDate.getTime()) {
      setError('End date must be later than or equal to start date.');
      return;
    }
    if (cardRewards.length === 0) {
      setError(cardKind === 'ITEM' ? 'Add one item first.' : 'Add at least one reward first.');
      return;
    }
    if (cardKind === 'ITEM' && cardRewards.length > 1) {
      setError('Campaign card only supports one item. Bundles are not allowed.');
      return;
    }
    const invalidRewardMessage = cardRewards.map(getDraftRewardValidationError).find((message) => message !== null);
    if (invalidRewardMessage) {
      setError(invalidRewardMessage);
      return;
    }
    if (cardKind === 'ITEM' && cardGroupId.trim().length === 0) {
      setError('Group ID is required for item cards.');
      return;
    }
    if (cardKind === 'ITEM' && cardGroupName.trim().length === 0) {
      setError('Group Name is required for item cards.');
      return;
    }
    if (cardKind === 'ITEM' && cardSubgroupName.trim().length === 0) {
      setError('Subgroup Name is required for item cards.');
      return;
    }
    if (cardClaimType === 'CUSTOM' && (!Number.isFinite(Number(cardMaxClaims)) || Number(cardMaxClaims) <= 1)) {
      setError('Custom claim value must be greater than 1.');
      return;
    }
    if (!isWholeNumberString(cardCost)) {
      setError('Loyalty Point Cost must be a whole number 0 or higher.');
      return;
    }
    if (Number(cardCost) < 0) {
      setError('Loyalty Point Cost must be a whole number 0 or higher.');
      return;
    }
    if (cardItemBonusLoyaltyPoints.trim().length > 0 && !isWholeNumberString(cardItemBonusLoyaltyPoints)) {
      setError('Bonus Loyalty Points must be a whole number 0 or higher.');
      return;
    }
    if (Number(cardCost) > 0 && cardItemSKUPrice.trim().length > 0) {
      setError('Choose only one pricing mode: Loyalty Point Cost or Store Offer / SKU.');
      return;
    }
    if (Number(cardCost) <= 0 && cardItemSKUPrice.trim().length === 0) {
      setError('Set Loyalty Point Cost or Store Offer / SKU.');
      return;
    }
    if (cardItemBaseStoreOfferId.trim().length > 0 && cardItemSKUPrice.trim().length === 0) {
      setError('Set Active Store Offer before setting Base Store Offer.');
      return;
    }
    if (
      cardItemBaseStoreOfferId.trim().length > 0 &&
      cardItemBaseStoreOfferId.trim() === cardItemSKUPrice.trim()
    ) {
      setError('Base Store Offer must be different from Active Store Offer.');
      return;
    }
    const shouldCloseCreateGroupModal =
      cardKind === 'ITEM' &&
      !!editingGroup &&
      isDraftGroupId(editingGroup.originalGroupId) &&
      cardGroupId.trim() === editingGroup.groupId.trim();

    const normalizedMaxClaims =
      cardClaimType === 'INFINITE' ? 0 : cardClaimType === 'CUSTOM' ? Math.max(2, Math.floor(Number(cardMaxClaims))) : 1;

    const existingCard = editingCardId ? draftCards.find((card) => card.id === editingCardId) : undefined;
    const normalizedGroupId = cardKind === 'ITEM' ? cardGroupId.trim() : '';
    const normalizedGroupName = cardKind === 'ITEM' ? cardGroupName.trim() : '';
    const normalizedSubgroupName = cardKind === 'ITEM' ? cardSubgroupName.trim() : '';
    const generatedSubgroupId = normalizedSubgroupName ? toSubgroupId(normalizedSubgroupName) : '';
    const normalizedSubgroupId =
      cardKind === 'ITEM' ? cardSubgroupId.trim() || generatedSubgroupId || `${normalizedGroupId}-subgroup` : '';
    const existingItemGroups = buildItemGroups(
      draftCards.filter((card) => card.kind === 'ITEM' && (card.groupId ?? '').trim().length > 0),
      draftGroups,
      draftSubgroups
    );
    const selectedGroup = existingItemGroups.find((group) => group.id === normalizedGroupId);
    const selectedSubgroup = selectedGroup?.subgroups.find((subgroup) => subgroup.id === normalizedSubgroupId);
    const itemScheduleError =
      cardKind === 'ITEM' && cardUseSchedule
        ? getItemScheduleValidationError({
            group: selectedGroup,
            subgroup: selectedSubgroup,
            startDate: cardStartDate ? cardStartDate.toISOString() : null,
            endDate: cardEndDate ? cardEndDate.toISOString() : null,
          })
        : null;
    if (itemScheduleError) {
      setError(itemScheduleError);
      return;
    }
    const derivedGroupDisplayOrder =
      cardKind === 'ITEM'
        ? existingCard?.groupDisplayOrder ??
          (cardGroupDisplayOrder.trim() !== ''
            ? Number(cardGroupDisplayOrder)
            : draftCards.find((card) => (card.groupId ?? '').trim() === normalizedGroupId)?.groupDisplayOrder ??
              groupedItemCardsByState.active.length +
                groupedItemCardsByState.scheduled.length +
                groupedItemCardsByState.ended.length)
        : null;
    const derivedSubgroupDisplayOrder =
      cardKind === 'ITEM'
        ? existingCard?.subgroupDisplayOrder ??
          (cardSubgroupDisplayOrder.trim() !== ''
            ? Number(cardSubgroupDisplayOrder)
            : draftCards.filter(
                (card) =>
                  (card.groupId ?? '').trim() === normalizedGroupId && (card.subgroupId ?? '').trim().length > 0
              ).length)
        : null;
    const derivedItemDisplayOrder =
      cardKind === 'ITEM'
        ? existingCard?.itemDisplayOrder ??
          draftCards.filter(
            (card) =>
              (card.groupId ?? '').trim() === normalizedGroupId &&
              (card.subgroupId ?? '').trim() === normalizedSubgroupId
          ).length
        : null;
    const nextCard: DraftLoyaltyCampaignCard = {
      id: editingCardId ?? `${DRAFT_CARD_ID_PREFIX}${Date.now()}`,
      kind: cardKind,
      displayOrder: existingCard?.displayOrder,
      name: cardName.trim(),
      groupId: cardKind === 'ITEM' ? normalizedGroupId || null : null,
      groupName: cardKind === 'ITEM' ? normalizedGroupName || null : null,
      groupDisplayOrder: derivedGroupDisplayOrder,
      subgroupId: cardKind === 'ITEM' ? normalizedSubgroupId || null : null,
      subgroupName: cardKind === 'ITEM' ? normalizedSubgroupName || null : null,
      subgroupDisplayOrder: derivedSubgroupDisplayOrder,
      subgroupStartDate: cardKind === 'ITEM' && cardUseSchedule && cardStartDate ? cardStartDate.toISOString() : null,
      subgroupEndDate: cardKind === 'ITEM' && cardUseSchedule && cardEndDate ? cardEndDate.toISOString() : null,
      itemDisplayOrder: derivedItemDisplayOrder,
      cost: Number(cardCost),
      itemBonusLoyaltyPoints: cardItemBonusLoyaltyPoints.trim() ? Number(cardItemBonusLoyaltyPoints) : null,
      claimType: normalizedMaxClaims === 0 ? 'INFINITE' : 'ONE_TIME',
      maxClaims: normalizedMaxClaims,
      rewards: cardRewards.map(fromRewardFormToSavedReward),
      draftRewardInputs: [...cardRewards],
    };

    if (editingCardId) {
      if (existingCard && serializeComparableCard(existingCard) === serializeComparableCard(nextCard)) {
        setShowCardModal(false);
        clearCardForm();
        setNotice('No card changes detected.');
        return;
      }
    }

    setDraftCards((prev) => {
      if (editingCardId) {
        return replaceCardAt(prev, editingCardId, () => nextCard);
      }
      return [...prev, nextCard];
    });
    if (cardKind === 'ITEM' && normalizedGroupId) {
      setDraftGroups((prev) => prev.filter((group) => group.groupId !== normalizedGroupId));
      setDraftSubgroups((prev) =>
        prev.filter(
          (subgroup) =>
            !(subgroup.groupId === normalizedGroupId && subgroup.subgroupId === normalizedSubgroupId)
        )
      );
    }

    setShowCardModal(false);
    clearCardForm();
    if (shouldCloseCreateGroupModal) {
      clearGroupForm();
      setNotice('Group added. Click Save in section or Save All to persist.');
      return;
    }

    setNotice('Card changes staged locally. Click Save in section or Save All to persist.');
  };

  const onEditCard = (card: DraftLoyaltyCampaignCard) => {
    setEditingCardId(card.id);
    setCardName(card.name ?? '');
    setCardKind(card.kind ?? 'BUNDLE');
    setCardGroupId(card.groupId ?? '');
    setCardGroupName(card.groupName ?? '');
    setCardGroupDisplayOrder(
      card.groupDisplayOrder === null || card.groupDisplayOrder === undefined ? '' : String(card.groupDisplayOrder)
    );
    setCardSubgroupId(card.subgroupId ?? '');
    setCardSubgroupName(card.subgroupName ?? '');
    setCardSubgroupDisplayOrder(
      card.subgroupDisplayOrder === null || card.subgroupDisplayOrder === undefined
        ? ''
        : String(card.subgroupDisplayOrder)
    );
    setItemCardCategoryPreset((card.kind ?? 'BUNDLE') === 'ITEM' ? getItemCategoryFromReward(card.rewards?.[0]) : null);
    setVanityItemTypePreset((card.kind ?? 'BUNDLE') === 'ITEM' ? getVanityItemTypeFromReward(card.rewards?.[0]) : null);
    setCardCost(String(card.cost ?? 0));
    setCardItemBaseStoreOfferId('');
    setCardItemSKUPrice('');
    setCardItemBonusLoyaltyPoints(card.itemBonusLoyaltyPoints ? String(card.itemBonusLoyaltyPoints) : '');
    setCardClaimType(getCardClaimMode(card.maxClaims));
    setCardMaxClaims(String(card.maxClaims && card.maxClaims > 1 ? card.maxClaims : 3));
    const cardScheduleStartDate = card.subgroupStartDate ?? null;
    const cardScheduleEndDate = card.subgroupEndDate ?? null;
    setCardUseSchedule(Boolean(card.subgroupStartDate || card.subgroupEndDate));
    setCardStartDate(cardScheduleStartDate ? new Date(cardScheduleStartDate) : moment.utc().startOf('day').toDate());
    setCardEndDate(cardScheduleEndDate ? new Date(cardScheduleEndDate) : null);
    setCardRewards(card.draftRewardInputs ?? []);
    setError(null);
    setNotice(null);
    if ((card.kind ?? 'BUNDLE') === 'ITEM' && (card.groupId ?? '').trim() && (card.subgroupId ?? '').trim()) {
      setInlineComposerSubgroupKey(`${card.groupId}:${card.subgroupId}`);
      setShowCardModal(false);
      return;
    }
    setShowCardModal(true);
  };

  const onDeleteCardDraft = (id: string) => {
    const shouldDelete = window.confirm('Delete this card from draft?');
    if (!shouldDelete) {
      return;
    }

    setError(null);
    setNotice(null);
    setDeletingCardId(id);
    setDraftCards((prev) => prev.filter((card) => card.id !== id));
    if (editingCardId === id) {
      clearCardForm();
      setShowCardModal(false);
    }
    setDeletingCardId(null);
    setNotice('Card removed from draft. Click Save in section or Save All to persist.');
  };

  const deleteAllGroupedItems = async () => {
    const groupedDraftCards = draftCards.filter(
      (card) => (card.kind ?? 'BUNDLE') === 'ITEM' && (card.groupId ?? '').trim().length > 0
    );
    if (groupedDraftCards.length === 0 && draftGroups.length === 0 && draftSubgroups.length === 0) {
      setNotice('No grouped items to delete.');
      return;
    }

    const shouldDelete = window.confirm('Delete all grouped items, groups, and subgroups?');
    if (!shouldDelete) {
      return;
    }

    setError(null);
    setNotice(null);
    setDeletingAllGrouped(true);

    try {
      const persistedGroupedIds = groupedDraftCards.filter((card) => !isDraftCardId(card.id)).map((card) => card.id);
      await Promise.all(persistedGroupedIds.map((id) => deleteCard({ variables: { id } })));

      const remainingDraftCards = draftCards.filter(
        (card) => !((card.kind ?? 'BUNDLE') === 'ITEM' && (card.groupId ?? '').trim().length > 0)
      );
      const remainingSavedCards = savedCards.filter(
        (card) => !((card.kind ?? 'BUNDLE') === 'ITEM' && (card.groupId ?? '').trim().length > 0)
      );

      setDraftCards(remainingDraftCards);
      setSavedCards(remainingSavedCards);
      setDraftGroups([]);
      setSavedDraftGroups([]);
      setDraftSubgroups([]);
      setSavedDraftSubgroups([]);
      clearCardForm();
      clearGroupForm();
      setNotice('All grouped items deleted.');
    } catch (e) {
      setError(`Failed to delete grouped items: ${String(e)}`);
    } finally {
      setDeletingAllGrouped(false);
    }
  };

  const clearGroupedDraft = () => {
    if (!isGroupedItemDirty) {
      setNotice('No grouped item draft to clear.');
      return;
    }

    const shouldClear = window.confirm('Discard all grouped item draft changes and restore the last saved version?');
    if (!shouldClear) {
      return;
    }

    setError(null);
    setNotice(null);
    setDraftCards((prev) => [
      ...prev.filter((card) => !((card.kind ?? 'BUNDLE') === 'ITEM' && (card.groupId ?? '').trim().length > 0)),
      ...savedCards.filter((card) => (card.kind ?? 'BUNDLE') === 'ITEM' && (card.groupId ?? '').trim().length > 0),
    ]);
    setDraftGroups(savedDraftGroups);
    setDraftSubgroups(savedDraftSubgroups);
    setShowDraftGroupedItemsOnly(false);
    clearCardForm();
    clearGroupForm();
    setNotice('Grouped item draft cleared.');
  };

  const onMoveCardDraft = (cardId: string, direction: 'up' | 'down', sectionId: LoyaltyCampaignCardSectionId) => {
    setError(null);
    setNotice(null);
    setMovingCardId(cardId);
    setDraftCards((prev) => {
      const sectionIndexes = prev.reduce<number[]>((list, card, index) => {
        if (getCardSectionId(card) === sectionId) {
          list.push(index);
        }
        return list;
      }, []);
      const currentSectionIndex = sectionIndexes.findIndex((index) => prev[index]?.id === cardId);
      const targetSectionIndex = direction === 'up' ? currentSectionIndex - 1 : currentSectionIndex + 1;
      if (currentSectionIndex === -1 || targetSectionIndex < 0 || targetSectionIndex >= sectionIndexes.length) {
        return prev;
      }

      const currentIndex = sectionIndexes[currentSectionIndex];
      const targetIndex = sectionIndexes[targetSectionIndex];
      const next = [...prev];
      const currentCard = next[currentIndex];
      const targetCard = next[targetIndex];
      if (!currentCard || !targetCard) {
        return prev;
      }
      next[currentIndex] = targetCard;
      next[targetIndex] = currentCard;
      return next;
    });
    setMovingCardId(null);
    setNotice('Card order updated in draft. Click Save in section or Save All to persist.');
  };

  const onMoveGroupDraft = (groupId: string, direction: 'up' | 'down') => {
    setError(null);
    setNotice(null);
    setMovingCardId(`group:${groupId}`);
    setDraftCards((prev) => {
      const groupedCards = prev.filter(
        (card) => (card.kind ?? 'BUNDLE') === 'ITEM' && (card.groupId ?? '').trim().length > 0
      );
      const groups = buildItemGroups(groupedCards);
      const currentIndex = groups.findIndex((group) => group.id === groupId);
      const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      if (currentIndex === -1 || targetIndex < 0 || targetIndex >= groups.length) {
        return prev;
      }

      const reorderedGroups = [...groups];
      const [movedGroup] = reorderedGroups.splice(currentIndex, 1);
      reorderedGroups.splice(targetIndex, 0, movedGroup);
      const groupOrderMap = reorderedGroups.reduce<Record<string, number>>((acc, group, index) => {
        acc[group.id] = index;
        return acc;
      }, {});

      return prev.map((card) => {
        const normalizedGroupId = (card.groupId ?? '').trim();
        if ((card.kind ?? 'BUNDLE') !== 'ITEM' || !normalizedGroupId || groupOrderMap[normalizedGroupId] === undefined) {
          return card;
        }
        return {
          ...card,
          groupDisplayOrder: groupOrderMap[normalizedGroupId],
        };
      });
    });
    setMovingCardId(null);
    setNotice('Group order updated in draft. Click Save in section or Save All to persist.');
  };

  const onMoveSubgroupItemDraft = (groupId: string, subgroupId: string, cardId: string, direction: 'up' | 'down') => {
    setError(null);
    setNotice(null);
    setMovingCardId(cardId);
    setDraftCards((prev) => {
      const groupedCards = prev
        .filter(
          (card) =>
            (card.kind ?? 'BUNDLE') === 'ITEM' &&
            (card.groupId ?? '').trim() === groupId &&
            (card.subgroupId ?? '').trim() === subgroupId
        )
        .sort((left, right) => {
          const leftOrder = left.itemDisplayOrder ?? left.displayOrder ?? 0;
          const rightOrder = right.itemDisplayOrder ?? right.displayOrder ?? 0;
          if (leftOrder !== rightOrder) {
            return leftOrder - rightOrder;
          }
          return (left.name ?? '').localeCompare(right.name ?? '');
        });
      if (!groupedCards || groupedCards.length === 0) {
        return prev;
      }

      const currentIndex = groupedCards.findIndex((card) => card.id === cardId);
      const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      if (currentIndex === -1 || targetIndex < 0 || targetIndex >= groupedCards.length) {
        return prev;
      }

      const reorderedCards = [...groupedCards];
      const [movedCard] = reorderedCards.splice(currentIndex, 1);
      reorderedCards.splice(targetIndex, 0, movedCard);
      const itemOrderMap = reorderedCards.reduce<Record<string, number>>((acc, card, index) => {
        acc[card.id] = index;
        return acc;
      }, {});

      return prev.map((card) =>
        itemOrderMap[card.id] === undefined
          ? card
          : {
              ...card,
              itemDisplayOrder: itemOrderMap[card.id],
            }
      );
    });
    setMovingCardId(null);
    setNotice('Item order updated in draft. Click Save in section or Save All to persist.');
  };

  const reorderSubgroupItemDraft = (groupId: string, subgroupId: string, sourceCardId: string, targetCardId: string) => {
    if (sourceCardId === targetCardId) {
      return;
    }

    setError(null);
    setNotice(null);
    setMovingCardId(sourceCardId);
    setDraftCards((prev) => {
      const groupedCards = prev
        .filter(
          (card) =>
            (card.kind ?? 'BUNDLE') === 'ITEM' &&
            (card.groupId ?? '').trim() === groupId &&
            (card.subgroupId ?? '').trim() === subgroupId
        )
        .sort((left, right) => {
          const leftOrder = left.itemDisplayOrder ?? left.displayOrder ?? 0;
          const rightOrder = right.itemDisplayOrder ?? right.displayOrder ?? 0;
          if (leftOrder !== rightOrder) {
            return leftOrder - rightOrder;
          }
          return (left.name ?? '').localeCompare(right.name ?? '');
        });

      const sourceIndex = groupedCards.findIndex((card) => card.id === sourceCardId);
      const targetIndex = groupedCards.findIndex((card) => card.id === targetCardId);
      if (sourceIndex === -1 || targetIndex === -1) {
        return prev;
      }

      const reorderedCards = [...groupedCards];
      const [movedCard] = reorderedCards.splice(sourceIndex, 1);
      reorderedCards.splice(targetIndex, 0, movedCard);
      const itemOrderMap = reorderedCards.reduce<Record<string, number>>((acc, card, index) => {
        acc[card.id] = index;
        return acc;
      }, {});

      return prev.map((card) =>
        itemOrderMap[card.id] === undefined
          ? card
          : {
              ...card,
              itemDisplayOrder: itemOrderMap[card.id],
            }
      );
    });
    setMovingCardId(null);
    setNotice('Item order updated in draft. Click Save in section or Save All to persist.');
  };

  const onAutoImportConfigByPrice = () => {
    setError(null);
    setNotice(null);

    const confirmed = window.confirm(
      'Auto import will stage all SKUs that have a price-to-loyalty-points mapping. Continue?'
    );
    if (!confirmed) {
      return;
    }

    const allSkus = Array.from(new Set([...skuOptions, ...draftConfigs.map((config) => config.id)]));
    if (allSkus.length === 0) {
      setError('No SKU available for auto import.');
      return;
    }

    const operations: Array<{ id: string; amount: number }> = [];
    const skippedNoPrice: string[] = [];
    const skippedNoRule: string[] = [];
    const skippedNoRuleDetails: Array<{ sku: string; priceUsd: number }> = [];

    for (const sku of allSkus) {
      const price = getSkuPriceUSD(sku, skuPriceMap);
      if (price === null) {
        skippedNoPrice.push(sku);
      } else {
        const points = getLoyaltyPointsByPrice(price);
        if (points === null) {
          skippedNoRule.push(sku);
          skippedNoRuleDetails.push({ sku, priceUsd: price });
        } else {
          operations.push({ id: sku, amount: points });
        }
      }
    }

    if (operations.length === 0) {
      setError(
        `No SKU matched price rule. Missing price: ${skippedNoPrice.length}, unsupported price: ${skippedNoRule.length}.`
      );
      return;
    }

    setAutoImportLoading(true);
    setDraftConfigs((prev) => {
      const map = new Map(prev.map((row) => [row.id, row]));
      for (const operation of operations) {
        map.set(operation.id, operation);
      }
      return Array.from(map.values()).sort((a, b) => a.id.localeCompare(b.id));
    });
    const unsupportedList = skippedNoRuleDetails
      .sort((a, b) => a.priceUsd - b.priceUsd || a.sku.localeCompare(b.sku))
      .map((item) => `${item.sku} ($${item.priceUsd.toFixed(2)})`)
      .join(', ');
    const unsupportedSuffix = unsupportedList ? ` Unsupported: ${unsupportedList}.` : '';
    setNotice(
      `Auto import staged ${operations.length} SKU. Skipped: no price ${skippedNoPrice.length}, unsupported price ${skippedNoRule.length}.${unsupportedSuffix}`
    );
    setAutoImportLoading(false);
  };

  const openConfigImportPicker = () => {
    if (savingConfigs || importingConfigs) {
      return;
    }
    configImportInputRef.current?.click();
  };

  const onExportCurrentConfigWorkbook = async () => {
    if (draftConfigs.length === 0 || exportingConfigs) {
      return;
    }

    setError(null);
    setNotice(null);
    setExportingConfigs(true);
    try {
      await downloadLoyaltyConfigWorkbook(draftConfigs, skuPriceMap);
      setNotice(`Exported ${draftConfigs.length} SKU from the current mapping draft.`);
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      setError(`Failed to export loyalty point mapping: ${message}`);
    } finally {
      setExportingConfigs(false);
    }
  };

  const onImportConfigWorkbook = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) {
      return;
    }

    if (isConfigDirty) {
      const shouldReplaceDraft = window.confirm(
        'Import will replace the current Loyalty Point Mapping draft. Continue?'
      );
      if (!shouldReplaceDraft) {
        return;
      }
    }

    setError(null);
    setNotice(null);
    setImportingConfigs(true);

    try {
      const result = await parseLoyaltyConfigWorkbook(file, skuPriceMap);
      const previousDraftMap = new Map(draftConfigs.map((config) => [config.id, config]));
      const nextDraftMap = new Map(result.configs.map((config) => [config.id, config]));
      const nextImportedChanges = result.configs.reduce<Record<string, ImportedConfigChangeType>>((acc, config) => {
        const previous = previousDraftMap.get(config.id);
        if (!previous) {
          acc[config.id] = 'added';
        } else if (previous.amount !== config.amount) {
          acc[config.id] = 'updated';
        }
        return acc;
      }, {});
      const nextRemovedConfigs = draftConfigs.filter((config) => !nextDraftMap.has(config.id));

      setDraftConfigs(result.configs);
      setImportedConfigChanges(nextImportedChanges);
      setRemovedImportedConfigs(nextRemovedConfigs);
      setShowOnlyImportedConfigChanges(false);
      const warningPreview = result.warnings.slice(0, 3).join(' ');
      const warningSuffix =
        result.warnings.length > 0
          ? ` ${result.warnings.length} warning(s)${warningPreview ? `: ${warningPreview}` : ''}${
              result.warnings.length > 3 ? ' ...' : ''
            }`
          : '';
      const changeSummary = [
        Object.values(nextImportedChanges).filter((value) => value === 'added').length > 0
          ? `${Object.values(nextImportedChanges).filter((value) => value === 'added').length} added`
          : '',
        Object.values(nextImportedChanges).filter((value) => value === 'updated').length > 0
          ? `${Object.values(nextImportedChanges).filter((value) => value === 'updated').length} updated`
          : '',
        nextRemovedConfigs.length > 0 ? `${nextRemovedConfigs.length} removed` : '',
      ]
        .filter(Boolean)
        .join(', ');
      setNotice(
        `Imported ${result.configs.length} SKU from sheet "${result.sheetName}" into local draft.${
          changeSummary ? ` Changes: ${changeSummary}.` : ''
        }${warningSuffix}`
      );
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      setError(`Failed to import loyalty point mapping: ${message}`);
    } finally {
      setImportingConfigs(false);
    }
  };

  const saveConfigDraft = async () => {
    setError(null);
    setNotice(null);
    setSavingConfigs(true);
    try {
      const savedMap = new Map(savedConfigs.map((config) => [config.id, config]));
      const draftMap = new Map(draftConfigs.map((config) => [config.id, config]));

      const deletedIds = savedConfigs.map((config) => config.id).filter((id) => !draftMap.has(id));
      const upserts = draftConfigs.filter((config) => {
        const savedConfig = savedMap.get(config.id);
        return !savedConfig || serializeConfig(savedConfig) !== serializeConfig(config);
      });

      await Promise.all(deletedIds.map((id) => deleteConfig({ variables: { id } })));
      const upsertResults = await Promise.all(
        upserts.map((config) => upsertConfig({ variables: { id: config.id, amount: config.amount } }))
      );

      const updatedMap = new Map(draftConfigs.map((config) => [config.id, config]));
      for (const result of upsertResults) {
        const row = result.data?.upsertLoyaltyCampaignConfig;
        if (row?.id) {
          updatedMap.set(row.id, {
            id: row.id,
            amount: row.amount ?? 0,
            updatedAt: row.updatedAt ?? undefined,
          });
        }
      }

      const nextSavedConfigs = Array.from(updatedMap.values()).sort((a, b) => a.id.localeCompare(b.id));
      setSavedConfigs(nextSavedConfigs);
      setDraftConfigs(nextSavedConfigs);
      setImportedConfigChanges({});
      setRemovedImportedConfigs([]);
      setShowOnlyImportedConfigChanges(false);
      setNotice('Loyalty Point Mapping saved.');
    } catch (e) {
      setError(`Failed to save loyalty point mapping: ${String(e)}`);
    } finally {
      setSavingConfigs(false);
    }
  };

  const saveCardDraft = async (scope: CardSaveScope = 'all') => {
    setError(null);
    setNotice(null);
    try {
      const savedMap = new Map(savedCards.map((card) => [card.id, card]));
      const inScope = (card: DraftLoyaltyCampaignCard): boolean => {
        if (scope === 'all') {
          return true;
        }
        if (scope === 'bundle') {
          return getCardSectionId(card) === 'bundle';
        }
        if (typeof scope === 'object' && scope.kind === 'subgroup') {
          return (
            (card.groupId ?? '').trim() === scope.groupId && (card.subgroupId ?? '').trim() === scope.subgroupId
          );
        }
        if ((card.kind ?? 'BUNDLE') !== 'ITEM') {
          return false;
        }
        const isGrouped = (card.groupId ?? '').trim().length > 0;
        return scope === 'grouped' ? isGrouped : !isGrouped;
      };
      const invalidDraftMessage = draftCards
        .filter(inScope)
        .map(getDraftCardValidationError)
        .find((message) => message !== null);
      if (invalidDraftMessage) {
        setError(invalidDraftMessage);
        return;
      }
      setSavingCards(true);
      const persistedDraftCards = draftCards.filter((card) => !isDraftCardId(card.id) && inScope(card));
      const draftIds = new Set(persistedDraftCards.map((card) => card.id));
      const deletedIds = savedCards
        .filter((card) => inScope(card))
        .map((card) => card.id)
        .filter((id) => !draftIds.has(id));

      await Promise.all(deletedIds.map((id) => deleteCard({ variables: { id } })));

      const createdCards = new Map<string, DraftLoyaltyCampaignCard>();
      for (let index = 0; index < draftCards.length; index += 1) {
        const card = draftCards[index];
        if (isDraftCardId(card.id) && inScope(card)) {
          const rewardsPayload = buildRewardMutationPayload(card);

          const result = await createCard({
            variables: {
              name: card.name.trim(),
              kind: card.kind ?? 'BUNDLE',
              groupId: card.groupId ?? null,
              groupName: card.groupName ?? null,
              groupDisplayOrder: card.groupDisplayOrder ?? null,
              groupStartDate: card.groupStartDate ?? null,
              groupEndDate: card.groupEndDate ?? null,
              subgroupId: card.subgroupId ?? null,
              subgroupName: card.subgroupName ?? null,
              subgroupDisplayOrder: card.subgroupDisplayOrder ?? null,
              subgroupStartDate: card.subgroupStartDate ?? null,
              subgroupEndDate: card.subgroupEndDate ?? null,
              itemDisplayOrder: card.itemDisplayOrder ?? null,
              cost: Number(card.cost),
              itemBonusLoyaltyPoints: card.itemBonusLoyaltyPoints ?? null,
              maxClaims: card.maxClaims ?? 1,
              rewards: rewardsPayload,
            },
          });

          const created = result.data?.createLoyaltyCampaignCard;
          if (created?.id) {
            createdCards.set(card.id, {
              ...card,
              ...created,
              id: created.id,
              rewards: [...(card.rewards ?? [])],
              draftRewardInputs: [...(card.draftRewardInputs ?? [])],
            });
          }
        }
      }

      const nextDraftCards = draftCards.map((card, index) => {
        const resolvedCard = createdCards.get(card.id) ?? card;
        return {
          ...resolvedCard,
          displayOrder: index,
        };
      });

      const updates = nextDraftCards
        .filter((card) => !isDraftCardId(card.id))
        .filter((card) => inScope(card))
        .filter((card) => {
          const savedCard = savedMap.get(card.id);
          return !savedCard || serializeComparableCard(savedCard) !== serializeComparableCard(card);
        });

      await Promise.all(
        updates.map((card) => {
          const savedCard = savedMap.get(card.id);
          return updateCard({
            variables: buildCardUpdateVariables(savedCard, card),
          });
        })
      );

      const nextSavedCards = [
        ...savedCards.filter((card) => !inScope(card)),
        ...nextDraftCards.filter((card) => !isDraftCardId(card.id) && inScope(card)),
      ];
      setSavedCards(nextSavedCards);
      setDraftCards(nextDraftCards);
      if (scope === 'grouped') {
        setSavedDraftGroups(sortDraftGroups(draftGroups));
        setSavedDraftSubgroups(sortDraftSubgroups(draftSubgroups));
      } else if (typeof scope === 'object' && scope.kind === 'subgroup') {
        setSavedDraftGroups(sortDraftGroups(draftGroups));
        setSavedDraftSubgroups(sortDraftSubgroups(draftSubgroups));
      }
      setNotice(
        typeof scope === 'object' && scope.kind === 'subgroup'
          ? 'Subgroup saved.'
          : scope === 'grouped'
          ? 'Grouped item config saved.'
          : scope === 'single'
            ? 'Single item config saved.'
            : scope === 'bundle'
              ? 'Bundle config saved.'
              : 'Card sections saved.'
      );
    } catch (e) {
      setError(`Failed to save campaign cards: ${String(e)}`);
    } finally {
      setSavingCards(false);
    }
  };

  const onSaveAll = async () => {
    setSavingAll(true);
    try {
      if (isConfigDirty) {
        await saveConfigDraft();
      }
      if (isCardSectionDirty('bundle')) {
        await saveCardDraft('bundle');
      }
      if (isGroupedItemDirty) {
        await saveCardDraft('grouped');
      }
      if (isSingleItemDirty) {
        await saveCardDraft('single');
      }
      if (!isConfigDirty && !isAnyCardDirty) {
        setNotice('No changes to save.');
      }
    } finally {
      setSavingAll(false);
    }
  };

  const openSectionAndScroll = (sectionId: string, beforeScroll?: () => void) => {
    beforeScroll?.();
    window.setTimeout(() => scrollToSection(sectionId), 0);
  };

  const getConfigGroupPriceLabel = (rows: LoyaltyCampaignConfigRow[]): string | null => {
    const uniquePrices = Array.from(
      new Set(rows.map((row) => getSkuPriceUSD(row.id, skuPriceMap)).filter((price): price is number => price !== null))
    ).sort((left, right) => left - right);

    if (uniquePrices.length === 0) {
      return null;
    }

    if (uniquePrices.length === 1) {
      return `$${uniquePrices[0].toFixed(2)}`;
    }

    return `$${uniquePrices[0].toFixed(2)} - $${uniquePrices[uniquePrices.length - 1].toFixed(2)}`;
  };

  const getGroupedConfigEntries = (rows: LoyaltyCampaignConfigRow[]) => {
    const entries: Array<
      | {
          key: string;
          kind: 'offer';
          offer: LoyaltyCampaignOfferOptionRow;
          rows: LoyaltyCampaignConfigRow[];
        }
      | {
          key: string;
          kind: 'sku';
          row: LoyaltyCampaignConfigRow;
        }
    > = [];
    const offerBuckets = new Map<string, LoyaltyCampaignConfigRow[]>();
    const rawRows: LoyaltyCampaignConfigRow[] = [];

    rows.forEach((row) => {
      const matchedOfferId = findOfferIdBySku(row.id, offerSkuMap);
      const resolvedOfferId = matchedOfferId && offerById[matchedOfferId] ? matchedOfferId : null;
      if (!resolvedOfferId || !offerById[resolvedOfferId]) {
        rawRows.push(row);
        return;
      }
      const bucket = offerBuckets.get(resolvedOfferId) ?? [];
      bucket.push(row);
      offerBuckets.set(resolvedOfferId, bucket);
    });

    offerBuckets.forEach((bucketRows, offerId) => {
      const offer = offerById[offerId];
      if (offer) {
        entries.push({
          key: `offer:${offerId}`,
          kind: 'offer',
          offer,
          rows: [...bucketRows].sort((left, right) => left.id.localeCompare(right.id)),
        });
      }
    });

    rawRows
      .sort((left, right) => left.id.localeCompare(right.id))
      .forEach((row) => {
        entries.push({
          key: `sku:${row.id}`,
          kind: 'sku',
          row,
        });
      });

    return entries.sort((left, right) => left.key.localeCompare(right.key));
  };

  const renderCardList = (cards: LoyaltyCampaignCardRow[], sectionId: LoyaltyCampaignCardSectionId) => {
    if (cards.length === 0) {
      return (
        <div
          className="text-muted px-3 py-4"
          style={{ border: '1px dashed #d7dbe0', borderRadius: 16, background: '#fbfcfd' }}
        >
          No cards created yet.
        </div>
      );
    }

    return cards.map((card, localIndex) => {
      const derivedClaimType = getCardClaimType(card.maxClaims, card.claimType);
      const itemCategory =
        (card.kind ?? 'BUNDLE') === 'ITEM' ? getItemCategoryFromReward(card.rewards?.[0]) : null;
      const cardVisualStyle =
        (card.kind ?? 'BUNDLE') === 'BUNDLE'
          ? BUNDLE_CARD_STYLE
          : itemCategory !== null
          ? ITEM_CATEGORY_CARD_STYLES[itemCategory]
          : {
              borderColor: '#e3e7eb',
              background: '#ffffff',
              badgeBackground: '#0f172a',
              badgeText: '#ffffff',
            };
      return (
        <Card
          key={card.id}
          className="mb-3"
          style={{
            borderRadius: 18,
            border: `1px solid ${cardVisualStyle.borderColor}`,
            background: cardVisualStyle.background,
            boxShadow: '0 8px 24px rgba(15, 23, 42, 0.04)',
          }}
        >
          <Card.Body style={{ padding: scaled(20) }}>
            <div
              className="d-flex justify-content-between align-items-start flex-wrap mb-3"
              style={{ gap: scaled(16) }}
            >
              <div className="d-flex align-items-start" style={{ gap: 14, minWidth: 0 }}>
                <div
                  style={{
                    width: scaled(42),
                    height: scaled(42),
                    borderRadius: scaled(14),
                    background: cardVisualStyle.badgeBackground,
                    color: cardVisualStyle.badgeText,
                    fontWeight: 700,
                    fontSize: scaled(14),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  #{localIndex + 1}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div className="font-weight-bold" style={{ fontSize: scaled(18), color: '#172033' }}>
                    {card.name}
                  </div>
                </div>
              </div>
              <div className="d-flex align-items-center flex-wrap justify-content-end" style={{ gap: 8 }}>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={localIndex === 0 || movingCardId !== null || savingCards}
                  onClick={() => onMoveCardDraft(card.id, 'up', sectionId)}
                  title="Move up"
                >
                  ↑
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={localIndex === cards.length - 1 || movingCardId !== null || savingCards}
                  onClick={() => onMoveCardDraft(card.id, 'down', sectionId)}
                  title="Move down"
                >
                  ↓
                </Button>
                <Button
                  size="sm"
                  variant="primary"
                  style={editButtonStyle}
                  disabled={savingCards || movingCardId !== null}
                  onClick={() => onEditCard(card)}
                >
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  style={deleteButtonStyle}
                  disabled={savingCards || movingCardId !== null}
                  onClick={() => onDeleteCardDraft(card.id)}
                >
                  {deletingCardId === card.id ? 'Deleting...' : 'Delete'}
                </Button>
              </div>
            </div>

            <div className="d-flex flex-wrap mb-3" style={{ gap: 10 }}>
              <span style={{ ...priceChipStyle, fontSize: scaled(13), padding: `${scaled(8)}px ${scaled(12)}px` }}>
                Price: {getCardPricingLabel(card)}
              </span>
              {card.groupName ? (
                <span style={{ ...metaChipStyle, fontSize: scaled(13), padding: `${scaled(8)}px ${scaled(12)}px` }}>
                  Group: {card.groupName}
                </span>
              ) : null}
              {card.subgroupName ? (
                <span style={{ ...metaChipStyle, fontSize: scaled(13), padding: `${scaled(8)}px ${scaled(12)}px` }}>
                  Subgroup: {card.subgroupName}
                </span>
              ) : null}
              {Number(card.itemBonusLoyaltyPoints ?? 0) > 0 ? (
                <span style={{ ...metaChipStyle, fontSize: scaled(13), padding: `${scaled(8)}px ${scaled(12)}px` }}>
                  Bonus: {formatLoyaltyPoints(card.itemBonusLoyaltyPoints, { signed: true })}
                </span>
              ) : null}
              <span style={{ ...metaChipStyle, fontSize: scaled(13), padding: `${scaled(8)}px ${scaled(12)}px` }}>
                Limit: {getClaimDisplayLabel(card.maxClaims, derivedClaimType)}
              </span>
              <span style={{ ...metaChipStyle, fontSize: scaled(13), padding: `${scaled(8)}px ${scaled(12)}px` }}>
                Start:{' '}
                {((card.kind ?? 'BUNDLE') === 'ITEM' && (card.groupId ?? '').trim().length > 0
                  ? card.subgroupStartDate ?? card.groupStartDate
                  : null)
                  ? `${moment
                      .utc(
                        (card.kind ?? 'BUNDLE') === 'ITEM' && (card.groupId ?? '').trim().length > 0
                          ? card.subgroupStartDate ?? card.groupStartDate
                          : null
                      )
                      .format('YYYY-MM-DD HH:mm')} UTC`
                  : 'Always active'}
              </span>
              <span style={{ ...metaChipStyle, fontSize: scaled(13), padding: `${scaled(8)}px ${scaled(12)}px` }}>
                End:{' '}
                {((card.kind ?? 'BUNDLE') === 'ITEM' && (card.groupId ?? '').trim().length > 0
                  ? card.subgroupEndDate ?? card.groupEndDate
                  : null)
                  ? `${moment
                      .utc(
                        (card.kind ?? 'BUNDLE') === 'ITEM' && (card.groupId ?? '').trim().length > 0
                          ? card.subgroupEndDate ?? card.groupEndDate
                          : null
                      )
                      .format('YYYY-MM-DD HH:mm')} UTC`
                  : 'No end date'}
              </span>
            </div>

            <div
              className="mb-2"
              style={{ fontSize: scaled(13), fontWeight: 700, color: '#475569', textTransform: 'uppercase' }}
            >
              {getRewardLabel((card.kind ?? 'BUNDLE') as LoyaltyCampaignCardKind)}
            </div>
            <div className="d-flex flex-wrap" style={{ gap: 8 }}>
              {(card.rewards ?? []).length === 0 && <span className="text-muted small">No rewards configured</span>}
              {(card.rewards ?? []).map((reward, rewardIndex) => (
                <span
                  key={`${card.id}_reward_${rewardIndex}`}
                  style={{
                    ...rewardChipStyle,
                    fontSize: scaled(13),
                    padding: `${scaled(8)}px ${scaled(12)}px`,
                  }}
                >
                  {getSavedRewardDisplayLabel(reward)}
                </span>
              ))}
            </div>
          </Card.Body>
        </Card>
      );
    });
  };

  const renderSectionExpandToggle = (
    expanded: boolean,
    onToggle: () => void,
    collapsedLabel = 'Show details',
    expandedLabel = 'Hide details'
  ) => (
    <Button
      size="sm"
      variant={expanded ? 'dark' : 'light'}
      onClick={onToggle}
      title={expanded ? expandedLabel : collapsedLabel}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        fontWeight: 700,
        borderRadius: 999,
        padding: `${scaled(7)}px ${scaled(12)}px`,
        border: expanded ? '1px solid #1f2937' : '1px solid #cbd5e1',
        background: expanded ? '#1f2937' : '#ffffff',
        color: expanded ? '#ffffff' : '#0f172a',
        boxShadow: expanded ? '0 8px 18px rgba(15, 23, 42, 0.18)' : '0 4px 12px rgba(15, 23, 42, 0.08)',
      }}
    >
      <span aria-hidden="true" style={{ fontSize: scaled(14), lineHeight: 1 }}>
        {expanded ? '▾' : '▸'}
      </span>
      <span>{expanded ? expandedLabel : collapsedLabel}</span>
    </Button>
  );

  const activeSecondaryButtonStyle = {
    background: '#ffffff',
    color: '#0f172a',
    border: '1px solid #94a3b8',
    boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)',
    fontWeight: 700,
  } as const;

  const renderLifecycleHeader = (
    title: string,
    count: number,
    variant: keyof typeof LIFECYCLE_SECTION_STYLES = 'active',
    toggle?: { expanded: boolean; onToggle: () => void }
  ) => {
    if (!title && !toggle) {
      return null;
    }

    const lifecycleStyle = LIFECYCLE_SECTION_STYLES[variant];
    const toggleLabel = toggle ? (toggle.expanded ? 'Hide details' : 'Show details') : null;

    const renderLifecycleExpandToggle = (
      expanded: boolean,
      onToggle: () => void,
      collapsedLabel = 'Show details',
      expandedLabel = 'Hide details'
    ) => (
      <Button
        size="sm"
        variant={expanded ? 'dark' : 'light'}
        onClick={(event) => {
          event.stopPropagation();
          onToggle();
        }}
        title={expanded ? expandedLabel : collapsedLabel}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          fontWeight: 700,
          borderRadius: 999,
          padding: `${scaled(7)}px ${scaled(12)}px`,
          border: expanded ? '1px solid #1f2937' : '1px solid #cbd5e1',
          background: expanded ? '#1f2937' : '#ffffff',
          color: expanded ? '#ffffff' : '#0f172a',
          boxShadow: expanded ? '0 8px 18px rgba(15, 23, 42, 0.18)' : '0 4px 12px rgba(15, 23, 42, 0.08)',
        }}
      >
        <span aria-hidden="true" style={{ fontSize: scaled(14), lineHeight: 1 }}>
          {expanded ? '▾' : '▸'}
        </span>
        <span>{expanded ? expandedLabel : collapsedLabel}</span>
      </Button>
    );

    return (
      <div
        className="d-flex align-items-center justify-content-between flex-wrap mb-3"
        style={{
          gap: 10,
          padding: `${scaled(12)}px ${scaled(14)}px`,
          borderRadius: scaled(14),
          background: lifecycleStyle.headerBackground,
          border: `1px solid ${lifecycleStyle.headerBorderColor}`,
        }}
      >
        <style>
          {`@keyframes loyaltyCampaignPulse {
            0% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.72; transform: scale(1.04); }
            100% { opacity: 1; transform: scale(1); }
          }`}
        </style>

        <div>
          <div className="font-weight-bold" style={{ color: '#172033' }}>
            {title}
          </div>
          {toggleLabel ? (
            <div style={{ fontSize: scaled(12), color: '#475569', marginTop: 2 }}>
              {toggleLabel}
            </div>
          ) : null}
        </div>
        {toggle ? renderLifecycleExpandToggle(toggle.expanded, toggle.onToggle) : null}
      </div>
    );
  };

  const renderLifecycleSection = (
    title: string,
    count: number,
    variant: keyof typeof LIFECYCLE_SECTION_STYLES,
    children: ReactNode,
    toggle?: { expanded: boolean; onToggle: () => void }
  ) => {
    const lifecycleStyle = LIFECYCLE_SECTION_STYLES[variant];

    return (
      <div
        className="mx-auto mb-4"
        style={{
          maxWidth: SECTION_MAX_WIDTH,
          padding: scaled(20),
          borderRadius: scaled(18),
          border: `1px solid ${lifecycleStyle.borderColor}`,
          background: lifecycleStyle.background,
        }}
      >
        {renderLifecycleHeader(title, count, variant, toggle)}
        {children}
      </div>
    );
  };

  const renderGroupBlock = (
    group: LoyaltyCampaignItemGroup,
    sectionId: LoyaltyCampaignCardSectionId,
    keyPrefix = '',
    options?: {
      enableReorder?: boolean;
      groupIndex?: number;
      totalGroups?: number;
      anchorId?: string;
      compactMode?: boolean;
    }
  ) => {
    const isCollapsed = Boolean(collapsedGroups[group.id]);
    const canReorderGroup = Boolean(options?.enableReorder && !group.id.startsWith('ungrouped:'));
    const canReorderItems = !group.id.startsWith('ungrouped:');
    const compactMode = Boolean(options?.compactMode);
    const groupDraftState = getGroupDraftState(group.id);
    const groupLifecycleState = getLifecycleStateFromSchedule(group.startDate, group.endDate);
    const groupScheduledRunAt = groupLifecycleState === 'scheduled' ? formatScheduledRunAt(group.startDate) : null;
    const renderInlineGroupedItemComposer = (submitLabel: string) => (
      <div
        className="px-3 py-3"
        style={{
          border: '1px solid #bfdbfe',
          borderRadius: 14,
          background: '#ffffff',
        }}
      >
        <div className="d-flex align-items-end flex-wrap" style={{ gap: 8 }}>
          <div style={{ minWidth: 140 }}>
            <Form.Label className="mb-1">Cost</Form.Label>
            <Form.Control
              size="sm"
              type="number"
              value={cardCost}
              onChange={(e) => setCardCost(e.currentTarget.value)}
            />
          </div>
          <div style={{ minWidth: 160 }}>
            <Form.Label className="mb-1">Claim</Form.Label>
            <Form.Control
              size="sm"
              as="select"
              value={cardClaimType}
              onChange={(e) => setCardClaimType(e.currentTarget.value as LoyaltyCampaignClaimMode)}
            >
              <option value="ONE_TIME">ONE_TIME</option>
              <option value="INFINITE">INFINITE</option>
              <option value="CUSTOM">CUSTOM</option>
            </Form.Control>
          </div>
          {cardClaimType === 'CUSTOM' ? (
            <div style={{ minWidth: 120 }}>
              <Form.Label className="mb-1">Max</Form.Label>
              <Form.Control
                size="sm"
                type="number"
                value={cardMaxClaims}
                onChange={(e) => setCardMaxClaims(e.currentTarget.value)}
              />
            </div>
          ) : null}
          <div className="d-flex align-items-center flex-wrap" style={{ gap: 8, flex: 1, minWidth: 220 }}>
            {cardRewards.length === 0 ? (
              <span className="text-muted small">No reward</span>
            ) : (
              cardRewards.map((reward, rewardIndex) => (
                <Button
                  key={`draft-reward-${rewardIndex}`}
                  size="sm"
                  variant="primary"
                  onClick={() => onOpenEditRewardModal(rewardIndex)}
                  title={getDraftRewardDisplayLabel(reward)}
                >
                  <span className="mr-2">{getRewardTypeEmoji(reward.type)}</span>
                  {getDraftRewardButtonLabel(reward)}
                </Button>
              ))
            )}
          </div>
          <div className="d-flex align-items-center ml-auto" style={{ gap: 8 }}>
            <Button
              size="sm"
              variant="primary"
              onClick={() => (cardRewards.length > 0 ? onOpenEditRewardModal(0) : onOpenAddRewardModal())}
            >
              <span className="mr-2">{getRewardTypeEmoji(cardRewards[0]?.type)}</span>
              {cardRewards.length > 0 ? 'Change Item' : 'Choose Item'}
            </Button>
            <Button size="sm" variant="secondary" style={activeSecondaryButtonStyle} onClick={clearCardForm}>
              Cancel
            </Button>
            <Button size="sm" variant="primary" onClick={onSaveCardDraft}>
              {submitLabel}
            </Button>
          </div>
        </div>
      </div>
    );

    return (
        <Card
          id={options?.anchorId}
          key={`${keyPrefix}${group.id}`}
          className="mb-3"
          style={{
            borderRadius: compactMode ? 16 : 20,
            border: '1px solid #cad5e2',
            background: '#ffffff',
            boxShadow: compactMode ? '0 8px 20px rgba(15, 23, 42, 0.04)' : '0 12px 28px rgba(15, 23, 42, 0.05)',
            overflow: 'visible',
            scrollMarginTop: 90,
        }}
      >
        <Card.Body style={{ padding: compactMode ? scaled(14) : scaled(18) }}>
          {!isCollapsed ? (
            <div className="mb-3">
              <div
                className="d-flex align-items-start justify-content-between flex-wrap mb-2"
                style={{ gap: scaled(12) }}
              >
                <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
                  {canReorderGroup ? (
                    <>
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={(options?.groupIndex ?? 0) === 0 || movingCardId !== null || savingCards}
                        onClick={() => onMoveGroupDraft(group.id, 'up')}
                        title="Move up"
                      >
                        ↑
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={
                          (options?.groupIndex ?? 0) === (options?.totalGroups ?? 1) - 1 ||
                          movingCardId !== null ||
                          savingCards
                        }
                        onClick={() => onMoveGroupDraft(group.id, 'down')}
                        title="Move down"
                      >
                        ↓
                      </Button>
                    </>
                  ) : null}
                  <div className="font-weight-bold" style={{ fontSize: scaled(20), color: '#172033' }}>
                    {group.name}
                  </div>
                </div>
                {!group.id.startsWith('ungrouped:') ? (
                  <div className="d-flex align-items-center flex-wrap justify-content-end" style={{ gap: 8 }}>
                    {renderSectionExpandToggle(
                      !isCollapsed,
                      () =>
                        setCollapsedGroups((prev) => ({
                          ...prev,
                          [group.id]: !prev[group.id],
                        })),
                      'Show group',
                      'Hide group'
                    )}
                    <Button
                      size="sm"
                      variant="success"
                      style={addButtonStyle}
                      onClick={() => openCreateSubgroupItemModal(group)}
                      disabled={savingCards}
                    >
                      Add Subgroup
                    </Button>
                    <Button size="sm" variant="primary" onClick={() => openEditGroupModal(group)} disabled={savingCards}>
                      Edit Group
                    </Button>
                    <Button size="sm" variant="danger" onClick={() => deleteGroupDraft(group)} disabled={savingCards}>
                      Delete Group
                    </Button>
                  </div>
                ) : null}
              </div>
              <div className="d-flex flex-wrap" style={{ gap: 8 }}>
                {!compactMode ? (
                  <span style={{ ...metaChipStyle, fontSize: scaled(12), padding: `${scaled(6)}px ${scaled(10)}px` }}>
                    Group Order: {group.groupDisplayOrder === Number.MAX_SAFE_INTEGER ? '-' : group.groupDisplayOrder}
                  </span>
                ) : null}
                {groupScheduledRunAt ? (
                  <span
                    style={{
                      ...metaChipStyle,
                      fontSize: scaled(12),
                      padding: `${scaled(6)}px ${scaled(10)}px`,
                      background: '#eff6ff',
                      border: '1px solid #93c5fd',
                      color: '#1d4ed8',
                      fontWeight: 700,
                    }}
                  >
                    Running at: {groupScheduledRunAt}
                  </span>
                ) : null}
                <div
                  className="d-flex flex-wrap"
                  style={{ gap: 8 }}
                  title={
                    `Start: ${
                      formatUtcDateTime(group.startDate, 'YYYY-MM-DD HH:mm') ?? 'Always active'
                    }${
                      formatLocalDateTime(group.startDate) ? ` • ${formatLocalDateTime(group.startDate)} (${localTimezone})` : ''
                    }\nEnd: ${
                      formatUtcDateTime(group.endDate, 'YYYY-MM-DD HH:mm') ?? 'No end date'
                    }${
                      formatLocalDateTime(group.endDate) ? ` • ${formatLocalDateTime(group.endDate)} (${localTimezone})` : ''
                    }`
                  }
                >
                  <div
                    style={{
                      ...metaChipStyle,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: 2,
                      padding: `${scaled(6)}px ${scaled(10)}px`,
                      minWidth: compactMode ? 238 : 270,
                      background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                      border: '1px solid #93c5fd',
                      boxShadow: '0 8px 18px rgba(59, 130, 246, 0.12)',
                    }}
                  >
                    <span style={{ fontSize: scaled(10), fontWeight: 800, letterSpacing: '0.06em', color: '#1d4ed8' }}>
                      UTC
                    </span>
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'baseline',
                        gap: 6,
                        fontSize: scaled(12),
                        color: '#0f172a',
                      }}
                    >
                      <span style={{ fontWeight: 700, color: '#1d4ed8', minWidth: 34 }}>Start</span>
                      <span style={{ fontWeight: 900, fontSize: scaled(13) }}>
                        {formatUtcDateTime(group.startDate) ?? 'Always active'}
                      </span>
                    </span>
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'baseline',
                        gap: 6,
                        fontSize: scaled(12),
                        color: '#0f172a',
                      }}
                    >
                      <span style={{ fontWeight: 700, color: '#1d4ed8', minWidth: 34 }}>End</span>
                      <span style={{ fontWeight: 900, fontSize: scaled(13) }}>
                        {formatUtcDateTime(group.endDate) ?? 'No end date'}
                      </span>
                    </span>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: 2,
                      padding: `${scaled(6)}px ${scaled(10)}px`,
                      minWidth: compactMode ? 238 : 270,
                      borderRadius: scaled(10),
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      color: '#64748b',
                    }}
                  >
                    <span style={{ fontSize: scaled(10), fontWeight: 700, letterSpacing: '0.04em', color: '#94a3b8' }}>
                      Local
                    </span>
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'baseline',
                        gap: 6,
                        fontSize: scaled(12),
                        color: '#475569',
                      }}
                    >
                      <span style={{ fontWeight: 700, color: '#94a3b8', minWidth: 34 }}>Start</span>
                      <span style={{ fontWeight: 600 }}>
                        {group.startDate && formatLocalDateTime(group.startDate)
                          ? formatLocalDateTime(group.startDate)
                          : 'Always active'}
                      </span>
                    </span>
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'baseline',
                        gap: 6,
                        fontSize: scaled(12),
                        color: '#475569',
                      }}
                    >
                      <span style={{ fontWeight: 700, color: '#94a3b8', minWidth: 34 }}>End</span>
                      <span style={{ fontWeight: 600 }}>
                        {group.endDate && formatLocalDateTime(group.endDate)
                          ? `${formatLocalDateTime(group.endDate)} (${localTimezone})`
                          : `No end date (${localTimezone})`}
                      </span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div
              className="d-flex align-items-start justify-content-between flex-wrap mb-2"
              style={{ gap: scaled(12) }}
            >
              <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
                {canReorderGroup ? (
                  <>
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={(options?.groupIndex ?? 0) === 0 || movingCardId !== null || savingCards}
                      onClick={() => onMoveGroupDraft(group.id, 'up')}
                      title="Move up"
                    >
                      ↑
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={
                        (options?.groupIndex ?? 0) === (options?.totalGroups ?? 1) - 1 ||
                        movingCardId !== null ||
                        savingCards
                      }
                      onClick={() => onMoveGroupDraft(group.id, 'down')}
                      title="Move down"
                    >
                      ↓
                    </Button>
                  </>
                ) : null}
                <div className="font-weight-bold" style={{ fontSize: scaled(20), color: '#172033' }}>
                  {group.name}
                </div>
              </div>
              {!group.id.startsWith('ungrouped:') ? (
                <div className="d-flex align-items-center flex-wrap justify-content-end" style={{ gap: 8 }}>
                  {renderSectionExpandToggle(
                    !isCollapsed,
                    () =>
                      setCollapsedGroups((prev) => ({
                        ...prev,
                        [group.id]: !prev[group.id],
                      })),
                    'Show group',
                    'Hide group'
                  )}
                  <Button
                    size="sm"
                    variant="success"
                    style={addButtonStyle}
                    onClick={() => openCreateSubgroupItemModal(group)}
                    disabled={savingCards}
                  >
                    Add Subgroup
                  </Button>
                  <Button size="sm" variant="primary" onClick={() => openEditGroupModal(group)} disabled={savingCards}>
                    Edit Group
                  </Button>
                  <Button size="sm" variant="danger" onClick={() => deleteGroupDraft(group)} disabled={savingCards}>
                    Delete Group
                  </Button>
                </div>
              ) : null}
            </div>
          )}

          {!isCollapsed ? (
            <div className="d-flex flex-column" style={{ gap: 14 }}>
              {group.subgroups.length === 0 ? (
                <div
                  className="px-3 py-3 text-muted"
                  style={{ border: '1px dashed #d7dbe0', borderRadius: 14, background: '#fbfcfd' }}
                >
                  No subgroup yet. Use `Add Subgroup` to create the first item in this group.
                </div>
              ) : null}
              {group.subgroups.map((subgroup) => {
                const subgroupDraftState = getSubgroupDraftState(group.id, subgroup.id);
                const subgroupDirty = isSubgroupDirty(group.id, subgroup.id);
                const subgroupChanges = getSubgroupChangeSummary(group.id, subgroup.id);
                const subgroupChangeLabel =
                  subgroupChanges.length > 0
                    ? `${subgroupDraftState === 'draft' ? 'Draft' : 'Changed'}: ${subgroupChanges.join(', ')}`
                    : subgroupDraftState === 'draft'
                      ? 'Draft'
                      : null;
                const subgroupCollapseKey = `${group.id}:${subgroup.id}`;
                const isSubgroupCollapsed = Boolean(collapsedSubgroups[subgroupCollapseKey]);
                const subgroupValidationErrors = getSubgroupValidationErrors(group, subgroup);
                const startDateError = subgroupValidationErrors.find((validationError) => validationError.field === 'startDate');
                const endDateError = subgroupValidationErrors.find((validationError) => validationError.field === 'endDate');

                return (
                <div
                  key={`${group.id}:${subgroup.id}`}
                  id={getGroupedItemSubgroupAnchorId(group.id, subgroup.id)}
                  style={{
                    border: '1px solid #cfd8e3',
                    borderLeft: `4px solid ${subgroupDraftState === 'draft' ? '#60a5fa' : subgroupDirty ? '#f59e0b' : '#cbd5e1'}`,
                    borderRadius: compactMode ? 12 : 14,
                    background: '#ffffff',
                    boxShadow: '0 1px 0 rgba(15, 23, 42, 0.04)',
                    padding: compactMode ? scaled(12) : scaled(14),
                    scrollMarginTop: 110,
                  }}
                >
                  <div
                    className="d-flex align-items-start justify-content-between flex-wrap mb-2"
                    style={{
                      gap: 10,
                      background: '#87CEEB',
                      border: '1px solid #76bfdc',
                      borderRadius: compactMode ? 10 : 12,
                      padding: `${scaled(10)}px ${scaled(12)}px`,
                      marginBottom: scaled(12),
                      borderBottom: '1px solid #e2e8f0',
                    }}
                  >
                    <div>
                      <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
                        <div
                          style={{
                            minWidth: compactMode ? 180 : 220,
                            fontSize: compactMode ? scaled(24) : scaled(28),
                            fontWeight: 800,
                            color: '#172033',
                            lineHeight: 1.2,
                          }}
                        >
                          {getSubgroupNameOptionLabel(subgroup.name)}
                        </div>
                        <div style={{ minWidth: compactMode ? 220 : 280 }}>
                          <Form.Control
                            as="select"
                            size="sm"
                            value={subgroup.name}
                            onChange={(e) =>
                              updateSubgroupDraft(group, subgroup, { subgroupName: e.currentTarget.value })
                            }
                          >
                            {Array.from(
                              new Set([subgroup.name, ...subgroupNameOptions].filter(Boolean))
                            ).map((option) => (
                              <option key={option} value={option}>
                                {getSubgroupNameOptionLabel(option)}
                              </option>
                            ))}
                          </Form.Control>
                        </div>
                      </div>
                    </div>
                    <div className="d-flex align-items-center flex-wrap justify-content-end" style={{ gap: 8 }}>
                      {!compactMode ? (
                        <span
                          style={{ ...metaChipStyle, fontSize: scaled(12), padding: `${scaled(6)}px ${scaled(10)}px` }}
                        >
                          Subgroup Order: {subgroup.subgroupDisplayOrder === Number.MAX_SAFE_INTEGER ? '-' : subgroup.subgroupDisplayOrder}
                        </span>
                      ) : null}
                      {subgroupChangeLabel ? (
                        <span
                          style={{
                            ...metaChipStyle,
                            fontSize: scaled(11),
                            padding: `${scaled(4)}px ${scaled(8)}px`,
                            background: subgroupDraftState === 'draft' ? '#2563eb' : '#dc2626',
                            border: `1px solid ${subgroupDraftState === 'draft' ? '#1d4ed8' : '#b91c1c'}`,
                            color: '#ffffff',
                            maxWidth: compactMode ? 220 : 360,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            fontWeight: 800,
                          }}
                          title={subgroupChangeLabel}
                        >
                          {subgroupChangeLabel}
                        </span>
                      ) : null}
                      {subgroupDirty ? (
                        <Button
                          size="sm"
                          variant="secondary"
                          style={resetButtonStyle}
                          onClick={() => resetSubgroupDraft(group.id, subgroup.id)}
                          disabled={savingCards || movingCardId !== null}
                        >
                          Reset
                        </Button>
                      ) : null}
                      {renderSectionExpandToggle(
                        !isSubgroupCollapsed,
                        () =>
                          setCollapsedSubgroups((prev) => ({
                            ...prev,
                            [subgroupCollapseKey]: !prev[subgroupCollapseKey],
                          })),
                        'Show subgroup',
                        'Hide subgroup'
                      )}
                      <Button
                        size="sm"
                        variant="primary"
                        onClick={() => saveCardDraft({ kind: 'subgroup', groupId: group.id, subgroupId: subgroup.id })}
                        disabled={!isSubgroupDirty(group.id, subgroup.id) || subgroup.cards.length === 0}
                      >
                        Save
                      </Button>
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => deleteSubgroupDraft(group.id, subgroup)}
                        disabled={savingCards || movingCardId !== null}
                      >
                        Delete
                      </Button>
                      {inlineComposerSubgroupKey !== `${group.id}:${subgroup.id}` ? (
                        <Button
                          size="sm"
                          variant="success"
                          style={addButtonStyle}
                          onClick={() => openCreateItemModalForSubgroup(group, subgroup)}
                          disabled={savingCards || isSubgroupCollapsed}
                          title={isSubgroupCollapsed ? 'Expand subgroup to add items' : 'Add item'}
                        >
                          Add Item
                        </Button>
                      ) : null}
                    </div>
                  </div>

                  {!collapsedSubgroups[`${group.id}:${subgroup.id}`] ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: scaled(12) }}>
                  <div className="mb-2">
                    <div
                      className="d-flex align-items-center justify-content-between flex-wrap"
                      style={{
                        ...getScheduleTogglePanelStyle(Boolean(subgroup.startDate || subgroup.endDate)),
                        padding: `${scaled(8)}px ${scaled(12)}px`,
                        gap: 10,
                      }}
                    >
                      <Form.Check
                        type="switch"
                        id={`subgroup-schedule-${group.id}-${subgroup.id}`}
                        label="Set schedule"
                        checked={Boolean(subgroup.startDate || subgroup.endDate)}
                        onChange={(e) =>
                          updateSubgroupDraft(
                            group,
                            subgroup,
                            e.currentTarget.checked
                              ? {
                                  startDate: subgroup.startDate ?? group.startDate ?? moment.utc().startOf('day').toISOString(),
                                  endDate: subgroup.endDate ?? group.endDate ?? null,
                                }
                              : {
                                  startDate: null,
                                  endDate: null,
                                }
                          )
                        }
                      />
                      <div className="small text-muted">Override group schedule</div>
                    </div>
                    {subgroup.startDate || subgroup.endDate ? (
                      <div
                        className="mt-2"
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                          gap: 12,
                        }}
                      >
                        <div>
                          <div className="d-flex align-items-center justify-content-between flex-wrap mb-1" style={{ gap: 8 }}>
                            <Form.Label className="mb-0">Start (UTC)</Form.Label>
                          </div>
                          <div
                            style={{
                              border:
                                startDateError
                                  ? '1px solid #ef4444'
                                  : undefined,
                              borderRadius: startDateError ? 8 : undefined,
                            }}
                          >
                            <DateTimePicker
                              dateFormat="YYYY-MM-DD"
                              timeFormat="HH:mm"
                              utc={true}
                              value={subgroup.startDate ? moment.utc(subgroup.startDate) : undefined}
                              onChange={(value) =>
                                updateSubgroupDraft(group, subgroup, {
                                  startDate: moment.isMoment(value) ? value.toDate().toISOString() : null,
                                })
                              }
                            />
                          </div>
                          {subgroup.startDate ? (
                            <div className="small text-muted mt-1">
                              {`${formatLocalDateTime(subgroup.startDate)} (${localTimezone})`}
                            </div>
                          ) : null}
                          {startDateError ? (
                            <div className="small mt-1" style={{ color: '#dc2626', fontWeight: 600 }}>
                              {startDateError.message}
                            </div>
                          ) : null}
                        </div>
                        <div>
                          <div className="d-flex align-items-center justify-content-between flex-wrap mb-1" style={{ gap: 8 }}>
                            <Form.Label className="mb-0">End (UTC)</Form.Label>
                            <Button
                              size="sm"
                              variant="danger"
                              disabled={savingCards}
                              onClick={() =>
                                updateSubgroupDraft(
                                  group,
                                  subgroup,
                                  applyEndNowToIsoRange(subgroup.startDate)
                                )
                              }
                            >
                              End now
                            </Button>
                          </div>
                          <div
                            style={{
                              border:
                                endDateError
                                  ? '1px solid #ef4444'
                                  : undefined,
                              borderRadius: endDateError ? 8 : undefined,
                            }}
                          >
                            <DateTimePicker
                              dateFormat="YYYY-MM-DD"
                              timeFormat="HH:mm"
                              utc={true}
                              value={subgroup.endDate ? moment.utc(subgroup.endDate) : undefined}
                              onChange={(value) =>
                                updateSubgroupDraft(group, subgroup, {
                                  endDate: moment.isMoment(value) ? value.toDate().toISOString() : null,
                                })
                              }
                            />
                          </div>
                          {subgroup.endDate ? (
                            <div className="small text-muted mt-1">
                              {`${formatLocalDateTime(subgroup.endDate)} (${localTimezone})`}
                            </div>
                          ) : null}
                          {endDateError ? (
                            <div className="small mt-1" style={{ color: '#dc2626', fontWeight: 600 }}>
                              {endDateError.message}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    ) : null}
                    {subgroupValidationErrors.length > 0 ? (
                      <div
                        className="mt-2 px-3 py-2"
                        style={{
                          border: '1px solid #fecaca',
                          borderRadius: 10,
                          background: '#fef2f2',
                          color: '#b91c1c',
                        }}
                      >
                        <div className="small font-weight-bold mb-1">Please fix:</div>
                        <ul className="mb-0 pl-3" style={{ fontSize: scaled(12) }}>
                          {subgroupValidationErrors.map((validationError, index) => (
                            <li key={`${validationError.field}-${index}`}>{validationError.message}</li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </div>

                  <div className="d-flex flex-column" style={{ gap: 8 }}>
                    {inlineComposerSubgroupKey === `${group.id}:${subgroup.id}` && editingCardId === null ? (
                      renderInlineGroupedItemComposer('Add Item')
                    ) : null}
                    {subgroup.cards.map((card, localIndex) => {
                      const derivedClaimType = getCardClaimType(card.maxClaims, card.claimType);
                      const isEditingInline = editingCardId === card.id;

                      return (
                        <div
                          key={card.id}
                          className="px-3 py-2"
                          draggable={!isEditingInline && !savingCards && movingCardId === null}
                          onDragStart={(e) => {
                            setDraggingSubgroupItemId(card.id);
                            setDragOverSubgroupItemId(null);
                            e.dataTransfer.effectAllowed = 'move';
                            e.dataTransfer.setData('text/plain', card.id);
                          }}
                          onDragOver={(e) => {
                            if (!draggingSubgroupItemId || draggingSubgroupItemId === card.id) {
                              return;
                            }
                            e.preventDefault();
                            e.dataTransfer.dropEffect = 'move';
                            setDragOverSubgroupItemId(card.id);
                          }}
                          onDragLeave={() => {
                            if (dragOverSubgroupItemId === card.id) {
                              setDragOverSubgroupItemId(null);
                            }
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            const sourceCardId = draggingSubgroupItemId ?? e.dataTransfer.getData('text/plain');
                            setDragOverSubgroupItemId(null);
                            setDraggingSubgroupItemId(null);
                            if (!sourceCardId || sourceCardId === card.id) {
                              return;
                            }
                            reorderSubgroupItemDraft(group.id, subgroup.id, sourceCardId, card.id);
                          }}
                          onDragEnd={() => {
                            setDraggingSubgroupItemId(null);
                            setDragOverSubgroupItemId(null);
                          }}
                          style={{
                            border: '1px solid #e5e7eb',
                            borderRadius: 6,
                            background: draggingSubgroupItemId === card.id ? '#f8fbff' : '#fcfdff',
                            minHeight: 84,
                            display: 'flex',
                            alignItems: 'center',
                            opacity: draggingSubgroupItemId === card.id ? 0.6 : 1,
                            boxShadow:
                              dragOverSubgroupItemId === card.id
                                ? 'inset 0 0 0 2px #60a5fa'
                                : undefined,
                            cursor: isEditingInline ? 'default' : 'grab',
                          }}
                        >
                          {isEditingInline ? (
                            renderInlineGroupedItemComposer('Save Item')
                          ) : (
                            <div
                              className="d-flex align-items-center justify-content-between"
                              style={{ gap: 12, flexWrap: 'nowrap', width: '100%', alignItems: 'center' }}
                            >
                              <div
                                className="d-flex align-items-center"
                                style={{ gap: 10, minWidth: 0, flex: 1, overflow: 'hidden', flexWrap: 'nowrap', alignItems: 'center' }}
                              >
                                {canReorderItems ? (
                                  <div className="d-flex flex-column align-items-start" style={{ gap: 4, flexShrink: 0 }}>
                                    <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
                                      <span
                                        title="Drag to reorder"
                                        style={{
                                          color: '#94a3b8',
                                          fontSize: scaled(14),
                                          lineHeight: 1,
                                          cursor: isEditingInline ? 'default' : 'grab',
                                          userSelect: 'none',
                                        }}
                                      >
                                        ⋮⋮
                                      </span>
                                      <Button
                                        size="sm"
                                        variant="secondary"
                                        disabled={localIndex === 0 || movingCardId !== null || savingCards}
                                        onClick={() => onMoveSubgroupItemDraft(group.id, subgroup.id, card.id, 'up')}
                                        title="Move up"
                                      >
                                        ↑
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="secondary"
                                        disabled={
                                          localIndex === subgroup.cards.length - 1 || movingCardId !== null || savingCards
                                        }
                                        onClick={() => onMoveSubgroupItemDraft(group.id, subgroup.id, card.id, 'down')}
                                        title="Move down"
                                      >
                                        ↓
                                      </Button>
                                    </div>
                                    <span
                                      style={{
                                        fontSize: scaled(11),
                                        fontWeight: 600,
                                        color: '#94a3b8',
                                        lineHeight: 1,
                                        paddingLeft: 2,
                                      }}
                                    >
                                      Order: {card.itemDisplayOrder ?? localIndex}
                                    </span>
                                  </div>
                                ) : null}
                                <span style={{ ...priceChipStyle, fontSize: scaled(12), padding: `${scaled(6)}px ${scaled(10)}px` }}>
                                  Price: {getCardPricingLabel(card)}
                                </span>
                                <span style={{ ...metaChipStyle, fontSize: scaled(12), padding: `${scaled(6)}px ${scaled(10)}px` }}>
                                  Limit: {getClaimDisplayLabel(card.maxClaims, derivedClaimType)}
                                </span>
                                {Number(card.itemBonusLoyaltyPoints ?? 0) > 0 ? (
                                  <span style={{ ...metaChipStyle, fontSize: scaled(12), padding: `${scaled(6)}px ${scaled(10)}px` }}>
                                    Bonus: {formatLoyaltyPoints(card.itemBonusLoyaltyPoints, { signed: true })}
                                  </span>
                                ) : null}
                                <div
                                  className="d-flex align-items-center"
                                  style={{ gap: 8, minWidth: 0, overflow: 'hidden', flexWrap: 'nowrap' }}
                                >
                                {(card.rewards ?? []).length === 0 && <span className="text-muted small">No rewards configured</span>}
                                {(card.rewards ?? []).map((reward, rewardIndex) => (
                                  <span
                                    key={`${card.id}_reward_${rewardIndex}`}
                                    style={{
                                      ...rewardChipStyle,
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      fontSize: scaled(12),
                                      padding: `${scaled(6)}px ${scaled(10)}px`,
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {getSavedRewardDisplayLabel(reward)}
                                  </span>
                                ))}
                                </div>
                              </div>
                              <div className="d-flex align-items-center justify-content-end" style={{ gap: 8, flexShrink: 0 }}>
                                <Button
                                  size="sm"
                                  variant="primary"
                                  style={editButtonStyle}
                                  disabled={savingCards || movingCardId !== null}
                                  onClick={() => onEditCard(card)}
                                >
                                  Edit
                                </Button>
                                <Button
                                  size="sm"
                                  variant="danger"
                                  style={deleteButtonStyle}
                                  disabled={savingCards || movingCardId !== null}
                                  onClick={() => onDeleteCardDraft(card.id)}
                                >
                                  {deletingCardId === card.id ? 'Deleting...' : 'Delete'}
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  </div>
                  ) : null}
                </div>
                );
              })}
            </div>
          ) : null}
        </Card.Body>
      </Card>
    );
  };

  const renderItemCategorySection = (
    category: LoyaltyCampaignItemCategory,
    cards: LoyaltyCampaignCardRow[],
    options?: { showAddButton?: boolean; sectionAnchorId?: string }
  ) => {
    const sectionId = `item-${category.toLowerCase()}` as LoyaltyCampaignCardSectionId;
    const categoryVisualStyle = ITEM_CATEGORY_CARD_STYLES[category];
    const showAddButton = options?.showAddButton ?? false;
    const vanityGroupOrder = [
      'VINYL',
      'FRAME',
      'STICKER',
      LevelRewardInputType.BADGE,
      LevelRewardInputType.POSTER,
      LevelRewardInputType.EMOJI,
      LevelRewardInputType.EMOJI_PACK,
      'OTHER',
    ] as const;
    const vanityGroups = new Map<string, { label: string; cards: LoyaltyCampaignCardRow[] }>();

    if (category === 'VANITY') {
      for (const card of cards) {
        const vanityType = getVanityItemTypeFromReward(card.rewards?.[0]);
        const rewardType = card.rewards?.[0] ? toLevelRewardInputType(card.rewards[0]) : null;
        const groupKey = vanityType ?? rewardType ?? 'OTHER';
        const groupLabel =
          vanityType
            ? VANITY_ITEM_LABELS[vanityType]
            : rewardType
              ? toTitleLabel(rewardType)
              : 'Other Vanity Item';
        const bucket = vanityGroups.get(groupKey) ?? { label: groupLabel, cards: [] };
        bucket.cards.push(card);
        vanityGroups.set(groupKey, bucket);
      }
    }

    const groupedCards = buildItemGroups(cards);

    return (
      <div
        key={`${options?.sectionAnchorId ?? 'item'}-${category}`}
        id={options?.sectionAnchorId}
        className="mx-auto mb-4"
        style={{
          maxWidth: SECTION_MAX_WIDTH,
          scrollMarginTop: 72,
          padding: scaled(20),
          borderRadius: scaled(18),
          border: `1px solid ${categoryVisualStyle.borderColor}`,
          background: categoryVisualStyle.background,
        }}
      >
        <div
          className="d-flex align-items-center justify-content-between flex-wrap mb-3"
          style={{
            gap: 10,
            padding: `${scaled(14)}px ${scaled(16)}px`,
            borderRadius: scaled(14),
            background: categoryVisualStyle.headerBackground,
            border: `1px solid ${categoryVisualStyle.headerBorderColor}`,
          }}
        >
          <div>
            <div className="font-weight-bold" style={{ fontSize: 19, color: '#172033' }}>
              {LOYALTY_CAMPAIGN_ITEM_CATEGORY_LABELS[category]}
            </div>
          </div>
          <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
            {showAddButton ? (
              <Button
                size="sm"
                className="px-3"
                style={{
                  ...getItemCategoryBadgeStyle(category),
                  borderColor: 'transparent',
                  fontWeight: 700,
                }}
                onClick={() => openCreateCardModal('ITEM')}
                disabled={savingCards}
              >
                Add Item
              </Button>
            ) : null}
            {isCardSectionDirty(sectionId) ? (
              <span
                style={{
                  ...metaChipStyle,
                  background: '#fff7ed',
                  borderColor: '#fdba74',
                  color: '#b45309',
                }}
              >
                Unsaved changes
              </span>
            ) : null}
            <Button
              variant="primary"
              size="sm"
              onClick={() => saveCardDraft(sectionId === 'bundle' ? 'bundle' : 'single')}
              disabled={!isCardSectionDirty(sectionId) || savingCards}
            >
              {savingCards ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
        {category === 'VANITY' ? (
          vanityGroupOrder
            .filter((groupKey) => vanityGroups.has(groupKey))
            .map((groupKey) => {
              const vanityGroup = vanityGroups.get(groupKey);
              if (!vanityGroup) {
                return null;
              }
              const vanityCards = vanityGroup.cards;
              const vanityLabel = vanityGroup.label;
              return (
                <div key={String(groupKey)} className="mb-3">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <div className="font-weight-bold">{vanityLabel}</div>
                    <div className="text-muted small">{vanityCards.length} card(s)</div>
                  </div>
                  {vanityCards.length > 0 ? (
                    buildItemGroups(vanityCards).map((group) => renderGroupBlock(group, sectionId, `${String(groupKey)}-`))
                  ) : (
                    <div
                      className="text-muted px-3 py-2"
                      style={{ border: '1px dashed #d7dbe0', borderRadius: 10, background: '#fbfcfd' }}
                    >
                      No {vanityLabel.toLowerCase()} cards yet.
                    </div>
                  )}
                </div>
              );
            })
        ) : groupedCards.length > 0 ? (
          groupedCards.map((group) => renderGroupBlock(group, sectionId))
        ) : (
          <div
            className="text-muted px-3 py-2"
            style={{ border: '1px dashed #d7dbe0', borderRadius: 10, background: '#fbfcfd' }}
          >
            No item cards in this category yet.
          </div>
        )}
      </div>
    );
  };

  const onSelectGroupedItemSidebarLink = (state: CardLifecycleState, groupId: string) => {
    setActiveSection('grouped-items');
    setShowDraftGroupedItemsOnly(false);
    setSelectedGroupedItemView({ state, groupId });
  };

  return (
    <div className="loyalty-campaign-screen">
      <LoadingLogo show={pending} />
      <EditRewardsModal
        isEdit={editingRewardIndex !== null}
        tier={null}
        level={0}
        show={showRewardModal}
        reward={selectedReward}
        handleSave={onSaveRewardModal}
        handleClose={() => {
          setShowRewardModal(false);
          setSelectedReward(null);
          setEditingRewardIndex(null);
        }}
        disableCurrencySpecialEvent
        disableMonthlyBuff
        disableSubscriptionDay={false}
        enableItemLevelEdit
        enableExtendedFragmentSelector
        disableRandomFragmentOption
        liveOpsAvailabilities={[ItemAvailability.EARN, ItemAvailability.EARN_EACH_LEVEL]}
        allowedRewardTypes={activeItemRewardTypes}
      />

      <ConfigModal
        show={showConfigModal}
        configModalMode={configModalMode}
        configGroupAmount={configGroupAmount}
        configAmount={configAmount}
        bulkSkusText={bulkSkusText}
        selectedSku={selectedSku}
        offerOptions={offerOptions}
        offerSkuMap={offerSkuMap}
        hasValidConfigAmount={hasValidConfigAmount}
        upsertConfigLoading={savingConfigs}
        onHide={() => {
          setShowConfigModal(false);
          clearConfigForm();
        }}
        onSave={onSaveConfigDraft}
        setConfigAmount={setConfigAmount}
        setBulkSkusText={setBulkSkusText}
        setSelectedSku={setSelectedSku}
      />

      <CardModal
        show={showCardModal}
        editingCardId={editingCardId}
        cardKind={cardKind}
        cardName={cardName}
        cardGroupId={cardGroupId}
        cardGroupName={cardGroupName}
        cardGroupDisplayOrder={cardGroupDisplayOrder}
        cardSubgroupName={cardSubgroupName}
        subgroupNameOptions={subgroupNameOptions}
        getSubgroupNameOptionLabel={getSubgroupNameOptionLabel}
        cardCost={cardCost}
        cardItemBaseStoreOfferId={cardItemBaseStoreOfferId}
        cardItemSKUPrice={cardItemSKUPrice}
        cardItemBonusLoyaltyPoints={cardItemBonusLoyaltyPoints}
        cardClaimType={cardClaimType}
        cardMaxClaims={cardMaxClaims}
        cardUseSchedule={cardUseSchedule}
        cardStartDate={cardStartDate}
        cardEndDate={cardEndDate}
        cardRewards={cardRewards}
        activeItemCategory={activeItemCategory}
        activeVanityItemType={activeVanityItemType}
        directCurrencyRewardType={directCurrencyRewardType}
        directCurrencyReward={directCurrencyReward}
        directCurrencyAmount={directCurrencyAmount}
        offerOptions={offerOptions}
        offerPriceMap={offerPriceMap}
        offerSkuMap={offerSkuMap}
        skuPriceMap={skuPriceMap}
        hasValidCardInput={hasValidCardInput}
        createCardLoading={false}
        updateCardLoading={false}
        editingRewardIndex={editingRewardIndex}
        onHide={() => setShowCardModal(false)}
        onSaveCard={onSaveCardDraft}
        onOpenAddRewardModal={onOpenAddRewardModal}
        onOpenEditRewardModal={onOpenEditRewardModal}
        onChangeDirectCurrencyAmount={onChangeDirectCurrencyAmount}
        setCardName={setCardName}
        setCardCost={setCardCost}
        setCardSubgroupName={setCardSubgroupName}
        setCardItemBaseStoreOfferId={setCardItemBaseStoreOfferId}
        setCardItemSKUPrice={setCardItemSKUPrice}
        setCardItemBonusLoyaltyPoints={setCardItemBonusLoyaltyPoints}
        setCardClaimType={setCardClaimType}
        setCardMaxClaims={setCardMaxClaims}
        setCardUseSchedule={setCardUseSchedule}
        setCardStartDate={setCardStartDate}
        setCardEndDate={setCardEndDate}
        setCardRewards={setCardRewards}
        setVanityItemTypePreset={setVanityItemTypePreset}
        setEditingRewardIndex={setEditingRewardIndex}
        setSelectedReward={setSelectedReward as (value: null) => void}
        setShowRewardModal={setShowRewardModal}
      />

      <Modal show={showGroupModal} onHide={clearGroupForm} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editingGroup && isDraftGroupId(editingGroup.originalGroupId) ? 'Create Group' : 'Edit Group'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingGroup ? (
            <Form>
              {(() => {
                const isCreatingGroup = isDraftGroupId(editingGroup.originalGroupId);
                return (
                  <>
              <Form.Group>
                <Form.Label>Group Name</Form.Label>
                <Form.Control
                  value={editingGroup.groupName}
                  onChange={(e) => {
                    const nextValue = e.currentTarget.value;
                    setEditingGroup((prev) => (prev ? { ...prev, groupName: nextValue } : prev));
                  }}
                />
              </Form.Group>
              <div style={getScheduleTogglePanelStyle(editingGroup.useSchedule)}>
                <div>
                  <Form.Check
                    type="switch"
                    id="group-use-schedule"
                    label="Set schedule"
                    checked={editingGroup.useSchedule}
                    onChange={(e) => {
                      const nextChecked = e.currentTarget.checked;
                      setEditingGroup((prev) =>
                        prev
                          ? {
                              ...prev,
                              useSchedule: nextChecked,
                              startDate: nextChecked ? prev.startDate : null,
                              endDate: nextChecked ? prev.endDate : null,
                            }
                          : prev
                      );
                    }}
                  />
                  <div className="small text-muted mt-1">
                    Default schedule for all subgroup and item entries in this group.
                  </div>
                </div>
              </div>
              <Form.Text muted>
                Group schedule applies to every subgroup and item inside this group.
              </Form.Text>
              {editingGroup.useSchedule ? (
                <>
                  <Form.Group>
                    <div className="d-flex justify-content-between align-items-center flex-wrap" style={{ gap: 8 }}>
                      <Form.Label className="mb-0">Start Date (UTC)</Form.Label>
                      {editingGroup.startDate ? (
                        <span className="small text-muted">
                          {`Local time: ${formatLocalDateTime(editingGroup.startDate)} (${localTimezone})`}
                        </span>
                      ) : null}
                    </div>
                    <DateTimePicker
                      dateFormat="YYYY-MM-DD"
                      timeFormat="HH:mm"
                      utc={true}
                      value={editingGroup.startDate ? moment.utc(editingGroup.startDate) : undefined}
                      onChange={(value) =>
                        setEditingGroup((prev) =>
                          prev ? { ...prev, startDate: moment.isMoment(value) ? value.toDate() : null } : prev
                        )
                      }
                    />
                  </Form.Group>
                  <Form.Group>
                    <div className="d-flex justify-content-between align-items-center flex-wrap" style={{ gap: 8 }}>
                      <Form.Label className="mb-0">End Date (UTC)</Form.Label>
                      <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() =>
                            setEditingGroup((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    ...applyEndNowToDateRange(prev.startDate),
                                  }
                                : prev
                            )
                          }
                        >
                          End now
                        </Button>
                        {editingGroup.endDate ? (
                          <span className="small text-muted">
                            {`Local time: ${formatLocalDateTime(editingGroup.endDate)} (${localTimezone})`}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <DateTimePicker
                      dateFormat="YYYY-MM-DD"
                      timeFormat="HH:mm"
                      utc={true}
                      value={editingGroup.endDate ? moment.utc(editingGroup.endDate) : undefined}
                      onChange={(value) =>
                        setEditingGroup((prev) =>
                          prev ? { ...prev, endDate: moment.isMoment(value) ? value.toDate() : null } : prev
                        )
                      }
                    />
                  </Form.Group>
                </>
              ) : null}
              {isCreatingGroup ? (
                <div
                  className="mt-4 px-3 py-3 text-muted"
                  style={{ border: '1px dashed #d7dbe0', borderRadius: 10, background: '#fbfcfd' }}
                >
                  This group will appear on screen immediately. Add the first subgroup item from the group card.
                </div>
              ) : null}
                  </>
                );
              })()}
            </Form>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" style={activeSecondaryButtonStyle} onClick={clearGroupForm}>
            Cancel
          </Button>
          <Button variant="primary" onClick={saveGroupDraft}>
            {editingGroup && isDraftGroupId(editingGroup.originalGroupId) ? 'Add Group' : 'Save Group'}
          </Button>
        </Modal.Footer>
      </Modal>

      <div>
        {error || notice ? (
          <div
            style={{
              position: 'fixed',
              top: scaled(16),
              left: '50%',
              transform: 'translateX(-50%)',
              width: 'min(720px, calc(100vw - 32px))',
              zIndex: 1080,
            }}
          >
            {error ? (
              <Alert
                variant="danger"
                dismissible
                onClose={() => setError(null)}
                className="mb-2"
                style={{ boxShadow: '0 12px 28px rgba(15, 23, 42, 0.18)' }}
              >
                {error}
              </Alert>
            ) : null}
            {notice ? (
              <Alert variant="success" className="mb-0" style={{ boxShadow: '0 12px 28px rgba(15, 23, 42, 0.18)' }}>
                {notice}
              </Alert>
            ) : null}
          </div>
        ) : null}

        <Tab.Container
          activeKey={activeSection}
          onSelect={(k) => {
            if (!k) {
              return;
            }
            setActiveSection(k);
            if (k !== 'grouped-items') {
              setSelectedGroupedItemView(null);
              setShowDraftGroupedItemsOnly(false);
            }
          }}
        >
          <Row className="mb-5">
            <Col className="col-2">
              <div
                style={{
                  position: 'sticky',
                  top: scaled(20),
                }}
              >
                <div style={{ padding: `${scaled(4)}px 0` }}>
                  <Nav variant="pills" className="flex-column" style={{ gap: scaled(8) }}>
                    <Nav.Item>
                      <Nav.Link
                        eventKey="loyalty-points"
                        className="d-flex justify-content-between align-items-center"
                        style={{
                          borderRadius: 10,
                          background: activeSection === 'loyalty-points' ? '#315fd6' : '#e8f0ff',
                          color: activeSection === 'loyalty-points' ? '#ffffff' : '#163b9f',
                          border: activeSection === 'loyalty-points' ? '1px solid #2b52b9' : '1px solid #2b52b9',
                          fontWeight: 700,
                          padding: `${scaled(10)}px ${scaled(12)}px`,
                          margin: 0,
                          boxShadow:
                            activeSection === 'loyalty-points'
                              ? '0 8px 18px rgba(49, 95, 214, 0.18)'
                              : '0 4px 10px rgba(30, 58, 138, 0.06)',
                        }}
                      >
                        <span>Loyalty Point Mapping</span>
                        {isConfigDirty && <Badge pill variant="warning" className="ml-1">!</Badge>}
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Button
                        className="w-100 d-flex justify-content-between align-items-center"
                        onClick={() => {
                          openCreateGroupModal();
                        }}
                        style={{
                          borderRadius: 10,
                          background: '#e8f0ff',
                          color: '#163b9f',
                          border: '1px solid #2b52b9',
                          fontWeight: 700,
                          padding: `${scaled(10)}px ${scaled(12)}px`,
                          margin: 0,
                          boxShadow: '0 4px 10px rgba(30, 58, 138, 0.06)',
                          textAlign: 'left',
                        }}
                        variant="link"
                      >
                        <span>Add Grouped Items</span>
                      </Button>
                    </Nav.Item>
                    <div className="mt-2">
                      {(['active', 'scheduled', 'ended'] as CardLifecycleState[]).map((state) => {
                        const groups = groupedItemCardsByState[state];
                        const stateAccent = SIDEBAR_STATE_ACCENT[state];

                        return (
                          <div key={state} className="mb-3">
                            <div
                              className="px-2 py-1 d-flex align-items-center"
                              style={{
                                fontSize: scaled(11),
                                fontWeight: 800,
                                letterSpacing: '0.05em',
                                color: '#64748b',
                                gap: 8,
                                textTransform: 'uppercase',
                              }}
                            >
                              <span
                                style={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  background: stateAccent.dot,
                                  display: 'inline-block',
                                }}
                              />
                              {state} ({groups.length})
                            </div>
                            <div className="d-flex flex-column" style={{ gap: 6 }}>
                              {groups.length === 0 ? (
                                <div className="px-2 pb-1 text-muted" style={{ fontSize: scaled(12) }}>
                                  No groups
                                </div>
                              ) : (
                                groups.map((group) => {
                                  const isSelected =
                                    activeSection === 'grouped-items' &&
                                    selectedGroupedItemView?.state === state &&
                                    selectedGroupedItemView.groupId === group.id;
                                  const groupSummary = `${group.subgroups.length} subgroup${group.subgroups.length === 1 ? '' : 's'} • ${group.cards.length} item${group.cards.length === 1 ? '' : 's'}`;

                                  return (
                                    <Nav.Item key={`${state}-${group.id}`} style={{ width: '100%' }}>
                                      <Nav.Link
                                        eventKey="grouped-items"
                                        onClick={() => onSelectGroupedItemSidebarLink(state, group.id)}
                                        className="d-flex align-items-start"
                                        style={{
                                          width: '100%',
                                          borderRadius: 10,
                                          background: isSelected ? '#315fd6' : '#ffffff',
                                          color: isSelected ? '#ffffff' : '#1e293b',
                                          border: isSelected ? '1px solid #2b52b9' : '1px solid #dbe4ee',
                                          fontWeight: 600,
                                          padding: `${scaled(9)}px ${scaled(12)}px`,
                                          boxShadow: 'none',
                                          gap: 8,
                                        }}
                                      >
                                        <span
                                          style={{
                                            width: 8,
                                            minWidth: 8,
                                            height: 8,
                                            marginTop: 6,
                                            borderRadius: '50%',
                                            background: isSelected ? '#ffffff' : stateAccent.dot,
                                            display: 'inline-block',
                                          }}
                                        />
                                        <div style={{ minWidth: 0, flex: 1 }}>
                                          <div
                                            style={{
                                              fontSize: scaled(13),
                                              fontWeight: 700,
                                              lineHeight: 1.25,
                                            }}
                                          >
                                            {group.name}
                                          </div>
                                          <div
                                            style={{
                                              fontSize: scaled(11),
                                              color: isSelected ? 'rgba(255,255,255,0.84)' : '#64748b',
                                              marginTop: 2,
                                              lineHeight: 1.3,
                                            }}
                                          >
                                            {groupSummary}
                                          </div>
                                        </div>
                                      </Nav.Link>
                                    </Nav.Item>
                                  );
                                })
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </Nav>
                </div>
              </div>
            </Col>
            <Col className="col-10">
              <Tab.Content>
                <Tab.Pane eventKey="loyalty-points">
                  <Card>
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <div>
                          <h5 className="mb-1">Loyalty Point Mapping</h5>
                          <small className="text-muted">{configGroups.length} group(s) · {draftConfigs.length} SKU</small>
                        </div>
                        <div className="d-flex align-items-center" style={{ gap: 8 }}>
                          {isConfigDirty && <Badge variant="warning">Unsaved changes</Badge>}
                          <input
                            ref={configImportInputRef}
                            type="file"
                            accept=".xlsx"
                            style={{ display: 'none' }}
                            onChange={onImportConfigWorkbook}
                          />
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            className="px-3"
                            onClick={onExportCurrentConfigWorkbook}
                            disabled={draftConfigs.length === 0 || exportingConfigs}
                          >
                            {exportingConfigs ? 'Exporting...' : 'Export Current Config'}
                          </Button>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="px-3"
                            onClick={openConfigImportPicker}
                            disabled={savingConfigs || importingConfigs}
                          >
                            {importingConfigs ? 'Importing...' : 'Import XLSX'}
                          </Button>
                          <Button variant="success" size="sm" className="px-3" onClick={openCreateConfigModal} disabled={savingConfigs}>
                            Add Mapping
                          </Button>
                          <Button variant="primary" size="sm" className="px-3" onClick={saveConfigDraft} disabled={!isConfigDirty || savingConfigs}>
                            {savingConfigs ? 'Saving...' : 'Save'}
                          </Button>
                        </div>
                      </div>
                      {(importedConfigChangeCount > 0 || removedImportedConfigs.length > 0) && (
                        <div
                          className="d-flex justify-content-between align-items-center flex-wrap mb-3"
                          style={{ gap: 12 }}
                        >
                          <Form.Check
                            id="show-only-imported-config-changes"
                            type="switch"
                            label={`Show only changed (${importedConfigChangeCount})`}
                            checked={showOnlyImportedConfigChanges}
                            disabled={importedConfigChangeCount === 0}
                            onChange={(event) => setShowOnlyImportedConfigChanges(event.target.checked)}
                          />
                          {removedImportedConfigs.length > 0 && (
                            <Badge variant="secondary">{removedImportedConfigs.length} removed in last import</Badge>
                          )}
                        </div>
                      )}
                      {(importedConfigChangeCount > 0 || removedImportedConfigs.length > 0) && (
                        <Alert variant="info" className="py-2 mb-3">
                          <div className="d-flex flex-wrap align-items-center" style={{ gap: 8 }}>
                            {importedConfigChangeCount > 0 && (
                              <span>
                                Last import marked {importedConfigChangeCount} changed SKU in draft.
                              </span>
                            )}
                            {removedImportedConfigs.length > 0 && (
                              <span>
                                Removed from draft: {removedImportedConfigs.slice(0, 3).map((config) => config.id).join(', ')}
                                {removedImportedConfigs.length > 3 ? ' ...' : ''}
                              </span>
                            )}
                          </div>
                        </Alert>
                      )}
                      {removedImportedConfigs.length > 0 && (
                        <Card className="mb-3">
                          <Card.Body style={{ padding: `${scaled(12)}px ${scaled(16)}px` }}>
                            <div className="d-flex justify-content-between align-items-center mb-2" style={{ gap: 8 }}>
                              <div className="font-weight-bold">Removed From Last Import</div>
                              <Badge variant="secondary">{removedImportedConfigs.length} SKU</Badge>
                            </div>
                            <div className="small text-monospace text-muted" style={{ wordBreak: 'break-all' }}>
                              {removedImportedConfigs.map((config) => (
                                <div key={config.id}>
                                  {config.id}
                                  {config.amount ? ` (${config.amount} pts)` : ''}
                                </div>
                              ))}
                            </div>
                          </Card.Body>
                        </Card>
                      )}

                      {visibleConfigGroups.length === 0 && (
                        <div className="text-center text-muted py-3">
                          {showOnlyImportedConfigChanges ? 'No changed SKU from the last import.' : 'No SKU config yet.'}
                        </div>
                      )}
                  {visibleConfigGroups.map((group) => {
                    const groupPriceLabel = getConfigGroupPriceLabel(group.rows);
                    const groupedConfigEntries = getGroupedConfigEntries(group.rows);
                    const groupImportedChangeCount = group.rows.filter((row) => importedConfigChanges[row.id]).length;

                    return (
                      <div
                        key={`group_${group.amount}`}
                        className="mb-3 p-3"
                        style={{
                          border: '1px solid #e5e8ec',
                          borderRadius: scaled(16),
                          background: '#fbfcfe',
                        }}
                      >
                        <div className="d-flex justify-content-between align-items-center flex-wrap mb-2" style={{ gap: 12 }}>
                          <div>
                            <div className="font-weight-bold" style={{ color: '#172033' }}>
                              {formatLoyaltyPoints(group.amount)}
                              {groupPriceLabel ? ` (${groupPriceLabel})` : ''}
                              {groupImportedChangeCount > 0 && (
                                <Badge variant="info" className="ml-2">
                                  {groupImportedChangeCount} changed
                                </Badge>
                              )}
                            </div>
                            <div className="text-muted small">
                              {groupedConfigEntries.filter((entry) => entry.kind === 'offer').length} offer mapping · {group.rows.length} SKU
                            </div>
                          </div>
                          <div className="d-flex justify-content-end flex-wrap" style={{ gap: 8 }}>
                            <Button
                              size="sm"
                              variant="success"
                              style={{ whiteSpace: 'nowrap', backgroundColor: '#22b573', borderColor: '#22b573' }}
                              disabled={savingConfigs}
                              onClick={() => onAddSkuToGroup(group.amount)}
                            >
                              Add SKU
                            </Button>
                            <Button
                              size="sm"
                              variant="primary"
                              style={{ backgroundColor: '#3f7ee8', borderColor: '#3f7ee8' }}
                              disabled={savingConfigs}
                              onClick={() => onEditGroupAmount(group.rows)}
                            >
                              Edit Points
                            </Button>
                            <Button size="sm" variant="danger" disabled={savingConfigs} onClick={() => onDeleteGroupDraft(group.rows)}>
                              {deletingConfigId === `group:${group.amount}` ? 'Deleting...' : 'Delete Group'}
                            </Button>
                          </div>
                        </div>
                        <Row className="g-1">
                          {groupedConfigEntries.map((entry) => {
                            if (entry.kind === 'offer') {
                              return (
                                <Col md={6} key={entry.key}>
                                  <div
                                    className="d-flex justify-content-between align-items-start px-3 py-2"
                                    style={{
                                      borderRadius: 12,
                                      background: '#fff',
                                      border: '1px solid #edf1f5',
                                      gap: 8,
                                    }}
                                  >
                                    <div style={{ overflow: 'visible', flex: 1, minWidth: 0 }}>
                                      <div
                                        title={formatOfferOptionLabel(entry.offer)}
                                        style={{
                                          whiteSpace: 'nowrap',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          fontWeight: 700,
                                          fontSize: scaled(12),
                                          lineHeight: 1.35,
                                        }}
                                      >
                                        {formatOfferOptionLabel(entry.offer)}
                                      </div>
                                      <div className="small text-muted" style={{ marginTop: 6 }}>
                                        {entry.rows.map((configRow) => (
                                          <div key={configRow.id} className="text-monospace" style={{ wordBreak: 'break-all' }}>
                                            <span className="font-weight-bold mr-1">{`${getSkuPlatformLabel(configRow.id)}: `}</span>
                                            {configRow.id}
                                            {importedConfigChanges[configRow.id] && (
                                              <Badge
                                                variant={importedConfigChanges[configRow.id] === 'added' ? 'success' : 'warning'}
                                                className="ml-2"
                                              >
                                                {importedConfigChanges[configRow.id] === 'added' ? 'Added' : 'Updated'}
                                              </Badge>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                    <div className="text-right ml-2">
                                      <Button
                                        size="sm"
                                        variant="danger"
                                        disabled={savingConfigs}
                                        onClick={() => onDeleteGroupDraft(entry.rows)}
                                      >
                                        Delete
                                      </Button>
                                    </div>
                                  </div>
                                </Col>
                              );
                            }

                            const config = entry.row;
                            const skuPrice = getSkuPriceUSD(config.id, skuPriceMap);
                            const importChangeType = importedConfigChanges[config.id];
                            return (
                              <Col md={6} key={entry.key}>
                                <div
                                  className="d-flex justify-content-between align-items-center px-3 py-2"
                                  style={{
                                    borderRadius: 12,
                                    background: '#fff',
                                    border: '1px solid #edf1f5',
                                    gap: 8,
                                  }}
                                >
                                  <div style={{ overflow: 'visible', flex: 1, minWidth: 0 }}>
                                    <div
                                      title={config.id}
                                      style={{
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        fontWeight: 600,
                                        fontSize: scaled(12),
                                        lineHeight: 1.35,
                                      }}
                                    >
                                      {`${config.id}${skuPrice !== null ? ` ($${skuPrice.toFixed(2)})` : ''}`}
                                    </div>
                                    <div className="small text-muted" style={{ marginTop: 2 }}>
                                      Manual SKU fallback
                                    </div>
                                    {importChangeType && (
                                      <div style={{ marginTop: 6 }}>
                                        <Badge variant={importChangeType === 'added' ? 'success' : 'warning'}>
                                          {importChangeType === 'added' ? 'Added by import' : 'Updated by import'}
                                        </Badge>
                                      </div>
                                    )}
                                  </div>
                                  <div className="text-right ml-2">
                                    <Button size="sm" variant="danger" disabled={savingConfigs} onClick={() => onDeleteConfigDraft(config.id)}>
                                      {deletingConfigId === config.id ? 'Deleting...' : 'Delete'}
                                    </Button>
                                  </div>
                                </div>
                              </Col>
                            );
                          })}
                        </Row>
                      </div>
                    );
                  })}
                    </Card.Body>
                  </Card>
                </Tab.Pane>

                <Tab.Pane eventKey="grouped-items">
                  <Card>
                    <Card.Body>
                      {(() => {
                        return (
                          <>
                            <div className="d-flex justify-content-between align-items-start mb-3">
                              <div className="d-flex align-items-center" style={{ gap: 8 }}>
                                {isGroupedItemDirty && (
                                  <Badge variant="warning" style={pulseWarningBadgeStyle}>
                                    Unsaved grouped items
                                  </Badge>
                                )}
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  disabled={!isGroupedItemDirty}
                                  onClick={clearGroupedDraft}
                                >
                                  Clear Draft
                                </Button>
                              </div>
                            </div>
                            {resolvedGroupedItemView && selectedGroupedItemGroup ? (
                              <div
                                className="mx-auto"
                                style={{
                                  maxWidth: SECTION_MAX_WIDTH,
                                }}
                              >
                                <div
                                  className="d-flex align-items-center justify-content-between flex-wrap mb-3"
                                  style={{
                                    gap: 12,
                                    padding: `${scaled(4)}px 0 ${scaled(8)}px`,
                                  }}
                                >
                                  <div>
                                    {resolvedGroupedItemView.state === 'scheduled' &&
                                    formatScheduledRunAt(selectedGroupedItemGroup.startDate) ? (
                                      <div className="text-muted mt-1" style={{ fontSize: scaled(12) }}>
                                        Running at {formatScheduledRunAt(selectedGroupedItemGroup.startDate)}
                                      </div>
                                    ) : null}
                                  </div>
                                </div>
                                {renderGroupBlock(
                                  selectedGroupedItemGroup,
                                  selectedGroupedItemGroup.cards[0]
                                    ? getCardSectionId(selectedGroupedItemGroup.cards[0])
                                    : 'item-other',
                                  `${resolvedGroupedItemView.state}-detail-`,
                                  {
                                    enableReorder: resolvedGroupedItemView.state !== 'ended',
                                    groupIndex: groupedItemsToRender[resolvedGroupedItemView.state].findIndex(
                                      (group) => group.id === selectedGroupedItemGroup.id
                                    ),
                                    totalGroups: groupedItemsToRender[resolvedGroupedItemView.state].length,
                                    anchorId: getGroupedItemAnchorId(
                                      resolvedGroupedItemView.state,
                                      selectedGroupedItemGroup.id
                                    ),
                                    compactMode: true,
                                  }
                                )}
                              </div>
                            ) : (
                              <div
                                className="text-muted px-3 py-2"
                                style={{ border: '1px dashed #d7dbe0', borderRadius: 10, background: '#fbfcfd' }}
                              >
                                {showDraftGroupedItemsOnly ? 'No draft grouped item yet.' : 'No grouped item config yet.'}
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </Card.Body>
                  </Card>
                </Tab.Pane>
              </Tab.Content>
            </Col>
          </Row>
        </Tab.Container>

        <div
          style={{
            position: 'fixed',
            right: scaled(24),
            bottom: scaled(24),
            zIndex: 1070,
          }}
        >
          <Button
            variant={hasAnyDraftChanges ? 'primary' : 'secondary'}
            style={{
              minWidth: 148,
              minHeight: 48,
              borderRadius: 999,
              fontWeight: 700,
              boxShadow: hasAnyDraftChanges
                ? '0 16px 30px rgba(49, 95, 214, 0.28)'
                : '0 12px 24px rgba(15, 23, 42, 0.16)',
            }}
            disabled={!hasAnyDraftChanges || savingAll || savingConfigs || savingCards}
            onClick={onSaveAll}
          >
            {savingAll || savingConfigs || savingCards ? (
              <span className="d-inline-flex align-items-center" style={{ gap: 8 }}>
                <Spinner animation="border" size="sm" />
                Saving...
              </span>
            ) : hasAnyDraftChanges ? (
              'Save All'
            ) : (
              'Saved All'
            )}
          </Button>
        </div>
      </div>

    </div>
  );
};

export default LoyaltyCampaign;
